<template>
  <div class="cw-workspace h-screen flex overflow-hidden bg-[#0c0c0e] text-white">
    <ConversationWorkflowSidebar
      :items="sessionList"
      :active-uuid="sessionUuid"
      :loading="sessionListLoading"
      :error="sessionListError"
      @new-project="onSidebarNewProject"
      @open-session="openWorkflowSession"
      @refresh="loadSessionList"
    />

    <!-- 中：对话 -->
    <section
      class="cw-chat-panel w-[min(100%,420px)] sm:w-[400px] lg:w-[440px] shrink-0 border-r border-white/[0.06] flex flex-col bg-[#121215] min-h-0 relative"
    >
      <header
        class="shrink-0 px-4 py-3 border-b border-white/[0.06] flex items-center justify-between gap-2"
      >
        <h1 class="text-xs sm:text-sm font-semibold text-gray-100 truncate flex-1" :title="taskTitle">
          {{ taskTitle }}
        </h1>
        <div class="flex items-center gap-1 shrink-0">
          <button
            type="button"
            class="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] text-[11px]"
          >
            分享
          </button>
          <button
            type="button"
            class="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06]"
            title="更多"
          >
            <i class="fas fa-ellipsis-h"></i>
          </button>
        </div>
      </header>

      <div
        ref="chatScroll"
        class="flex-1 overflow-y-auto scrollbar-thin min-h-0"
        @scroll="onChatScroll"
      >
        <!-- 顶部提示条：独立于消息间距，确保紧贴顶部无间隙 -->
        <div
          v-if="shouldShowLoadMoreHint"
          class="sticky top-0 z-10 px-4 py-2 bg-[#121215]/90 backdrop-blur-sm"
        >
          <div class="w-full flex items-center justify-center gap-2 text-[11px] text-gray-400">
            <template v-if="loadingMoreUi">
              <span class="cw-loading-spinner" aria-hidden="true"></span>
              <span>加载中…</span>
            </template>
            <template v-else>
              <i class="fas fa-angle-double-up"></i>
              <span>上拉加载更多</span>
            </template>
          </div>
        </div>

        <div class="px-4 pb-4 pt-3 space-y-3">
          <div
            v-if="!chatMessages.length"
            class="text-center text-gray-600 text-[11px] py-10 leading-relaxed px-2"
          >
            对话模式 · 文生视频<br />
            <span class="text-gray-500"
              >在下方输入文案，选择比例、时长与分辨率后，由火山 Seedance 异步生成视频。</span
            >
          </div>

          <template v-for="m in visibleChatMessages">
          <div v-if="m.role === 'user'" :key="'u-' + m.id" class="flex justify-end cw-chat-item">
            <div
              class="cw-user-bubble max-w-[90%] rounded-2xl rounded-tr-md px-3 py-2 text-[12px] leading-relaxed"
            >
              <div
                v-if="(m.contentHtml || '').trim()"
                class="cw-user-html text-right break-words"
                v-html="sanitizeUserBubbleHtml(m.contentHtml)"
                @click="onUserBubbleHtmlClick($event)"
              ></div>
              <template v-else-if="m.kind === 'uploads' && Array.isArray(m.uploads) && m.uploads.length">
                <p v-if="(m.content || '').trim()" class="whitespace-pre-wrap break-words text-right mb-2">{{ m.content }}</p>
                <div class="grid gap-2" :class="m.uploads.length <= 1 ? 'grid-cols-1' : 'grid-cols-2'">
                  <button
                    v-for="(u, idx) in m.uploads"
                    :key="idx"
                    type="button"
                    class="block rounded-xl overflow-hidden border border-white/20 bg-black/20 hover:border-white/35 transition-colors text-left w-[156px]"
                    title="点击预览"
                    @click="u.kind === 'video' ? openMediaPreview(u) : openLocalUpload(u)"
                  >
                    <img
                      v-if="u.kind === 'image'"
                      :src="u.url"
                      :alt="u.name || ''"
                      loading="lazy"
                      decoding="async"
                      class="w-full h-32 object-cover"
                    />
                    <div v-else class="w-full h-32 bg-black/40 relative">
                      <video
                        :src="u.url"
                        muted
                        playsinline
                        preload="metadata"
                        class="w-full h-32 object-cover opacity-90"
                      ></video>
                      <div class="absolute inset-0 flex items-center justify-center text-white/70">
                        <i class="fas fa-play-circle text-3xl"></i>
                      </div>
                    </div>
                  </button>
                </div>
                <p class="text-[10px] text-white/80 text-right mt-2">已添加 {{ m.uploads.length }} 个文件</p>
              </template>
              <p v-else class="whitespace-pre-wrap break-words text-right">{{ m.content }}</p>
            </div>
          </div>
          <div
            v-else-if="m.role === 'assistant' && m.kind === 'video_options'"
            :key="'vo-' + m.id"
            class="flex justify-start cw-chat-item"
          >
            <div
              class="cw-video-options max-w-[min(100%,440px)] w-full rounded-2xl rounded-tl-md border border-white/[0.08] bg-[#1a1a1f] px-4 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <p class="text-[13px] text-gray-200 leading-relaxed mb-4">
                已收到你的文案。请选择<strong class="text-fuchsia-300">影片比例</strong>、<strong
                  class="text-fuchsia-300"
                  >时长（1～15 秒）</strong
                >与<strong class="text-fuchsia-300">分辨率（480p / 720p / 1080p）</strong>，确认后将开始生成视频。
              </p>

              <template v-if="!m.resolved">
                <div class="mb-5">
                  <p class="text-sm text-white font-medium mb-3">影片比例</p>
                  <div class="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      class="cw-ratio-card flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 transition-all bg-[#141418] text-left"
                      :class="
                        m.paramRatio === '16:9'
                          ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]'
                          : 'border-transparent hover:border-white/10'
                      "
                      @click="$set(m, 'paramRatio', '16:9')"
                    >
                      <span
                        class="cw-mini-frame cw-mini-frame--landscape rounded-md bg-gradient-to-br from-fuchsia-500 to-teal-500/90"
                      ></span>
                      <span class="text-xs text-gray-200">横版 16:9</span>
                    </button>
                    <button
                      type="button"
                      class="cw-ratio-card flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 transition-all bg-[#141418] text-left"
                      :class="
                        m.paramRatio === '9:16'
                          ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]'
                          : 'border-transparent hover:border-white/10'
                      "
                      @click="$set(m, 'paramRatio', '9:16')"
                    >
                      <span
                        class="cw-mini-frame cw-mini-frame--portrait rounded-md bg-gradient-to-br from-fuchsia-500 to-teal-500/90"
                      ></span>
                      <span class="text-xs text-gray-200">竖版 9:16</span>
                    </button>
                  </div>
                </div>

                <div class="mb-5">
                  <p class="text-sm text-white font-medium mb-3">视频时长</p>
                  <div
                    class="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#141418] px-3 py-3.5"
                  >
                    <input
                      type="range"
                      class="cw-duration-slider flex-1 min-w-0 h-7"
                      min="1"
                      max="15"
                      step="1"
                      :value="durationSliderValue(m)"
                      :style="{ '--cw-fill': durationSliderPct(m) + '%' }"
                      @input="onDurationSliderInput(m, $event)"
                    />
                    <span
                      class="shrink-0 min-w-[2.25rem] text-right text-sm font-semibold tabular-nums text-gray-300"
                    >{{ durationSliderValue(m) }}s</span>
                  </div>
                </div>

                <div class="mb-5">
                  <p class="text-sm text-white font-medium mb-3">分辨率</p>
                  <div class="grid grid-cols-3 gap-2">
                    <button
                      v-for="rp in videoResolutionPresets"
                      :key="'res-' + rp"
                      type="button"
                      class="py-2.5 rounded-xl text-xs font-semibold border-2 transition-all bg-[#141418]"
                      :class="
                        m.paramResolution === rp
                          ? 'border-fuchsia-500 bg-fuchsia-600/20 text-fuchsia-100'
                          : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-white/10'
                      "
                      @click="$set(m, 'paramResolution', rp)"
                    >
                      {{ rp }}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-fuchsia-700 to-pink-700 hover:from-fuchsia-600 hover:to-pink-600 shadow-lg shadow-fuchsia-900/30 transition-all"
                  @click="confirmVideoParams(m)"
                >
                  确认并继续 →
                </button>
                <button
                  type="button"
                  class="mt-2 w-full py-2 text-[11px] text-gray-500 hover:text-gray-300"
                  @click="cancelVideoParams(m)"
                >
                  取消本次生成
                </button>
              </template>

              <div v-else class="flex flex-wrap items-center justify-between gap-3 pt-1">
                <p v-if="m.cancelled" class="text-xs text-gray-500">已取消本次视频生成。</p>
                <template v-else>
                  <p class="text-xs text-gray-400">
                    横竖屏：<span class="text-gray-200">{{ m.paramRatio === '9:16' ? '竖版 9:16' : '横版 16:9' }}</span>
                    · 时长：<span class="text-gray-200">{{ m.paramDuration }} 秒</span>
                    · 分辨率：<span class="text-gray-200">{{ m.paramResolution || '720p' }}</span>
                  </p>
                  <span
                    class="shrink-0 px-3 py-1.5 rounded-full text-[10px] font-medium text-white bg-gradient-to-r from-fuchsia-600 to-pink-600 shadow-md"
                  >
                    信息已确认
                  </span>
                </template>
              </div>
            </div>
          </div>
          <div
            v-else-if="m.role === 'assistant' && m.kind === 'images'"
            :key="'img-' + m.id"
            class="flex justify-start cw-chat-item"
          >
            <div
              class="max-w-[90%] rounded-2xl rounded-tl-md px-3.5 py-3 text-[12px] leading-relaxed text-gray-200/95 bg-gradient-to-br from-white/[0.07] to-white/[0.03] border border-white/[0.1]"
            >
              <div v-if="m.streaming" class="flex items-center gap-2 text-gray-500 text-xs py-1">
                <i class="fas fa-spinner fa-spin"></i>
                生成中…
              </div>
              <div v-else class="flex flex-col items-center text-center space-y-2">
                <div
                  v-if="(m.content || '').trim()"
                  class="markdown-reply stream-prose text-center"
                  v-html="assistantHtml(m.content)"
                ></div>
                <div v-if="Array.isArray(m.images) && m.images.length" class="w-full flex justify-center">
                  <div
                    class="grid gap-2 w-fit"
                    :class="(m.images || []).length <= 1 ? 'grid-cols-1' : 'grid-cols-2'"
                  >
                    <button
                      v-for="(u, idx) in m.images"
                      :key="idx"
                      type="button"
                      class="block rounded-xl overflow-hidden border border-white/10 bg-black/30 hover:border-white/20 transition-colors text-left w-[156px]"
                      title="点击预览"
                      @click="openImagePreview(u)"
                    >
                      <img :src="u" alt="" loading="lazy" decoding="async" class="w-full h-32 object-cover" />
                    </button>
                  </div>
                </div>
                <div v-if="Array.isArray(m.images) && m.images.length" class="flex items-center justify-center gap-2 pt-1 w-full">
                  <button
                    type="button"
                    class="h-9 px-3 rounded-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.10] text-[12px] text-gray-200 font-semibold transition-colors"
                    @click="downloadImages(m.images)"
                    title="下载本次生成图片"
                  >
                    <i class="fas fa-download mr-2 text-[12px]"></i>下载
                  </button>
                  <button
                    type="button"
                    class="h-9 px-3 rounded-full bg-fuchsia-600/20 hover:bg-fuchsia-600/30 border border-fuchsia-500/25 text-[12px] text-fuchsia-100 font-semibold transition-colors"
                    @click="addImagesToAssetLibrary(m.images)"
                    title="加入资产（本地保存）"
                  >
                    <i class="fas fa-plus mr-2 text-[12px]"></i>加入资产
                  </button>
                </div>
                <div v-else class="text-gray-500 text-xs">未返回图片链接（请检查后端返回体或控制台任务详情）。</div>
              </div>
            </div>
          </div>
          <div
            v-else-if="m.role === 'assistant' && m.kind === 'agent_style_picker'"
            :key="'agp-' + m.id"
            class="flex justify-start cw-chat-item"
          >
            <div
              class="max-w-[min(100%,560px)] w-full rounded-2xl rounded-tl-md border border-white/[0.08] bg-[#1a1a1f] px-4 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <p class="text-[13px] text-gray-200 leading-relaxed mb-4 whitespace-pre-wrap">
                {{ m.title || '欢迎来到故事短片创作！为了更好地帮您打造专属故事，请先选择一个情感基调～' }}
              </p>
              <div class="space-y-3">
                <button
                  v-for="opt in (m.options || [])"
                  :key="opt.id"
                  type="button"
                  :disabled="!!m._locked"
                  class="w-full text-left px-4 py-3 rounded-2xl border transition-colors"
                  :class="
                    (m.selectedOptId != null && String(m.selectedOptId) === String(opt.id))
                      ? 'border-fuchsia-500/60 bg-fuchsia-500/10 hover:bg-fuchsia-500/15'
                      : 'border-white/[0.06] bg-white/[0.04] hover:bg-white/[0.07]'
                  "
                  @click="confirmAgentStyle(m, opt)"
                >
                  <div class="text-[13px] font-bold text-gray-100">{{ opt.title }}</div>
                  <div class="mt-1 text-[11px] text-gray-400 leading-relaxed">{{ opt.desc }}</div>
                </button>
              </div>
              <button
                v-if="!m._locked"
                type="button"
                class="mt-3 w-full py-2 text-[11px] text-gray-500 hover:text-gray-300"
                @click="cancelAgentStyle(m)"
              >
                取消
              </button>
            </div>
          </div>
          <div
            v-else-if="m.role === 'assistant' && m.kind === 'agent_art_director'"
            :key="'agad-' + m.id"
            class="flex justify-start cw-chat-item"
          >
            <div
              class="max-w-[min(100%,560px)] w-full rounded-2xl rounded-tl-md border border-white/[0.08] bg-[#1a1a1f] px-4 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-2">
                  <span class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/[0.10]">
                    <span class="text-[12px] font-black text-gray-100">艺术总监</span>
                  </span>
                  <div class="text-[13px] font-bold text-gray-200">规划完成</div>
                </div>
                <button
                  type="button"
                  class="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.10] text-gray-300 flex items-center justify-center"
                  :title="m.collapsed ? '展开' : '收起'"
                  @click="toggleAgentArtDirector(m)"
                >
                  <i class="fas" :class="m.collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                </button>
              </div>
              <div v-if="!m.collapsed" class="mt-3 text-[12px] text-gray-200/95 leading-relaxed">
                <div class="markdown-reply stream-prose" v-html="assistantHtml(m.content)"></div>
              </div>
            </div>
          </div>
          <div
            v-else-if="m.role === 'assistant' && m.kind === 'agent_video_options'"
            :key="'agv-' + m.id"
            class="flex justify-start cw-chat-item"
          >
            <div
              class="cw-video-options max-w-[min(100%,560px)] w-full rounded-2xl rounded-tl-md border border-white/[0.08] bg-[#1a1a1f] px-4 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <p class="text-[13px] text-gray-200 leading-relaxed mb-4">
                好的。接下来请选择<strong class="text-fuchsia-300">影片长度</strong>、<strong class="text-fuchsia-300">比例</strong>与<strong
                  class="text-fuchsia-300"
                  >对白语言</strong
                >，确认后将开始生成。
              </p>

              <template v-if="!m.resolved">
                <div class="mb-5">
                  <p class="text-sm text-white font-medium mb-3">创作模式</p>
                  <div class="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      class="py-3 px-3 rounded-2xl border-2 transition-all bg-[#141418] text-left"
                      :class="m.pipelineMode !== 'agent2' ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]' : 'border-transparent hover:border-white/10'"
                      @click="$set(m, 'pipelineMode', 'classic')"
                    >
                      <div class="text-[13px] font-black text-gray-100">一键 Agent</div>
                      <div class="text-[11px] text-gray-400 mt-1">单模型快速出本</div>
                    </button>
                    <button
                      type="button"
                      class="py-3 px-3 rounded-2xl border-2 transition-all bg-[#141418] text-left"
                      :class="m.pipelineMode === 'agent2' ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]' : 'border-transparent hover:border-white/10'"
                      @click="setAgent2PipelineMode(m)"
                    >
                      <div class="text-[13px] font-black text-gray-100">Agent² 八专家</div>
                      <div class="text-[11px] text-gray-400 mt-1">智剧通 ZJT 分工</div>
                    </button>
                  </div>
                  <p class="text-[10px] text-gray-500 mt-2 leading-relaxed">
                    Agent² 依次调用 Story Writer / Character / Location / Plot / Compliance / Splitter / 角色与场景 Designer，最后汇总为成片 JSON；耗时更长。
                  </p>
                </div>

                <div v-if="m.pipelineMode === 'agent2'" class="mb-5">
                  <p class="text-sm text-white font-medium mb-2">Agent² 对话模型</p>
                  <select
                    :value="m.agent2ModelPreset == null ? '' : m.agent2ModelPreset"
                    class="w-full rounded-xl border border-white/10 bg-[#141418] px-3 py-2.5 text-[12px] text-gray-200 outline-none focus:border-fuchsia-500/50"
                    @change="onAgent2ModelPresetChange(m, $event)"
                  >
                    <option
                      v-for="opt in agent2ModelPresetOptions"
                      :key="opt.value === '' ? '_default' : opt.value === '__custom__' ? '_custom' : opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                  <input
                    v-if="m.agent2ModelPreset === '__custom__'"
                    v-model="m.agent2ModelCustom"
                    type="text"
                    placeholder="方舟：控制台 Endpoint ID（走火山时勿填 DeepSeek 模型名）"
                    class="mt-2 w-full rounded-xl border border-white/10 bg-[#141418] px-3 py-2 text-[12px] text-gray-200 placeholder:text-gray-600 outline-none focus:border-fuchsia-500/50"
                  />
                  <p class="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
                    八专家与汇总均走同一对话上游。选 DeepSeek 时后端使用 <code class="text-gray-400">DEEPSEEK_API_KEY</code>；出图/视频仍走火山
                    <code class="text-gray-400">ARK_API_KEY</code>。
                  </p>
                </div>

                <div class="mb-5">
                  <p class="text-sm text-white font-medium mb-3">影片长度</p>
                  <div class="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      class="py-4 px-3 rounded-2xl border-2 transition-all bg-[#141418] text-left"
                      :class="m.videoLength === 'short' ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]' : 'border-transparent hover:border-white/10'"
                      @click="$set(m, 'videoLength', 'short')"
                    >
                      <div class="text-[13px] font-black text-gray-100">短视频</div>
                      <div class="text-[11px] text-gray-400 mt-1">&lt; 1min</div>
                    </button>
                    <button
                      type="button"
                      class="py-4 px-3 rounded-2xl border-2 transition-all bg-[#141418] text-left"
                      :class="m.videoLength === 'long' ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]' : 'border-transparent hover:border-white/10'"
                      @click="$set(m, 'videoLength', 'long')"
                    >
                      <div class="text-[13px] font-black text-gray-100">长视频</div>
                      <div class="text-[11px] text-gray-400 mt-1">&gt;= 1min</div>
                    </button>
                  </div>
                </div>

                <div class="mb-5">
                  <p class="text-sm text-white font-medium mb-3">影片比例</p>
                  <div class="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      class="cw-ratio-card flex flex-col items-center gap-2 py-4 px-3 rounded-2xl border-2 transition-all bg-[#141418] text-left"
                      :class="m.paramRatio === '16:9' ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]' : 'border-transparent hover:border-white/10'"
                      @click="$set(m, 'paramRatio', '16:9')"
                    >
                      <span class="cw-mini-frame cw-mini-frame--landscape rounded-md bg-gradient-to-br from-fuchsia-500 to-teal-500/90"></span>
                      <span class="text-xs text-gray-200">横版 16:9</span>
                    </button>
                    <button
                      type="button"
                      class="cw-ratio-card flex flex-col items-center gap-2 py-4 px-3 rounded-2xl border-2 transition-all bg-[#141418] text-left"
                      :class="m.paramRatio === '9:16' ? 'border-fuchsia-500 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]' : 'border-transparent hover:border-white/10'"
                      @click="$set(m, 'paramRatio', '9:16')"
                    >
                      <span class="cw-mini-frame cw-mini-frame--portrait rounded-md bg-gradient-to-br from-fuchsia-500 to-teal-500/90"></span>
                      <span class="text-xs text-gray-200">竖版 9:16</span>
                    </button>
                  </div>
                </div>

                <div class="mb-5">
                  <p class="text-sm text-white font-medium mb-3">对白语言</p>
                  <div class="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      class="py-2.5 rounded-xl text-xs font-semibold border-2 transition-all bg-[#141418]"
                      :class="m.dialogLang === 'en' ? 'border-fuchsia-500 bg-fuchsia-600/20 text-fuchsia-100' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-white/10'"
                      @click="$set(m, 'dialogLang', 'en')"
                    >
                      英文
                    </button>
                    <button
                      type="button"
                      class="py-2.5 rounded-xl text-xs font-semibold border-2 transition-all bg-[#141418]"
                      :class="m.dialogLang === 'zh' ? 'border-fuchsia-500 bg-fuchsia-600/20 text-fuchsia-100' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-white/10'"
                      @click="$set(m, 'dialogLang', 'zh')"
                    >
                      中文
                    </button>
                    <button
                      type="button"
                      class="py-2.5 rounded-xl text-xs font-semibold border-2 transition-all bg-[#141418]"
                      :class="m.dialogLang === 'ja' ? 'border-fuchsia-500 bg-fuchsia-600/20 text-fuchsia-100' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-white/10'"
                      @click="$set(m, 'dialogLang', 'ja')"
                    >
                      日文
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-fuchsia-700 to-pink-700 hover:from-fuchsia-600 hover:to-pink-600 shadow-lg shadow-fuchsia-900/30 transition-all"
                  @click="confirmAgentVideoParams(m)"
                >
                  确认并继续 →
                </button>
                <button
                  type="button"
                  class="mt-2 w-full py-2 text-[11px] text-gray-500 hover:text-gray-300"
                  @click="cancelAgentVideoParams(m)"
                >
                  取消本次生成
                </button>
              </template>

              <div v-else class="flex flex-wrap items-center justify-between gap-3 pt-1">
                <p class="text-xs text-gray-400">
                  模式：<span class="text-gray-200">{{ m.pipelineMode === 'agent2' ? 'Agent² 八专家' : '一键 Agent' }}</span>
                  <template v-if="m.pipelineMode === 'agent2'">
                    · <span class="text-gray-200">{{ agent2ModelSummaryLine(m) }}</span>
                  </template>
                  · 长度：<span class="text-gray-200">{{ m.videoLength === 'long' ? '长视频' : '短视频' }}</span>
                  · 比例：<span class="text-gray-200">{{ m.paramRatio === '9:16' ? '竖版 9:16' : '横版 16:9' }}</span>
                  · 语言：<span class="text-gray-200">{{ m.dialogLang === 'en' ? '英文' : m.dialogLang === 'ja' ? '日文' : '中文' }}</span>
                </p>
                <span class="shrink-0 px-3 py-1.5 rounded-full text-[10px] font-medium text-white bg-gradient-to-r from-fuchsia-600 to-pink-600 shadow-md">
                  信息已确认
                </span>
              </div>
            </div>
          </div>
          <div
            v-else-if="m.role === 'assistant' && m.kind === 'agent_script_confirm'"
            :key="'agsc-' + m.id"
            class="flex justify-start cw-chat-item"
          >
            <div
              class="max-w-[min(100%,560px)] w-full rounded-2xl rounded-tl-md border border-white/[0.08] bg-[#1a1a1f] px-4 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <p class="text-[13px] text-gray-200 leading-relaxed mb-4">
                剧本已生成！你满意吗？如果满意，我将继续为你生成角色设计图、分镜与分镜视频。
              </p>
              <button
                type="button"
                :disabled="!!m._busy || !!m.resolved"
                class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-fuchsia-700 to-pink-700 hover:from-fuchsia-600 hover:to-pink-600 shadow-lg shadow-fuchsia-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                @click="confirmAgentScript(m)"
              >
                满意，请继续生成 →
              </button>
              <button
                type="button"
                :disabled="!!m._busy || !!m.resolved"
                class="mt-2 w-full py-2 text-[11px] text-gray-500 hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="editAgentScript(m)"
              >
                我想修改
              </button>
            </div>
          </div>
          <div
            v-else-if="m.role === 'assistant' && m.kind === 'agent_character_confirm'"
            :key="'agcc-' + m.id"
            class="flex justify-start cw-chat-item"
          >
            <div
              class="max-w-[min(100%,560px)] w-full rounded-2xl rounded-tl-md border border-white/[0.08] bg-[#1a1a1f] px-4 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <p class="text-[13px] text-gray-200 leading-relaxed mb-4">
                角色设计图已生成！你满意吗？如果满意，我将继续生成分镜视频。
              </p>
              <button
                type="button"
                :disabled="!!m._busy || !!m.resolved"
                class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-fuchsia-700 to-pink-700 hover:from-fuchsia-600 hover:to-pink-600 shadow-lg shadow-fuchsia-900/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                @click="confirmAgentCharacters(m)"
              >
                满意，请继续生成视频 →
              </button>
              <button
                type="button"
                :disabled="!!m._busy || !!m.resolved"
                class="mt-2 w-full py-2 text-[11px] text-gray-500 hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="editAgentCharacters(m)"
              >
                我想修改
              </button>
            </div>
          </div>
          <div v-else-if="m.role === 'assistant'" :key="'a-' + m.id" class="flex justify-start cw-chat-item">
            <div
              class="max-w-[90%] rounded-2xl rounded-tl-md px-3.5 py-2.5 text-[12px] leading-relaxed text-gray-200/95 bg-gradient-to-br from-white/[0.07] to-white/[0.03] border border-white/[0.1]"
            >
              <div v-if="m.kind === 'agent_error' && (m.content || '').trim()" class="space-y-2">
                <div class="markdown-reply stream-prose">{{ m.content }}</div>
                <button
                  v-if="m.retryOpts && !m._busy && !m.resolved"
                  type="button"
                  class="mt-1 w-full py-2 rounded-xl text-[12px] font-semibold text-white bg-white/[0.08] hover:bg-white/[0.12] border border-white/[0.12] transition-colors"
                  @click="retryAgentJob(m)"
                >
                  重试
                </button>
              </div>
              <div
                v-if="m.videoUrl"
                class="mb-2 rounded-lg overflow-hidden border border-white/10 bg-black/40"
              >
                <video
                  :src="m.videoUrl"
                  controls
                  playsinline
                  class="w-full max-h-[min(70vh,360px)] object-contain bg-black"
                ></video>
              </div>
              <div
                v-if="m.streaming && !(m.content || '').trim()"
                class="flex items-center gap-2 text-gray-500 text-xs py-1"
              >
                <i class="fas fa-spinner fa-spin"></i>
                生成中…
              </div>
              <div
                v-else-if="m.kind !== 'agent_error' && (m.content || '').trim()"
                class="markdown-reply stream-prose"
                v-html="assistantHtml(m.content)"
              ></div>
            </div>
          </div>
          </template>
        </div>
      </div>

      <footer ref="assetAnchor" class="shrink-0 border-t border-white/[0.06] px-3 py-4 bg-[#0f0f12]">
        <div
          class="rounded-[28px] bg-gradient-to-br from-white/[0.06] to-white/[0.03] shadow-[0_18px_60px_rgba(0,0,0,0.55)] overflow-hidden"
        >
          <div class="px-5 pt-4 pb-3">
            <div v-if="(localUploads || []).length" class="mb-3">
              <div class="flex items-center gap-2 overflow-x-auto overflow-y-visible pr-1 py-2">
                <div
                  v-for="(u, idx) in localUploads"
                  :key="u.url || idx"
                  class="group relative shrink-0 w-[52px] h-[52px] rounded-2xl border border-white/10 bg-black/30 overflow-visible"
                  :title="u.name || (u.kind === 'video' ? '视频' : '图片')"
                >
                  <div class="w-full h-full rounded-2xl overflow-hidden">
                    <img
                      v-if="u.kind === 'image'"
                      :src="u.url"
                      alt=""
                      loading="lazy"
                      decoding="async"
                      class="w-full h-full object-cover opacity-95"
                    />
                  <button
                    v-else
                    type="button"
                    class="w-full h-full relative"
                    title="点击预览视频"
                    @click.stop="openMediaPreview(u)"
                  >
                    <video
                      :src="u.url"
                      muted
                      playsinline
                      preload="metadata"
                      class="w-full h-full object-cover opacity-90"
                    ></video>
                    <span class="absolute inset-0 flex items-center justify-center text-white/70">
                      <i class="fas fa-play-circle text-lg"></i>
                    </span>
                  </button>
                  </div>

                  <div
                    class="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <button
                      type="button"
                      class="absolute -top-2 -right-2 z-[9999] w-6 h-6 rounded-full bg-black/70 hover:bg-black/85 border border-white/20 text-white/90 flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.55)] backdrop-blur-sm"
                      title="移除"
                      @click.stop="removeLocalUpload(idx)"
                    >
                      <i class="fas fa-times text-[10px]"></i>
                    </button>

                    <button
                      type="button"
                      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-7 px-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/20 text-[11px] text-white font-semibold whitespace-nowrap"
                      @click.stop="insertReferenceUpload(u)"
                    >
                      引用
                    </button>
                  </div>
                </div>
              </div>
              <!-- hint removed -->
            </div>
            <div class="relative">
              <div
                v-if="composerIsEmpty"
                class="pointer-events-none absolute left-0 top-0 text-sm text-gray-500 leading-relaxed"
                style="padding:0;"
              >
                {{
                  awaitingVideoParams
                    ? '请先在上方的参数卡片中点击「确认并继续」…'
                    : '对话工作流 / 输入文案后选择比例、时长与分辨率，提交文生视频任务'
                }}
              </div>
              <div
                ref="bottomComposer"
                :contenteditable="!(isStreaming || awaitingVideoParams)"
                class="w-full bg-transparent text-sm text-gray-100 resize-none border-0 outline-none focus:outline-none focus:ring-0 focus:border-0 disabled:opacity-50 leading-relaxed min-h-[4.5rem] whitespace-pre-wrap break-words"
                @input="onComposerInput"
                @keydown="onComposerKeydown"
                @paste="onComposerPaste"
              ></div>
            </div>
          </div>

          <div class="px-4 pb-4">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  :disabled="isStreaming || awaitingVideoParams"
                  class="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] flex items-center justify-center text-gray-300 disabled:opacity-40"
                  title="上传图片或视频"
                  @click="triggerUploadPick"
                >
                  <i class="fas fa-plus text-sm"></i>
                </button>
                <input
                  ref="uploadInput"
                  type="file"
                  tabindex="-1"
                  style="position:absolute;left:-9999px;width:1px;height:1px;opacity:0;"
                  accept="image/*,video/*"
                  multiple
                  @change="onUploadFilesSelected"
                />

                <button
                  type="button"
                  class="h-10 px-4 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] text-[12px] text-gray-200 flex items-center gap-2 min-w-0"
                  title="选择模型"
                  @click.stop="toggleModelPicker"
                >
                  <span class="w-5 h-5 rounded-lg bg-white/[0.08] border border-white/[0.08] flex items-center justify-center">
                    <i class="fas fa-cube text-[10px] text-fuchsia-200"></i>
                  </span>
                  <span class="font-semibold truncate">{{ selectedModelName }}</span>
                  <i class="fas fa-chevron-down text-[10px] text-gray-500"></i>
                </button>

                <button
                  type="button"
                  class="h-10 px-4 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] text-[12px] text-gray-300 flex items-center gap-2 whitespace-nowrap"
                  :title="selectedStyle ? `风格：${selectedStyle.name}` : '选择风格'"
                  @click.stop="openStyleLibrary"
                >
                  <i class="fas fa-palette text-[12px] text-gray-400"></i>
                  <span class="font-semibold whitespace-nowrap">{{ selectedStyle ? selectedStyle.name : '风格' }}</span>
                </button>

                <button
                  type="button"
                  class="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] flex items-center justify-center text-gray-300"
                  title="表情"
                  @click="openAssetPanel"
                >
                  <i class="far fa-smile text-sm"></i>
                </button>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  :disabled="isStreaming"
                  class="h-10 px-3 rounded-full bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] text-[12px] text-gray-300 disabled:opacity-40"
                  title="重置对话"
                  @click="resetSession"
                >
                  <i class="fas fa-rotate-left"></i>
                </button>

                <button
                  type="button"
                  :disabled="
                    isStreaming ||
                    awaitingVideoParams ||
                    (!(bottomInput || '').trim() &&
                      !(Array.isArray(referencedUploads) && referencedUploads.length) &&
                      !composerHasInlineUploads())
                  "
                  @click="onSend"
                  class="cw-composer-submit w-11 h-11 rounded-full flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  title="发送"
                >
                  <i v-if="!isStreaming && !awaitingVideoParams" class="fas fa-arrow-up text-base"></i>
                  <i v-else-if="awaitingVideoParams" class="fas fa-hourglass-half text-base"></i>
                  <i v-else class="fas fa-spinner fa-spin text-base"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <!-- 模型选择弹层（仅覆盖聊天窗口，不影响右侧） -->
      <div
        v-if="showModelPicker"
        class="absolute inset-0 z-[200] bg-black/40 backdrop-blur-[1px]"
        @click="closeModelPicker"
      ></div>
      <div
        v-if="showModelPicker"
        class="absolute left-1/2 -translate-x-1/2 bottom-[108px] z-[210] w-[min(92%,420px)]"
        @click.stop
      >
        <div
          class="rounded-3xl border border-white/[0.10] bg-[#1b1b1f]/95 shadow-[0_30px_120px_rgba(0,0,0,0.70)] overflow-hidden"
        >
          <div class="px-5 pt-5 pb-4 flex items-center justify-between">
            <div class="text-lg font-black text-gray-100 tracking-tight">模型</div>
            <div class="flex items-center gap-3">
              <span class="text-[12px] text-gray-400 font-semibold">智能模型</span>
              <button
                type="button"
                class="w-11 h-6 rounded-full border border-white/[0.10] bg-white/[0.06] relative transition-colors overflow-hidden"
                :class="smartModel ? 'bg-fuchsia-500/35 border-fuchsia-500/35' : ''"
                @click="smartModel = !smartModel"
              >
                <span
                  class="absolute left-0.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white shadow transition-transform"
                  :class="smartModel ? 'translate-x-5' : 'translate-x-0'"
                ></span>
              </button>
            </div>
          </div>

          <div class="px-5 pb-4">
            <div class="grid grid-cols-3 gap-2 p-1 rounded-2xl bg-black/20 border border-white/[0.08]">
              <button
                type="button"
                class="h-10 rounded-xl flex items-center justify-center gap-1.5 text-[12px] font-bold transition-all"
                :class="modelTab === 'image' ? 'bg-white/[0.12] text-white' : 'text-gray-400 hover:text-gray-200'"
                @click="modelTab = 'image'"
              >
                <i class="far fa-image"></i> 图片
              </button>
              <button
                type="button"
                class="h-10 rounded-xl flex items-center justify-center gap-1.5 text-[12px] font-bold transition-all"
                :class="modelTab === 'video' ? 'bg-white/[0.12] text-white' : 'text-gray-400 hover:text-gray-200'"
                @click="modelTab = 'video'"
              >
                <i class="fas fa-video"></i> 视频
              </button>
              <button
                type="button"
                class="h-10 rounded-xl flex items-center justify-center gap-1.5 text-[12px] font-bold transition-all"
                :class="modelTab === 'text' ? 'bg-white/[0.12] text-white' : 'text-gray-400 hover:text-gray-200'"
                @click="modelTab = 'text'"
              >
                <i class="fas fa-file-lines"></i> 文本
              </button>
            </div>
          </div>

          <div class="px-3 pb-4">
            <div class="max-h-[360px] overflow-auto pr-1">
              <button
                v-for="m in filteredModelOptions"
                :key="m.id"
                type="button"
                class="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-left transition-colors"
                :class="m.id === selectedModelId ? 'bg-white/[0.10] border border-white/[0.10]' : 'hover:bg-white/[0.06]'"
                @click="selectModel(m)"
              >
                <div
                  class="w-10 h-10 rounded-2xl bg-white/[0.06] border border-white/[0.10] flex items-center justify-center text-gray-200"
                >
                  <i :class="m.icon"></i>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <div class="text-[14px] font-extrabold text-gray-100 truncate">
                      {{ m.name }}
                    </div>
                    <span
                      v-if="m.badge"
                      class="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/25 text-fuchsia-200 border border-fuchsia-500/25 font-bold"
                    >
                      {{ m.badge }}
                    </span>
                  </div>
                  <div class="text-[12px] text-gray-400 leading-snug truncate">
                    {{ m.desc }}
                  </div>
                </div>
                <div class="text-gray-500">
                  <i class="fas fa-chevron-right text-[12px]"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 风格库弹层（点击底部“风格”按钮弹出） -->
      <div
        v-if="showStyleLibrary"
        class="absolute inset-0 z-[260] bg-black/45 backdrop-blur-[1px]"
        @click="closeStyleLibrary"
      ></div>
      <div
        v-if="showStyleLibrary"
        class="absolute left-1/2 -translate-x-1/2 bottom-[108px] z-[270] w-[min(96%,520px)]"
        @click.stop
      >
        <div class="rounded-3xl border border-white/[0.10] bg-[#1b1b1f]/95 shadow-[0_30px_120px_rgba(0,0,0,0.70)] overflow-hidden">
          <div class="px-5 pt-5 pb-3 flex items-center justify-between gap-3">
            <div class="text-lg font-black text-gray-100 tracking-tight">风格库</div>
            <div class="flex items-center gap-2">
              <button
                v-if="selectedStyle"
                type="button"
                class="h-8 px-3 rounded-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.10] text-[12px] text-gray-200 font-semibold"
                title="清除风格"
                @click="clearStyle"
              >
                清除
              </button>
              <button
                type="button"
                class="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.10] text-gray-300 flex items-center justify-center"
                title="关闭"
                @click="closeStyleLibrary"
              >
                <i class="fas fa-times text-sm"></i>
              </button>
            </div>
          </div>

          <!-- tabs -->
          <div class="px-5 pb-3">
            <div class="flex flex-wrap gap-2">
              <button
                v-for="t in styleCategoryTabs"
                :key="t.id"
                type="button"
                class="h-8 px-3 rounded-full border text-[12px] font-semibold transition-colors"
                :class="
                  styleTab === t.id
                    ? 'bg-white/[0.12] text-white border-white/[0.14]'
                    : 'bg-white/[0.06] hover:bg-white/[0.10] text-gray-300 border-white/[0.10]'
                "
                @click="onStyleCategoryTabClick(t)"
              >
                {{ t.label }}
              </button>
            </div>
          </div>

          <!-- grid（可滚动 + 触底自动加载） -->
          <div
            ref="styleLibraryScroll"
            class="px-5 pb-5 max-h-[62vh] overflow-y-auto scrollbar-thin"
            @scroll="onStyleLibraryScroll"
          >
            <div class="grid grid-cols-3 sm:grid-cols-4 gap-3">
              <!-- 上传风格占位 -->
              <button
                type="button"
                class="cw-style-card group rounded-2xl border border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.07] transition-colors overflow-hidden aspect-[3/4] flex flex-col items-center justify-center gap-2 text-gray-300"
                title="上传风格（占位）"
                @click="triggerStyleUploadPick"
              >
                <span class="w-11 h-11 rounded-2xl bg-white/[0.06] border border-white/[0.10] flex items-center justify-center">
                  <i class="fas fa-plus text-lg text-gray-300"></i>
                </span>
                <span class="text-[12px] font-semibold">上传风格</span>
              </button>
              <input
                ref="styleUploadInput"
                type="file"
                tabindex="-1"
                style="position:absolute;left:-9999px;width:1px;height:1px;opacity:0;"
                accept="image/*"
                @change="onStyleUploadSelected"
              />

              <button
                v-for="s in filteredStyles"
                :key="s.id"
                type="button"
                class="cw-style-card group rounded-2xl border transition-colors overflow-hidden aspect-[3/4] text-left"
                :class="
                  selectedStyle && selectedStyle.id === s.id
                    ? 'border-fuchsia-500/50 bg-fuchsia-500/10'
                    : 'border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.07]'
                "
                @click="selectStyle(s)"
                :title="s.name"
              >
                <div class="h-full w-full p-3 flex flex-col justify-between">
                  <div class="w-full rounded-xl bg-gradient-to-br from-white/[0.10] to-white/[0.02] border border-white/[0.10] flex-1 flex items-center justify-center overflow-hidden">
                    <img
                      v-if="s.thumb"
                      :src="s.thumb"
                      alt=""
                      loading="lazy"
                      decoding="async"
                      class="w-full h-full object-cover opacity-95"
                    />
                    <span v-else class="text-[22px] font-black tracking-tight text-white/80">{{ s.badge || 'S' }}</span>
                  </div>
                  <div class="pt-2 text-center">
                    <div class="text-[12px] font-bold text-gray-100 truncate">{{ s.name }}</div>
                    <div
                      v-if="String(s.tab || '') !== 'mine'"
                      class="text-[10px] text-gray-400 truncate mt-0.5"
                    >
                      {{ s.desc || '' }}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 我的资产：固定在聊天输入框右侧（精确贴边） -->
      <div
        v-if="showAssetPanel"
        class="fixed z-[1205]"
        :style="assetPanelStyle"
        @click.stop
      >
        <div class="rounded-3xl bg-[#1a1a1f]/95 shadow-[0_30px_120px_rgba(0,0,0,0.70)] overflow-hidden">
          <div class="px-6 pt-5 pb-4">
            <div class="flex items-start justify-between">
              <div>
                <div class="text-[14px] font-black text-gray-100 tracking-tight">我的资产</div>
                <div class="mt-2 flex items-center gap-2 text-[10px]">
                  <button
                    type="button"
                    class="px-2.5 py-1 rounded-lg border transition-colors"
                    :class="assetTab === 'image' ? 'bg-white/[0.10] border-white/[0.10] text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-300'"
                    @click="assetTab = 'image'"
                  >
                    角色
                  </button>
                  <button
                    type="button"
                    class="px-2.5 py-1 rounded-lg border transition-colors"
                    :class="assetTab === 'scene' ? 'bg-white/[0.10] border-white/[0.10] text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-300'"
                    @click="assetTab = 'scene'"
                  >
                    场景
                  </button>
                </div>
              </div>
              <button
                type="button"
                class="w-9 h-9 rounded-full bg-black/30 border border-white/10 text-white/70 hover:text-white hover:bg-black/50 transition-colors"
                title="关闭"
                @click="closeAssetPanel"
              >
                <i class="fas fa-times text-[12px]"></i>
              </button>
            </div>
          </div>

          <div class="px-6 pb-6">
            <div v-if="assetLoading" class="py-10 text-center text-gray-500 text-sm">加载中…</div>
            <div v-else-if="assetError" class="py-6 text-amber-200/90 text-sm">
              {{ assetError }}
            </div>
            <div v-else class="grid grid-cols-6 gap-4">
              <button
                type="button"
                class="col-span-1 group rounded-2xl overflow-hidden bg-black/25 border border-white/10 hover:border-fuchsia-500/30 transition-colors"
                title="创建"
                @click="$router.push('/assets')"
              >
                <div class="aspect-[4/5] flex items-center justify-center">
                  <div class="w-11 h-11 rounded-2xl bg-white/6 border border-white/10 flex items-center justify-center text-white/80">
                    <i class="fas fa-plus"></i>
                  </div>
                </div>
                <div class="pb-3 text-center text-[10px] text-gray-500 font-semibold">创建</div>
              </button>

              <button
                v-for="a in filteredAssetRows"
                :key="a.id"
                type="button"
                class="col-span-1 group rounded-2xl overflow-hidden bg-black/10 border border-white/10 hover:border-white/20 transition-colors"
                title="点击引用到输入框（可重复引用，双击预览）"
                @click="toggleReferenceAsset(a)"
                @dblclick.stop="openImagePreview(a.url)"
              >
                <div class="aspect-[4/5] bg-black/20">
                  <img
                    :src="a.url"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    class="w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <div class="pb-3 pt-2 px-1">
                  <div class="text-[10px] font-bold text-gray-200 truncate text-center">{{ a.title || '未命名' }}</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 右：Agent 模式下智剧节点画布 + 素材总览 -->
    <section
      class="flex-1 flex flex-col min-w-0 relative"
      style="background-color:#08080a;background-image:radial-gradient(circle at 1px 1px, rgba(255,255,255,0.10) 1px, rgba(0,0,0,0) 1px);background-size:22px 22px;"
    >
      <div
        class="shrink-0 px-4 py-3 flex items-center justify-between border-b border-white/[0.04] bg-[#0a0a0c]/80 backdrop-blur-sm"
      >
        <div class="flex items-center gap-2 text-[10px]">
          <template v-if="isAgentModel">
            <button
              type="button"
              class="px-2.5 py-1 rounded-lg transition-colors"
              :class="
                rightWorkspaceTab === 'canvas'
                  ? 'bg-fuchsia-500/25 text-fuchsia-100 border border-fuchsia-500/30'
                  : 'text-gray-500 hover:text-gray-300'
              "
              @click="rightWorkspaceTab = 'canvas'"
            >
              智剧节点
            </button>
            <button
              type="button"
              class="px-2.5 py-1 rounded-lg transition-colors"
              :class="
                rightWorkspaceTab === 'assets'
                  ? 'bg-white/[0.10] text-gray-100'
                  : 'text-gray-500 hover:text-gray-300'
              "
              @click="rightWorkspaceTab = 'assets'"
            >
              素材总览
            </button>
          </template>
          <template v-else>
            <button type="button" class="px-2.5 py-1 rounded-lg bg-white/[0.08] text-gray-200">总览</button>
            <button type="button" class="px-2.5 py-1 rounded-lg text-gray-500 hover:text-gray-300">
              图片/视频
            </button>
          </template>
        </div>
        <div
          class="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/[0.08]"
        >
          <i class="fas fa-gift text-amber-400 text-[10px]"></i>
          <span class="text-fuchsia-200 font-semibold">100 FREE</span>
        </div>
      </div>

      <!-- Agent 节点画布 -->
      <div
        v-show="isAgentModel && rightWorkspaceTab === 'canvas'"
        class="flex-1 relative min-h-0 overflow-hidden cw-agent-canvas-root"
      >
        <div
          class="absolute inset-0 overflow-hidden select-none"
          :class="agentCanvasTool === 'hand' ? 'cursor-grab' : 'cursor-default'"
          :style="{ cursor: agentCanvasPanning ? 'grabbing' : undefined }"
          @wheel.prevent.stop="onAgentCanvasWheel"
          @mousedown="onAgentCanvasSurfaceMouseDown"
        >
          <svg
            class="cw-agent-canvas-edges absolute left-0 top-0 pointer-events-none text-white/25"
            :width="3200"
            :height="3200"
            :style="agentCanvasTransformStyle"
          >
            <path
              v-for="edge in agentCanvasEdgePaths"
              :key="edge.key"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              :d="edge.d"
            />
          </svg>
          <div class="cw-agent-canvas-world absolute left-0 top-0" :style="agentCanvasTransformStyle">
            <div
              v-if="!agentCanvasNodes.length"
              class="absolute left-[120px] top-[100px] max-w-sm text-[12px] text-gray-500 leading-relaxed"
            >
              选择底部模型为 <strong class="text-gray-300">Agent</strong> 并运行一键 Agent 或 Agent² 后，八专家产出会出现在此画布。<br />
              可拖拽卡片、使用右侧菜单<strong class="text-gray-400">重新生成 / 编辑指令</strong>。
            </div>
            <div
              v-for="n in agentCanvasNodes"
              :key="n.id"
              class="cw-agent-node absolute rounded-2xl border bg-[#121216]/95 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-sm transition-shadow"
              :class="[
                n.expert === 'character_designer' ? 'w-[min(96vw,520px)]' : 'w-[min(92vw,360px)]',
                agentCanvasSelectedId === n.id
                  ? 'border-fuchsia-500/50 ring-1 ring-fuchsia-500/30'
                  : 'border-white/[0.10]'
              ]"
              :style="{ left: n.x + 'px', top: n.y + 'px' }"
              @mousedown.stop="onAgentCanvasNodeMouseDown($event, n)"
            >
              <div
                class="cw-agent-node-drag px-3 py-2.5 border-b border-white/[0.06] flex items-center justify-between gap-2 cursor-grab active:cursor-grabbing"
                @mousedown.stop="onAgentCanvasNodeHeaderDown($event, n)"
              >
                <div class="min-w-0">
                  <div class="text-[10px] text-fuchsia-300/90 font-bold truncate">{{ agentCanvasStepLabel(n) }}</div>
                  <div class="text-[12px] text-gray-100 font-semibold truncate">{{ n.title }}</div>
                </div>
                <span
                  v-if="n.regenerating"
                  class="text-[10px] text-gray-400 shrink-0"
                >
                  <i class="fas fa-spinner fa-spin"></i>
                </span>
              </div>
              <!-- 角色设计：按 ### 角色 分块，每角色单独编辑 / 重生 -->
              <div
                v-if="n.expert === 'character_designer' && n.characterSlots && n.characterSlots.length"
                class="px-3 py-2 max-h-[min(72vh,520px)] overflow-y-auto scrollbar-thin space-y-3"
              >
                <div v-if="(n.characterIntro || '').trim()" class="rounded-xl border border-white/[0.06] bg-black/15 p-2">
                  <div class="text-[10px] text-gray-500 mb-1">前言（非单角色段落）</div>
                  <textarea
                    class="w-full rounded-lg border border-white/10 bg-[#0c0c10] px-2 py-1.5 text-[11px] text-gray-200 outline-none focus:border-fuchsia-500/35"
                    rows="3"
                    :value="n.characterIntro"
                    @input="onCharacterIntroInput(n, $event)"
                  ></textarea>
                </div>
                <div
                  v-for="slot in n.characterSlots"
                  :key="slot.id"
                  class="rounded-xl border border-white/[0.08] bg-black/22 p-2.5"
                >
                  <div class="flex items-start justify-between gap-2 mb-1.5">
                    <div class="text-[11px] font-bold text-fuchsia-200/95 leading-snug break-words min-w-0">
                      ### {{ slot.name }}
                    </div>
                    <div class="flex flex-col items-end gap-1 shrink-0">
                      <button
                        type="button"
                        class="text-[10px] px-2 py-0.5 rounded-lg bg-white/[0.08] text-gray-200 hover:bg-white/12"
                        @click.stop="copyAgentCanvasCharacterSlot(slot)"
                      >
                        复制本节
                      </button>
                      <button
                        type="button"
                        class="text-[10px] px-2 py-0.5 rounded-lg bg-fuchsia-600/35 text-fuchsia-100 hover:bg-fuchsia-600/50 disabled:opacity-40"
                        :disabled="!!slot.regenerating || !!n.regenerating"
                        @click.stop="regenerateAgentCanvasCharacterSlot(n, slot)"
                      >
                        <i v-if="slot.regenerating" class="fas fa-spinner fa-spin mr-1"></i>
                        重生本节
                      </button>
                    </div>
                  </div>
                  <textarea
                    class="w-full rounded-lg border border-white/10 bg-[#0c0c10] px-2 py-1.5 text-[11px] text-gray-200 outline-none focus:border-fuchsia-500/35 min-h-[5rem]"
                    rows="5"
                    :value="slot.content"
                    @input="onCharacterSlotContentInput(n, slot, $event)"
                  ></textarea>
                  <div class="mt-2 flex flex-wrap items-center gap-2">
                    <div v-if="slot.images && slot.images.length" class="flex flex-wrap gap-1.5">
                      <button
                        v-for="(iu, ii) in slot.images.slice(0, 12)"
                        :key="ii"
                        type="button"
                        class="w-14 h-14 rounded-lg overflow-hidden border border-white/10 bg-black/30"
                        @click.stop="openImagePreview(iu)"
                      >
                        <img :src="iu" alt="" class="w-full h-full object-cover opacity-90" loading="lazy" />
                      </button>
                    </div>
                    <button
                      type="button"
                      class="text-[10px] px-2.5 py-1.5 rounded-lg bg-white/[0.08] text-gray-200 hover:bg-white/12 border border-white/[0.08] disabled:opacity-40 shrink-0"
                      :disabled="!!slot.regeneratingImage || !!slot.regenerating || !!n.regenerating"
                      @click.stop="regenerateAgentCanvasCharacterSlotImage(n, slot)"
                    >
                      <i v-if="slot.regeneratingImage" class="fas fa-spinner fa-spin mr-1"></i>
                      重新生成图片
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="px-3 py-2 max-h-[200px] overflow-y-auto scrollbar-thin text-[11px] text-gray-300/95 leading-relaxed whitespace-pre-wrap break-words"
              >
                {{ n.body }}
              </div>
              <div
                v-if="n.expert !== 'character_designer' && n.images && n.images.length"
                class="px-3 pb-2 flex flex-wrap gap-1.5"
              >
                <button
                  v-for="(iu, ii) in n.images.slice(0, 8)"
                  :key="ii"
                  type="button"
                  class="w-14 h-14 rounded-lg overflow-hidden border border-white/10 bg-black/30"
                  @click.stop="openImagePreview(iu)"
                >
                  <img :src="iu" alt="" class="w-full h-full object-cover opacity-90" loading="lazy" />
                </button>
              </div>
              <!-- 节点右侧操作条 -->
              <div
                v-if="agentCanvasSelectedId === n.id"
                class="absolute top-1/2 -translate-y-1/2 left-[calc(100%+10px)] flex flex-col gap-1 py-2 px-1.5 rounded-xl bg-[#1a1a1f]/98 border border-white/10 shadow-xl z-10"
                @mousedown.stop
              >
                <button
                  type="button"
                  class="cw-agent-node-action"
                  title="滚动到左侧对话"
                  @click="scrollChatBottom()"
                >
                  <i class="fas fa-comments text-[10px]"></i><span>对话</span>
                </button>
                <button
                  type="button"
                  class="cw-agent-node-action"
                  :disabled="!!n.regenerating"
                  @click="regenerateAgentCanvasNode(n)"
                >
                  <i class="fas fa-redo text-[10px]"></i><span>重生成</span>
                </button>
                <button type="button" class="cw-agent-node-action" @click="openAgentCanvasEdit(n)">
                  <i class="fas fa-pen text-[10px]"></i><span>编辑</span>
                </button>
                <button type="button" class="cw-agent-node-action" @click="copyAgentCanvasNode(n)">
                  <i class="fas fa-copy text-[10px]"></i><span>复制</span>
                </button>
                <button type="button" class="cw-agent-node-action" @click="downloadAgentCanvasNode(n)">
                  <i class="fas fa-download text-[10px]"></i><span>下载</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 素材格 -->
      <div
        v-show="!isAgentModel || rightWorkspaceTab === 'assets'"
        class="flex-1 overflow-auto p-4 min-h-0"
      >
        <div v-if="!rightAssets.length" class="h-full flex items-center justify-center text-gray-600 text-[12px]">
          暂无素材
        </div>
        <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-w-5xl mx-auto">
          <button
            v-for="(a, idx) in rightAssets"
            :key="idx"
            type="button"
            class="group rounded-2xl overflow-hidden border border-white/[0.08] bg-[#101014]/70 hover:border-white/[0.18] transition-colors"
            :title="a.kind === 'video' ? '打开视频' : '打开原图'"
            @click="a.kind === 'image' ? openImagePreview(a.url) : openMediaPreview({ kind: 'video', url: a.url, name: '' })"
          >
            <div class="aspect-square bg-black/30 relative">
              <img
                v-if="a.kind === 'image'"
                :src="a.url"
                alt=""
                loading="lazy"
                decoding="async"
                class="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
              />
              <div v-else class="absolute inset-0">
                <video
                  :src="a.url"
                  muted
                  playsinline
                  preload="metadata"
                  class="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity pointer-events-none"
                ></video>
                <div class="absolute inset-0 flex items-center justify-center text-white/70">
                  <i class="fas fa-play-circle text-3xl"></i>
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <div
        v-show="isAgentModel && rightWorkspaceTab === 'canvas'"
        class="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 pointer-events-auto"
      >
        <button
          type="button"
          class="px-4 py-2 rounded-full bg-[#1a1a1f] border border-white/10 text-[11px] text-gray-300 hover:bg-white/[0.08] shadow-xl"
          @click="resetAgentCanvasLayout"
        >
          重置布局
        </button>
      </div>

      <div
        v-show="isAgentModel && rightWorkspaceTab === 'canvas'"
        class="absolute bottom-6 right-4 flex items-center gap-0.5 rounded-xl bg-[#1a1a1f]/95 border border-white/10 px-1.5 py-1 shadow-xl"
      >
        <button
          type="button"
          class="p-1.5 rounded-lg text-[10px] transition-colors"
          :class="agentCanvasTool === 'select' ? 'text-fuchsia-200 bg-white/10' : 'text-gray-500 hover:text-white'"
          title="选择 / 拖拽节点"
          @click="agentCanvasTool = 'select'"
        >
          <i class="fas fa-mouse-pointer"></i>
        </button>
        <button
          type="button"
          class="p-1.5 rounded-lg text-[10px] transition-colors"
          :class="agentCanvasTool === 'hand' ? 'text-fuchsia-200 bg-white/10' : 'text-gray-500 hover:text-white'"
          title="平移画布"
          @click="agentCanvasTool = 'hand'"
        >
          <i class="fas fa-hand-paper"></i>
        </button>
        <span class="w-px h-4 bg-white/10 mx-1"></span>
        <button type="button" class="px-1.5 text-gray-500 hover:text-white text-xs" @click="agentCanvasZoomDelta(-1)">
          −
        </button>
        <span class="text-[10px] text-gray-400 tabular-nums px-1 min-w-[2.5rem] text-center">{{
          agentCanvasZoomPctLabel
        }}</span>
        <button type="button" class="px-1.5 text-gray-500 hover:text-white text-xs" @click="agentCanvasZoomDelta(1)">
          +
        </button>
      </div>
    </section>

    <!-- 节点：编辑补充指令 -->
    <div
      v-if="agentCanvasEditTarget"
      class="fixed inset-0 z-[1300] bg-black/60 backdrop-blur-[2px] flex items-center justify-center p-6"
      @click.self="closeAgentCanvasEdit"
    >
      <div
        class="w-full max-w-md rounded-2xl border border-white/10 bg-[#1a1a1f] shadow-2xl p-5"
        @click.stop
      >
        <div class="text-sm font-bold text-gray-100 mb-1">编辑本步补充指令</div>
        <div class="text-[11px] text-gray-500 mb-3">将附加在用户原始需求后，仅影响当前专家重生成。</div>
        <textarea
          v-model="agentCanvasEditNote"
          rows="5"
          class="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-[12px] text-gray-200 outline-none focus:border-fuchsia-500/40"
          placeholder="例如：加强喜剧节奏 / 主角改为女性 / 减少暴力描写…"
        ></textarea>
        <div class="mt-4 flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 rounded-xl text-[12px] text-gray-400 hover:text-white"
            @click="closeAgentCanvasEdit"
          >
            取消
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-xl text-[12px] font-semibold bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white"
            @click="confirmAgentCanvasEditRegenerate"
          >
            保存并重生成
          </button>
        </div>
      </div>
    </div>

    <!-- 图片预览（站内预览，避免点击触发下载） -->
    <div
      v-if="showImagePreview"
      class="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-[2px] flex items-center justify-center p-6"
      @click="closeImagePreview"
    >
      <div class="relative max-w-[min(92vw,1100px)] max-h-[88vh]" @click.stop>
        <button
          type="button"
          class="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-black/60 border border-white/10 text-white/80 hover:text-white hover:bg-black/80 transition-colors"
          title="关闭"
          @click="closeImagePreview"
        >
          <i class="fas fa-times"></i>
        </button>
        <img
          v-if="previewImageUrl"
          :src="previewImageUrl"
          alt=""
          decoding="async"
          class="max-w-full max-h-[88vh] object-contain rounded-2xl shadow-2xl"
        />
      </div>
    </div>

    <!-- 媒体预览：视频弹窗自动播放（独立于图片预览，避免无法弹出） -->
    <div
      v-if="showMediaPreview"
      class="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-[2px] flex items-center justify-center p-6"
      @click="closeMediaPreview"
    >
      <div class="relative max-w-[min(92vw,1100px)] max-h-[88vh]" @click.stop>
        <button
          type="button"
          class="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-black/60 border border-white/10 text-white/80 hover:text-white hover:bg-black/80 transition-colors"
          title="关闭"
          @click="closeMediaPreview"
        >
          <i class="fas fa-times"></i>
        </button>
        <video
          v-if="previewMediaKind === 'video' && previewMediaUrl"
          :src="previewMediaUrl"
          controls
          autoplay
          muted
          playsinline
          class="max-w-full max-h-[88vh] object-contain rounded-2xl shadow-2xl bg-black"
        ></video>
      </div>
    </div>

    <ComposerAtMentionPanel
      ref="atMentionPanel"
      :visible="atMentionOpen"
      :items="atMentionItems"
      :active-index="atMentionIndex"
      :query="atMentionQuery"
      :panel-style="atMentionPanelStyle"
      :loading="atMentionLoading"
      @pick="pickAtMentionItem"
      @hover-index="atMentionIndex = $event"
    />
  </div>
</template>

<script>
import { createSeedanceVideoTask, getSeedanceVideoTask } from '@/utils/seedanceVideoApi'
import ConversationWorkflowSidebar from '@/components/ConversationWorkflowSidebar.vue'
import {
  saveChatWorkflowSession,
  fetchChatWorkflowSessionDetail,
  fetchChatWorkflowSessionList
} from '@/utils/chatWorkflowHistoryApi'
import { showErrorToast, showSuccessToast } from '@/utils/toast'
import { generateSeedreamImages } from '@/utils/seedreamImageApi'
import { addAssetsToLibrary } from '@/utils/assetLibraryApi'
import { fetchAssetLibraryList } from '@/utils/assetLibraryApi'
import { uploadVideoImage, uploadVideoMediaFile } from '@/utils/videoImageApi'
import { streamWorkspaceReply } from '@/utils/workspaceStreamChat'
import { consumeWorkflowPromptHandoff } from '@/utils/workflowPromptHandoff'
import { persistWorkflowUploadUrl } from '@/utils/workflowMediaPersist'
import composerAtMentionMixin from '@/mixins/composerAtMentionMixin'
import { getComposerPlainText, hasInlineUploadByUrl } from '@/utils/workflowComposerInlineUpload'
import api from '@/utils/api'
import {
  buildWorkflowStylePromptSuffix,
  formatWorkflowStyleMeta,
  mergeWorkflowStyleSearchResponse,
  normalizeWorkflowStyleItem,
  pickWorkflowStyleRef,
  postWorkflowStyleSearch
} from '@/utils/workflowStyleApi'
import marked from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({ gfm: true, breaks: true })

function genId() {
  return `cw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

/** 去掉 data:image/*;base64,...（历史会话里若把大图塞进 HTML，会导致刷新/渲染极慢） */
function stripDataImageUrls(s) {
  const raw = s == null ? '' : String(s)
  if (!raw || raw.indexOf('data:image/') === -1) return raw
  try {
    return raw.replace(/data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=\r\n]+/gi, '')
  } catch {
    return raw
  }
}

/** 火山方舟等返回的英文任务状态 → 界面中文 */
function seedanceStatusZh(statusRaw) {
  const raw = String(statusRaw == null ? '' : statusRaw).trim()
  const s = raw.toLowerCase().replace(/\s+/g, '_')
  if (!s) return '未知'
  const map = {
    succeeded: '已完成',
    success: '已完成',
    complete: '已完成',
    completed: '已完成',
    failed: '失败',
    failure: '失败',
    error: '失败',
    cancelled: '已取消',
    canceled: '已取消',
    running: '进行中',
    processing: '处理中',
    in_progress: '进行中',
    working: '进行中',
    queued: '排队中',
    queueing: '排队中',
    pending: '等待中',
    waiting: '等待中',
    submitted: '已提交',
    created: '已创建',
    expired: '已过期',
    unknown: '未知'
  }
  return map[s] || raw || '未知'
}

function buildVideoApiContent(userText) {
  return `【文生视频】\n请根据以下描述生成视频。\n\n用户输入：\n${userText}`
}

function newSessionUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `cw-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

function safeJsonParse(s, fallback) {
  try {
    const v = JSON.parse(s)
    return v == null ? fallback : v
  } catch {
    return fallback
  }
}

function pickSeedreamImageUrls(out) {
  const urls = []
  const dataArr = Array.isArray(out?.data)
    ? out.data
    : Array.isArray(out?.result)
      ? out.result
      : Array.isArray(out?.images)
        ? out.images
        : Array.isArray(out?.data?.data)
          ? out.data.data
          : []
  for (const row of dataArr || []) {
    if (!row) continue
    if (typeof row === 'string' && row.trim()) {
      urls.push(row.trim())
      continue
    }
    const u =
      (typeof row.url === 'string' && row.url.trim() ? row.url.trim() : '') ||
      (typeof row.image_url === 'string' && row.image_url.trim() ? row.image_url.trim() : '') ||
      (typeof row.imageUrl === 'string' && row.imageUrl.trim() ? row.imageUrl.trim() : '')
    if (u) {
      urls.push(u)
      continue
    }
    const b64 =
      (typeof row.b64_json === 'string' && row.b64_json.trim() ? row.b64_json.trim() : '') ||
      (typeof row.b64 === 'string' && row.b64.trim() ? row.b64.trim() : '') ||
      ''
    if (b64) {
      // 默认 png；若你后端能返回 mime，再精确处理
      urls.push('data:image/png;base64,' + b64)
    }
  }
  return urls
}

/** 仅持久化可 JSON 化的消息字段 */
function cloneMessagesForStorage(messages) {
  return (messages || []).map(m => ({
    id: m.id != null ? String(m.id) : genId(),
    role: m.role,
    content: typeof m.content === 'string' ? m.content : '',
    contentPlain: typeof m.contentPlain === 'string' ? m.contentPlain : '',
    contentHtml: typeof m.contentHtml === 'string' ? m.contentHtml : '',
    kind: m.kind,
    uploads: Array.isArray(m.uploads)
      ? m.uploads
          .filter(u => u && typeof u === 'object')
          .map(u => ({
            kind: u.kind === 'video' ? 'video' : 'image',
            url: typeof u.url === 'string' ? u.url : '',
            name: typeof u.name === 'string' ? u.name : ''
          }))
          .filter(u => u.url)
      : [],
    referenceImages: Array.isArray(m.referenceImages)
      ? m.referenceImages.filter(x => typeof x === 'string' && x.trim()).map(x => String(x))
      : [],
    images: Array.isArray(m.images) ? m.images.filter(u => typeof u === 'string' && u.trim()).map(u => String(u)) : [],
    paramRatio: m.paramRatio,
    paramDuration: m.paramDuration,
    paramResolution: m.paramResolution,
    resolved: !!m.resolved,
    cancelled: !!m.cancelled,
    streaming: false,
    videoUrl: typeof m.videoUrl === 'string' ? m.videoUrl : '',
    // Seedance 任务恢复：保存 taskId / status
    taskId: typeof m.taskId === 'string' ? m.taskId : '',
    taskStatus: typeof m.taskStatus === 'string' ? m.taskStatus : '',
    // Seedream 任务恢复：保存请求参数（刷新后自动重试）
    seedreamJob: m.seedreamJob && typeof m.seedreamJob === 'object' ? m.seedreamJob : null,
    apiContent: typeof m.apiContent === 'string' ? m.apiContent : ''
  }))
}

export default {
  name: 'ConversationWorkflow',
  components: { ConversationWorkflowSidebar },
  mixins: [composerAtMentionMixin],
  data() {
    return {
      atMentionComposerRef: 'bottomComposer',
      sessionList: [],
      sessionListLoading: false,
      sessionListError: '',
      bottomInput: '',
      composerIsEmpty: true,
      chatMessages: [],
      /** 聊天消息渲染：大数据时增量加载，减少首屏卡顿 */
      chatRenderLimit: 20,
      chatRenderStep: 40,
      _loadingMoreChat: false,
      /** UI：上拉加载时转圈（避免加载过快看不到） */
      loadingMoreUi: false,
      loadingMoreUiTimer: null,
      loadingMoreUiSince: 0,
      /** 后台上传中的 blob/dataURL 数量：>0 时延迟自动保存，避免把 blob 写进会话 */
      pendingPersistUploads: 0,
      /** 服务端 tail 拉取（会话很大时避免一次性加载） */
      serverTail: 20,
      serverHasMore: false,
      serverTotalChatMessages: 0,
      isStreaming: false,
      streamAbort: null,
      /** 刷新恢复：正在轮询的 Seedance 任务集合 */
      _resumingSeedanceTaskIds: {},
      _resumingSeedreamMsgIds: {},
      /** Agent：等待用户确认“剧本风格” */
      agentPendingPrompt: '',
      agentPendingIsNewSession: false,
      agentPendingFinalPrompt: '',
      agentPendingScript: '',
      agentPendingVideoParams: null,
      agentPendingPlan: null,
      agentPendingCharacterImages: [],
      /** 发送文案后，等待用户在气泡内确认比例、时长与分辨率 */
      awaitingVideoParams: false,
      /** Agent：前端模拟流式打字（艺术总监/规划说明） */
      _agentTypingTimers: {},
      /** Agent：角色设计图任务轮询（后端分步出图，避免 502） */
      _agentCharImgTaskTimers: {},
      _agentCharImgTaskSeen: {},
      /** 文生视频参数（传给后端：text 后缀 --dur/--ratio，JSON `resolution`） */
      videoDuration: 5,
      videoRatio: '16:9',
      videoResolution: '720p',

      /** Agent²：可选对话模型；含 DeepSeek OpenAI 兼容 API（需 DEEPSEEK_API_KEY） */
      agent2ModelPresetOptions: [
        { value: '', label: '后台默认（Agent2ChatModelId → 一键 Agent 模型）', baseUrl: '' },
        { value: 'doubao-seed-character-251128', label: 'Doubao Seed Character（251128）', baseUrl: '' },
        {
          value: 'deepseek-v4-pro',
          label: 'DeepSeek V4 Pro（api.deepseek.com）',
          baseUrl: 'https://api.deepseek.com'
        },
        {
          value: 'deepseek-v4-flash',
          label: 'DeepSeek V4 Flash（api.deepseek.com）',
          baseUrl: 'https://api.deepseek.com'
        },
        { value: '__custom__', label: '自定义方舟 Endpoint ID…', baseUrl: '' }
      ],

      /** 与创作中心「对话工作流记录」联动；URL ?session=uuid */
      sessionUuid: '',
      _persistTimer: null,
      _persistBackoffAttempts: 0,
      _loadingRemoteSession: false,

      /** 底部模型选择（UI） */
      showModelPicker: false,
      modelTab: 'image',
      smartModel: true,
      selectedModelId: 'nano-pro',
      modelOptions: [
        {
          id: 'agent',
          kind: 'image',
          name: 'Agent',
          desc: '一键 Agent 或 Agent²（智剧通八专家分工）完成剧本→分镜→出图→视频',
          badge: 'New',
          icon: 'fas fa-robot'
        },
        {
          id: 'nano-2',
          kind: 'image',
          name: 'Nano 2',
          desc: '速度快，综合生图最好',
          icon: 'fas fa-bolt'
        },
        {
          id: 'midjourney-nij7',
          kind: 'image',
          name: 'Midjourney nij7',
          desc: '动漫美学最佳，无角色参考',
          icon: 'fas fa-hat-wizard'
        },
        {
          id: 'nano-pro',
          kind: 'image',
          name: 'Nano Pro',
          desc: '综合生图能力最好',
          badge: 'Best',
          icon: 'fas fa-crown'
        },
        {
          id: 'seedream-5',
          kind: 'image',
          name: 'Seedream 5.0',
          desc: '最懂亚洲人审美，理解能力更强',
          icon: 'fas fa-wave-square'
        },
        {
          id: 'character-designer',
          kind: 'image',
          name: '角色设计图',
          desc: '角色三视图/服装变体/色板/表情/动作（Seedream）',
          badge: 'New',
          icon: 'fas fa-user-astronaut'
        },
        {
          id: 'script-writer',
          kind: 'text',
          name: '剧本创作',
          desc: '生成剧本/分镜/台词（走对话模型）',
          badge: 'New',
          icon: 'fas fa-pen-nib'
        },
        {
          id: 'seedance-video',
          kind: 'video',
          name: 'Seedance',
          desc: '文生视频任务（对话工作流）',
          icon: 'fas fa-video'
        }
      ],

      /** 风格库（UI） */
      showStyleLibrary: false,
      /** 风格库分类（后端接口：/normal/categories） */
      categoryList: [],
      currentCategoryId: null,
      _categoryLoading: false,
      /** 分类下风格列表（后端接口：POST /search） */
      categoryStyleMap: {},
      styleCategoryLoading: false,
      styleTab: 'all',
      selectedStyle: null,
      styleMine: [],
      styleRecent: [],
      _styleMineLoaded: false,
      _styleRecentLoaded: false,
      _styleScrollRaf: 0,
      _styleLastAutoLoadAt: 0,
      styleTabs: [
        { id: 'all', label: '全部' },
        { id: 'mine', label: '我的风格' },
        { id: 'recent', label: '最近使用' },
        { id: 'ip', label: 'IP风格' }
      ],
      styleOptions: [
        // {
        //   id: 'kpopcg',
        //   tab: 'all',
        //   name: 'KpopCG',
        //   badge: 'K',
        //   desc: '韩系舞台质感',
        //   prompt: 'k-pop cg, clean layout, bright studio lighting, glossy details'
        // },
        // {
        //   id: 'twin-city',
        //   tab: 'all',
        //   name: '双城风格',
        //   badge: 'D',
        //   desc: '蒸汽+魔幻质感',
        //   prompt: 'arcane-inspired, painterly, cinematic lighting, detailed textures'
        // },
        // {
        //   id: 'wild-west',
        //   tab: 'all',
        //   name: '荒野大表哥',
        //   badge: 'W',
        //   desc: '写实西部电影感',
        //   prompt: 'western realism, cinematic, dusty atmosphere, film still, high detail'
        // },
        // {
        //   id: 'pixel-farm',
        //   tab: 'all',
        //   name: '像素农场',
        //   badge: 'P',
        //   desc: '像素/复古游戏',
        //   prompt: 'pixel art, retro game style, crisp pixels, limited palette'
        // },
        // {
        //   id: 'game-cg',
        //   tab: 'all',
        //   name: '游戏CG',
        //   badge: 'G',
        //   desc: '写实概念设计',
        //   prompt: 'game cg, concept art, ultra detailed, sharp lines, studio lighting'
        // },
        // {
        //   id: '3dz',
        //   tab: 'all',
        //   name: '3DZ之游',
        //   badge: '3D',
        //   desc: '3D写实角色',
        //   prompt: '3d character render, realistic materials, soft rim light, high poly'
        // },
        // {
        //   id: 'ai-real',
        //   tab: 'all',
        //   name: 'AI真人',
        //   badge: 'R',
        //   desc: '偏真人写真感',
        //   prompt: 'photorealistic, soft skin detail, natural light, clean background'
        // },
        // {
        //   id: '2d-comic',
        //   tab: 'all',
        //   name: '2D漫画',
        //   badge: '2D',
        //   desc: '二次元线稿上色',
        //   prompt: '2d anime, clean lineart, flat colors, crisp shading'
        // },
        // {
        //   id: '3-struct',
        //   tab: 'all',
        //   name: '三构造二',
        //   badge: 'C',
        //   desc: '结构清晰',
        //   prompt: 'clear construction, design sheet, orthographic views, tidy typography'
        // },
        // {
        //   id: 'gta',
        //   tab: 'all',
        //   name: '给他爱',
        //   badge: 'A',
        //   desc: '都市电影感',
        //   prompt: 'urban cinematic, dramatic contrast, film grain, realistic'
        // },
        // {
        //   id: '3d-cn',
        //   tab: 'all',
        //   name: '3D国创',
        //   badge: 'CN',
        //   desc: '国风3D动画',
        //   prompt: 'chinese 3d animation style, soft lighting, elegant colors'
        // }
      ],

      /** 图片预览 */
      showImagePreview: false,
      previewImageUrl: ''
      ,

      /** 媒体预览（视频） */
      showMediaPreview: false,
      previewMediaUrl: '',
      previewMediaKind: 'video'
      ,

      /** 本地上传：仅用于前端预览（blob: URL） */
      localUploads: [],
      _localObjectUrls: [],
      /** 已引用到输入框的附件（用于预览，不往 textarea 塞链接） */
      referencedUploads: [],

      /** 我的资产弹层 */
      showAssetPanel: false,
      assetTab: 'image',
      assetKeyword: '',
      assetRows: [],
      assetLoading: false,
      assetError: '',
      assetPanelStyle: {
        left: '0px',
        top: '0px',
        width: '720px'
      },

      /** Agent 模式：右侧智剧节点画布（与 Agent² 步骤同步） */
      rightWorkspaceTab: 'canvas',
      agentCanvasNodes: [],
      agentCanvasPan: { x: 100, y: 60 },
      agentCanvasScale: 1,
      agentCanvasTool: 'hand',
      agentCanvasSelectedId: null,
      agentCanvasRunContext: null,
      agentCanvasPanning: null,
      agentCanvasNodeDragging: null,
      agentCanvasEditTarget: null,
      agentCanvasEditNote: '',
      _agentCanvasWinMove: null,
      _agentCanvasWinUp: null
    }
  },
  computed: {
    styleCategoryTabs() {
      // 优先使用后端分类；没有数据时回退到原静态 tabs
      const list = Array.isArray(this.categoryList) ? this.categoryList : []
      if (list.length > 0) {
        const fixed = [
          { id: 'all', label: '全部' },
          { id: 'mine', label: '我的风格' },
          { id: 'recent', label: '最近使用' }
        ]
        return fixed.concat(
          list
          .filter(x => x && (x.id != null))
          .map(x => ({
            id: String(x.id),
            label: String(x.name || x.title || x.label || '未命名')
          }))
        )
      }
      return Array.isArray(this.styleTabs) ? this.styleTabs : []
    },
    shouldShowStyleLoadMore() {
      const hasCats = Array.isArray(this.categoryList) && this.categoryList.length > 0
      if (!hasCats) return false
      const tab = String(this.styleTab || '')
      if (!tab || tab === 'mine' || tab === 'recent' || tab === 'ip') return false
      const cur = this.categoryStyleMap && this.categoryStyleMap[tab]
      if (!cur) return true
      return !cur.finished
    },
    filteredStyles() {
      const tab = String(this.styleTab || 'all')
      const builtins = Array.isArray(this.styleOptions) ? this.styleOptions : []
      const mine = Array.isArray(this.styleMine) ? this.styleMine : []
      const recent = Array.isArray(this.styleRecent) ? this.styleRecent : []
      const hasCats = Array.isArray(this.categoryList) ? this.categoryList.length > 0 : false
      if (tab === 'mine') return mine
      if (tab === 'recent') return recent
      if (!hasCats) {
        if (tab === 'ip') {
          const hit = builtins.filter(s => String(s.tab || 'all') === 'ip')
          return hit
        }
        // all：内置 + 我的风格（便于快速选）
        return mine.concat(builtins)
      }
      // 有接口分类时：tab 为分类 id，则展示该分类下的接口列表
      const bucket = this.categoryStyleMap && this.categoryStyleMap[tab]
      const remote = bucket && Array.isArray(bucket.list) ? bucket.list : []
      return remote
    },
    videoResolutionPresets() {
      return ['480p', '720p', '1080p']
    },
    shouldShowLoadMoreHint() {
      const list = Array.isArray(this.chatMessages) ? this.chatMessages : []
      const limit = Math.max(0, parseInt(this.chatRenderLimit, 10) || 0)
      // 1) 服务端还有更早历史；或 2) 已拉取到的消息还没完全渲染出来（本地增量渲染）
      return !!this.serverHasMore || (limit > 0 && list.length > limit)
    },
    visibleChatMessages() {
      const list = Array.isArray(this.chatMessages) ? this.chatMessages : []
      const limit = Math.max(0, parseInt(this.chatRenderLimit, 10) || 0)
      if (!limit || list.length <= limit) return list
      return list.slice(list.length - limit)
    },
    taskTitle() {
      const firstUser = this.chatMessages.find(m => m.role === 'user')
      const raw =
        (firstUser && (firstUser.contentPlain || firstUser.content)) != null
          ? firstUser.contentPlain || firstUser.content
          : ''
      const p = String(raw).trim()
      if (p) return p.length > 28 ? p.slice(0, 28) + '…' : p
      return '对话工作流 · 文生视频'
    },
    selectedModelName() {
      const m = (this.modelOptions || []).find(x => x.id === this.selectedModelId)
      return (m && m.name) || '选择模型'
    },
    filteredModelOptions() {
      const tab = ['image', 'video', 'text'].includes(this.modelTab) ? this.modelTab : 'image'
      return (this.modelOptions || []).filter(m => m.kind === tab)
    },
    isAgentModel() {
      return this.selectedModelId === 'agent'
    },
    agentCanvasEdgePaths() {
      const nodes = Array.isArray(this.agentCanvasNodes) ? this.agentCanvasNodes : []
      const out = []
      const cardW = 360
      const bottomPad = 168
      for (let i = 0; i < nodes.length - 1; i++) {
        const a = nodes[i]
        const b = nodes[i + 1]
        const x1 = a.x + cardW / 2
        const y1 = a.y + bottomPad
        const x2 = b.x + cardW / 2
        const y2 = b.y + 28
        const mid = (y1 + y2) / 2
        out.push({
          key: `${a.id}-${b.id}`,
          d: `M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`
        })
      }
      return out
    },
    agentCanvasZoomPctLabel() {
      return `${Math.round((this.agentCanvasScale || 1) * 100)}%`
    },
    agentCanvasTransformStyle() {
      const p = this.agentCanvasPan || { x: 0, y: 0 }
      const s = this.agentCanvasScale || 1
      return {
        transform: `translate(${p.x}px, ${p.y}px) scale(${s})`,
        transformOrigin: '0 0'
      }
    },
    rightAssets() {
      const out = []
      const seen = new Set()
      const push = (kind, url) => {
        const u = typeof url === 'string' ? url.trim() : ''
        if (!u) return
        const k = kind + ':' + u
        if (seen.has(k)) return
        seen.add(k)
        out.push({ kind, url: u })
      }
      for (const u of this.localUploads || []) {
        if (!u) continue
        push(u.kind === 'video' ? 'video' : 'image', u.url)
      }
      for (const m of this.chatMessages || []) {
        if (!m || m.role !== 'assistant') continue
        if (m.videoUrl) push('video', m.videoUrl)
        if (Array.isArray(m.images)) {
          for (const u of m.images) push('image', u)
        }
      }
      return out.reverse()
    },
    filteredAssetRows() {
      const tab = this.assetTab || 'all'
      const kw = (this.assetKeyword || '').toLowerCase()
      return (this.assetRows || [])
        .filter(r => {
          if (tab === 'image') return (r.kind || 'image') === 'image'
          if (tab === 'scene') return (r.kind || '') === 'scene'
          return true
        })
        .filter(r => {
          if (!kw) return true
          return String(r.title || '').toLowerCase().includes(kw)
        })
    }
  },
  watch: {
    '$route.fullPath'() {
      this.loadSessionFromRoute()
      this.loadSessionList()
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onGlobalKeydown)
    window.addEventListener('resize', this.onGlobalResize)
    // 风格库本地数据改为懒加载：打开风格库/点击 tab 再读 localStorage
    this.loadSessionList()
    this.loadSessionFromRoute()
    this.$nextTick(() => {
      this.optimizeMediaElements(this.$refs.chatScroll)
      this.setupChatMediaObservers()
      this.applyPromptHandoffFromPlus()
    })
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onGlobalKeydown)
    window.removeEventListener('resize', this.onGlobalResize)
    this.teardownAgentCanvasListeners()
    this.teardownChatMediaObservers()
    try {
      if (this._styleScrollRaf) cancelAnimationFrame(this._styleScrollRaf)
    } catch {
      // ignore
    } finally {
      this._styleScrollRaf = 0
    }
    try {
      if (this.loadingMoreUiTimer) clearTimeout(this.loadingMoreUiTimer)
    } catch {
      // ignore
    } finally {
      this.loadingMoreUiTimer = null
    }

    // 清理 Agent 打字机定时器
    try {
      const mp = this._agentTypingTimers || {}
      for (const k of Object.keys(mp)) {
        try {
          clearInterval(mp[k])
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    } finally {
      this._agentTypingTimers = {}
    }

    // 清理 Agent 角色出图任务轮询
    try {
      const mp2 = this._agentCharImgTaskTimers || {}
      for (const k of Object.keys(mp2)) {
        try {
          clearTimeout(mp2[k])
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    } finally {
      this._agentCharImgTaskTimers = {}
      this._agentCharImgTaskSeen = {}
    }
  },
  methods: {
    startAgentCharacterImagesTaskPolling({ taskId, plan }) {
      const id = taskId != null ? String(taskId).trim() : ''
      if (!id) return
      if (!this._agentCharImgTaskTimers) this._agentCharImgTaskTimers = {}
      if (!this._agentCharImgTaskSeen) this._agentCharImgTaskSeen = {}
      if (this._agentCharImgTaskTimers[id]) return // 已在轮询

      // 记录 plan（用于后续生成视频任务的 storyboard_video_prompt_cn）
      if (plan && typeof plan === 'object') {
        this.agentPendingPlan = plan
      }
      this.agentPendingCharacterImages = []

      // 初始化已见集合：避免重复插入同一角色图片
      this._agentCharImgTaskSeen[id] = {}

      // 插入一个进度气泡（避免“无加载过程”的观感）
      const progressMsg = {
        id: genId(),
        role: 'assistant',
        kind: 'agent_progress',
        streaming: true,
        content: '正在生成角色设计图…'
      }
      this.chatMessages.push(progressMsg)
      this.scrollChatBottom()

      const tick = async () => {
        try {
          const res = await api.get(`/agent_character_images_task/${encodeURIComponent(id)}`)
          const payload0 = res && res.data ? res.data : res
          const payload = payload0 && payload0.data && typeof payload0.data === 'object' ? payload0.data : payload0
          const items = payload && Array.isArray(payload.items) ? payload.items : []
          const status = payload && payload.status ? String(payload.status).toLowerCase() : ''
          const done = payload && payload.done != null ? Number(payload.done) : 0
          const total = payload && payload.total != null ? Number(payload.total) : (Array.isArray(items) ? items.length : 0)
          const failed = payload && payload.failed != null ? Number(payload.failed) : 0

          // 更新进度气泡
          progressMsg.content = `正在生成角色设计图…（${Math.max(0, done)}/${Math.max(0, total)}）` + (failed ? `\n\n失败：${failed}` : '')
          this.scrollChatBottom()

          const seen = this._agentCharImgTaskSeen && this._agentCharImgTaskSeen[id] ? this._agentCharImgTaskSeen[id] : {}
          for (const it of items) {
            if (!it || typeof it !== 'object') continue
            const name = it.name != null ? String(it.name) : ''
            const st = it.status != null ? String(it.status) : ''
            const urls = Array.isArray(it.images) ? it.images.filter(u => typeof u === 'string' && u.trim()).map(u => u.trim()) : []
            if (!urls.length) continue
            const key = `${name}::${urls[0]}`
            if (seen[key]) continue
            seen[key] = 1

            // 每个角色取第一张作为参考图
            this.agentPendingCharacterImages.push({ name, url: urls[0] })
            this.chatMessages.push({
              id: genId(),
              role: 'assistant',
              kind: 'images',
              content: name ? `角色「${name}」设计图已生成。` : '角色设计图已生成。',
              streaming: false,
              images: urls
            })
            this.scrollChatBottom()
          }
          this._agentCharImgTaskSeen[id] = seen

          if (status === 'done') {
            // 停止轮询
            try {
              clearTimeout(this._agentCharImgTaskTimers[id])
            } catch {
              // ignore
            }
            delete this._agentCharImgTaskTimers[id]

            progressMsg.streaming = false
            if (Array.isArray(this.agentPendingCharacterImages) && this.agentPendingCharacterImages.length) {
              progressMsg.content = `角色设计图已生成（${Math.max(0, done)}/${Math.max(0, total)}）。` + (failed ? `\n\n失败：${failed}` : '')
            } else {
              const firstBad = items.find(x => x && x.status === 'failed' && (x.error || x.error_detail))
              const why = firstBad
                ? `\n\n失败示例：${String(firstBad.error || '').slice(0, 120)}${firstBad.error_detail ? `\n${String(firstBad.error_detail).slice(0, 240)}` : ''}`
                : ''
              progressMsg.content =
                `角色设计图生成完成，但未返回图片（${Math.max(0, done)}/${Math.max(0, total)}，失败：${failed || 0}）。` +
                `\n\n请检查后端 Seedream 配置/鉴权/模型ID/限流。` +
                why
            }
            this.scrollChatBottom()

            const syntheticCi = Array.isArray(items)
              ? items.map(it => ({
                  name: it && it.name != null ? String(it.name) : '',
                  images: (Array.isArray(it.images) ? it.images : [])
                    .filter(u => typeof u === 'string' && u.trim())
                    .map(u => ({ url: u.trim() }))
                }))
              : []
            this.attachAgentCanvasCharacterImages(syntheticCi)

            // 弹出确认卡片（只要有至少一张图）
            if (Array.isArray(this.agentPendingCharacterImages) && this.agentPendingCharacterImages.length) {
              this.chatMessages.push({
                id: genId(),
                role: 'assistant',
                kind: 'agent_character_confirm'
              })
              this.scrollChatBottom()
            }
            return
          }
        } catch (e) {
          // 遇到网络/后端抖动：延后重试，不中断整个流程
          const msg = e?.response?.data?.message || e?.message || '轮询失败'
          progressMsg.content = `正在生成角色设计图…\n\n（轮询失败：${String(msg).slice(0, 200)}，将自动重试）`
          this.scrollChatBottom()
        }

        this._agentCharImgTaskTimers[id] = setTimeout(tick, 2200)
      }

      this._agentCharImgTaskTimers[id] = setTimeout(tick, 800)
    },
    startAgentTyping(msg, fullText, msPerChar = 16) {
      if (!msg || !msg.id) return
      const id = String(msg.id)
      const text = fullText != null ? String(fullText) : ''
      // 防止重复启动
      if (!this._agentTypingTimers) this._agentTypingTimers = {}
      if (this._agentTypingTimers[id]) return

      msg.streaming = true
      msg.content = ''
      let i = 0

      const tick = () => {
        // 每 tick 输出少量字符，增强“流式”观感
        const step = 2
        i = Math.min(text.length, i + step)
        msg.content = text.slice(0, i)
        this.scrollChatBottom()
        if (i >= text.length) {
          try {
            clearInterval(this._agentTypingTimers[id])
          } catch {
            // ignore
          }
          delete this._agentTypingTimers[id]
          msg.streaming = false
          this.scrollChatBottom()
        }
      }

      this._agentTypingTimers[id] = setInterval(tick, Math.max(8, msPerChar))
      tick()
    },
    toggleAgentArtDirector(m) {
      if (!m || m.kind !== 'agent_art_director') return
      try {
        this.$set(m, 'collapsed', !m.collapsed)
      } catch {
        m.collapsed = !m.collapsed
      }
    },
    async requestAgentStyleOptions({ prompt }) {
      const p = prompt != null ? String(prompt).trim() : ''
      if (!p) return
      const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
      const stylePrompt = this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : ''
      const loadingMsg = {
        id: genId(),
        role: 'assistant',
        content: '正在为你的题材匹配可选剧情风格…',
        streaming: true
      }
      this.chatMessages.push(loadingMsg)
      this.scrollChatBottom()
      try {
        const res = await api.post('/agent_style_options', {
          prompt: p,
          ...(styleName ? { style_name: styleName } : {}),
          ...(stylePrompt ? { style_prompt: stylePrompt } : {})
        })
        const payload0 = res && res.data ? res.data : res
        const payload = payload0 && payload0.data && typeof payload0.data === 'object' ? payload0.data : payload0
        const title = payload && payload.title ? String(payload.title) : ''
        const options = payload && Array.isArray(payload.options) ? payload.options : []
        // 替换 loading 为 picker
        loadingMsg.streaming = false
        loadingMsg.kind = 'agent_style_picker'
        loadingMsg.title = title
        loadingMsg.options = options
        loadingMsg.content = ''
        this.scrollChatBottom()
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || '获取风格选项失败'
        loadingMsg.streaming = false
        loadingMsg.content = `获取风格选项失败：${msg}`
        showErrorToast('获取风格选项失败')
      }
    },
    confirmAgentStyle(m, opt) {
      if (!m || m.kind !== 'agent_style_picker' || !opt) return
      // 选中即锁定：已选过就不允许再改选
      if (m._locked || m.selectedOptId != null) return
      const base = String(this.agentPendingPrompt || '').trim()
      if (!base) {
        showErrorToast('缺少 Agent 输入内容')
        return
      }
      // 把“剧情风格”写进 prompt（后端返回的 prompt_hint 优先）
      const hint = opt && opt.prompt_hint ? String(opt.prompt_hint).trim() : ''
      const styleLine = hint
        ? `【剧情风格】${hint}`
        : `【剧情风格】${String(opt.title || '').trim()}：${String(opt.desc || '').trim()}`
      const finalPrompt = `${base}\n\n${styleLine}`
      this.agentPendingFinalPrompt = finalPrompt
      // 允许重复切换风格：记录当前选择（用于高亮/恢复），但不把 picker 锁死为 resolved
      try {
        this.$set(m, 'selectedOptId', opt.id)
      } catch {
        m.selectedOptId = opt.id
      }
      try {
        this.$set(m, '_locked', true)
      } catch {
        m._locked = true
      }

      // 下一步：确认视频参数（比例/长度/语言）
      // 只创建一次参数气泡；后续点击仅更新 prompt，不重复插入新气泡
      const existingId = m && m._videoOptionsMsgId ? String(m._videoOptionsMsgId) : ''
      let existing = null
      if (existingId) {
        existing = (this.chatMessages || []).find(x => x && String(x.id) === existingId) || null
      }
      const shouldCreate = !existing || existing.kind !== 'agent_video_options' || !!existing.resolved
      if (shouldCreate) {
        const nextId = genId()
        const msg = {
          id: nextId,
          role: 'assistant',
          kind: 'agent_video_options',
          videoLength: 'short',
          paramRatio: '9:16',
          dialogLang: 'zh',
          pipelineMode: 'classic',
          agent2ModelPreset: '',
          agent2ModelCustom: '',
          agent2ChatBaseUrl: '',
          resolved: false,
          cancelled: false
        }
        this.chatMessages.push(msg)
        try {
          this.$set(m, '_videoOptionsMsgId', nextId)
        } catch {
          m._videoOptionsMsgId = nextId
        }
        this.scrollChatBottom()
      }
    },
    cancelAgentStyle(m) {
      if (!m || m.kind !== 'agent_style_picker') return
      this.agentPendingPrompt = ''
      this.agentPendingIsNewSession = false
      this.agentPendingFinalPrompt = ''
      // 若已创建“视频参数确认”气泡但尚未确认，则一并移除（避免遗留不可用步骤）
      const vid = m && m._videoOptionsMsgId ? String(m._videoOptionsMsgId) : ''
      if (vid) {
        const idx = (this.chatMessages || []).findIndex(x => x && String(x.id) === vid)
        if (idx >= 0) {
          const vm = this.chatMessages[idx]
          if (vm && vm.kind === 'agent_video_options' && !vm.resolved) {
            this.chatMessages.splice(idx, 1)
          }
        }
      }
      try {
        this.$set(m, 'resolved', true)
      } catch {
        // ignore
      }
      this.chatMessages.push({
        id: genId(),
        role: 'assistant',
        content: '已取消本次 Agent 生成。'
      })
      this.scrollChatBottom()
    },
    setAgent2PipelineMode(m) {
      if (!m) return
      try {
        this.$set(m, 'pipelineMode', 'agent2')
      } catch {
        m.pipelineMode = 'agent2'
      }
      if (m.agent2ModelPreset === undefined) {
        try {
          this.$set(m, 'agent2ModelPreset', '')
        } catch {
          m.agent2ModelPreset = ''
        }
      }
      if (m.agent2ModelCustom === undefined) {
        try {
          this.$set(m, 'agent2ModelCustom', '')
        } catch {
          m.agent2ModelCustom = ''
        }
      }
      if (m.agent2ChatBaseUrl === undefined) {
        try {
          this.$set(m, 'agent2ChatBaseUrl', '')
        } catch {
          m.agent2ChatBaseUrl = ''
        }
      }
    },
    onAgent2ModelPresetChange(m, evt) {
      if (!m || !evt || !evt.target) return
      const v = evt.target.value
      try {
        this.$set(m, 'agent2ModelPreset', v)
      } catch {
        m.agent2ModelPreset = v
      }
      if (v !== '__custom__') {
        try {
          this.$set(m, 'agent2ModelCustom', '')
        } catch {
          m.agent2ModelCustom = ''
        }
      }
      const opt = (this.agent2ModelPresetOptions || []).find(o => o && o.value === v)
      const bu = opt && opt.baseUrl ? String(opt.baseUrl).trim() : ''
      try {
        this.$set(m, 'agent2ChatBaseUrl', bu)
      } catch {
        m.agent2ChatBaseUrl = bu
      }
    },
    /** 解析参数气泡上的 Agent² 模型：有值则传给后端 model，空则走后端默认 */
    resolveAgent2ChatModelFromBubble(m) {
      if (!m || m.pipelineMode !== 'agent2') return ''
      const preset = m.agent2ModelPreset != null ? String(m.agent2ModelPreset) : ''
      if (preset === '__custom__') {
        return String(m.agent2ModelCustom || '').trim()
      }
      return preset.trim()
    },
    agent2ModelSummaryLine(m) {
      if (!m || m.pipelineMode !== 'agent2') return ''
      const id = this.resolveAgent2ChatModelFromBubble(m)
      if (!id) return '模型：后台默认'
      if (m.agent2ModelPreset === '__custom__') return `模型：${id}`
      const opt = this.agent2ModelPresetOptions.find(o => o.value === id)
      return opt ? `模型：${opt.label}` : `模型：${id}`
    },
    confirmAgentVideoParams(m) {
      if (!m || m.resolved || m.kind !== 'agent_video_options') return
      this.$set(m, 'resolved', true)
      const ratio = String(m.paramRatio || '9:16')
      // 后端 Seedance 目前最大 15 秒；长视频先映射为 15 秒跑通流程
      const duration = m.videoLength === 'long' ? 15 : 5
      const resolution = '720p'
      const dialogLang = String(m.dialogLang || 'zh')
      this.agentPendingVideoParams = { ratio, duration, resolution, dialogLang }
      const isNewSession = !!this.agentPendingIsNewSession
      const prompt = String(this.agentPendingFinalPrompt || this.agentPendingPrompt || '').trim()
      const vp = { ratio, duration, resolution, dialogLang }
      if (m.pipelineMode === 'agent2') {
        const chatModel = this.resolveAgent2ChatModelFromBubble(m)
        if (m.agent2ModelPreset === '__custom__' && !chatModel) {
          showErrorToast('请选择模型或填写自定义 Endpoint ID')
          try {
            this.$set(m, 'resolved', false)
          } catch {
            m.resolved = false
          }
          return
        }
        let chatBaseUrl = m.agent2ChatBaseUrl != null ? String(m.agent2ChatBaseUrl).trim() : ''
        if (!chatBaseUrl && m.agent2ModelPreset && m.agent2ModelPreset !== '__custom__') {
          const opt = (this.agent2ModelPresetOptions || []).find(o => o && o.value === m.agent2ModelPreset)
          if (opt && opt.baseUrl) chatBaseUrl = String(opt.baseUrl).trim()
        }
        this.runAgent2Job({
          prompt,
          isNewSession,
          videoParams: vp,
          chatModel,
          chatBaseUrl,
          generateImages: true,
          generateVideoTask: false
        })
        return
      }
      this.runAgentJob({
        prompt,
        isNewSession,
        videoParams: vp,
        stage: 'script'
      })
    },
    cancelAgentVideoParams(m) {
      if (!m || m.resolved || m.kind !== 'agent_video_options') return
      this.$set(m, 'resolved', true)
      this.$set(m, 'cancelled', true)
      this.agentPendingPrompt = ''
      this.agentPendingFinalPrompt = ''
      this.agentPendingIsNewSession = false
      this.agentPendingVideoParams = null
      this.chatMessages.push({
        id: genId(),
        role: 'assistant',
        content: '已取消本次 Agent 生成。'
      })
      this.scrollChatBottom()
    },
    confirmAgentScript(m) {
      if (!m || m.kind !== 'agent_script_confirm') return
      if (m._busy || m.resolved) return
      try {
        this.$set(m, '_busy', true)
      } catch {
        m._busy = true
      }
      const script = String(this.agentPendingScript || '').trim()
      if (!script) {
        showErrorToast('缺少剧本内容')
        try {
          this.$set(m, '_busy', false)
        } catch {
          m._busy = false
        }
        return
      }
      const vp = this.agentPendingVideoParams && typeof this.agentPendingVideoParams === 'object' ? this.agentPendingVideoParams : {}
      // 继续生成其它内容（分镜/角色设计图/视频任务），并把“已确认剧本”传给后端
      const prompt = String(this.agentPendingFinalPrompt || '').trim()
      if (!prompt) {
        showErrorToast('缺少 Agent 上下文，请重新发起一次生成')
        try {
          this.$set(m, '_busy', false)
        } catch {
          m._busy = false
        }
        return
      }
      const isNewSession = !!this.agentPendingIsNewSession
      this.runAgentJob({
        prompt,
        isNewSession,
        videoParams: vp,
        stage: 'full',
        scriptCn: script
      , generateVideoTask: false })
      // 清理 pending，避免串单
      this.agentPendingPrompt = ''
      this.agentPendingFinalPrompt = ''
      this.agentPendingScript = ''
      this.agentPendingIsNewSession = false
      try {
        this.$set(m, 'resolved', true)
      } catch {
        // ignore
      }
      try {
        this.$set(m, '_busy', false)
      } catch {
        m._busy = false
      }
    },
    editAgentScript() {
      // 轻量：让用户直接在输入框描述修改意见，然后重新走 stage=script
      this.chatMessages.push({
        id: genId(),
        role: 'assistant',
        content: '请直接告诉我你要修改的点（例如：结局更反转、加快节奏、把主角改成…）。我会重新生成剧本并再次让你确认。'
      })
      this.scrollChatBottom()
      // 复用一个简单标记：下一次 Agent 发送仍走 script 阶段（用户修改意见会在 prompt 中体现）
      this.agentPendingIsNewSession = false
    },
    confirmAgentCharacters(m) {
      if (!m || m.kind !== 'agent_character_confirm') return
      if (m._busy || m.resolved) return
      try {
        this.$set(m, '_busy', true)
      } catch {
        m._busy = true
      }
      const plan = this.agentPendingPlan && typeof this.agentPendingPlan === 'object' ? this.agentPendingPlan : null
      const videoPrompt = plan && plan.storyboard_video_prompt_cn ? String(plan.storyboard_video_prompt_cn).trim() : ''
      if (!videoPrompt) {
        showErrorToast('缺少分镜视频提示词')
        try {
          this.$set(m, '_busy', false)
        } catch {
          m._busy = false
        }
        return
      }
      const vp = this.agentPendingVideoParams && typeof this.agentPendingVideoParams === 'object' ? this.agentPendingVideoParams : {}
      const ratio = String(vp.ratio || '9:16')
      const duration = Number(vp.duration || 5)
      const resolution = String(vp.resolution || '720p')
      const images = Array.isArray(this.agentPendingCharacterImages) ? this.agentPendingCharacterImages : []
      const refs = images
        .map(ci => (ci && ci.url ? String(ci.url).trim() : ''))
        .filter(Boolean)
        .slice(0, 6)
        .map(u => ({ url: u, role: 'reference_image' }))

      // 创建 Seedance 任务并轮询（复用已有方法）
      ;(async () => {
        const assistant = {
          id: genId(),
          role: 'assistant',
          content: '正在创建分镜视频任务…',
          streaming: true,
          taskId: '',
          taskStatus: 'queued'
        }
        this.chatMessages.push(assistant)
        this.scrollChatBottom()
        try {
          const created = await createSeedanceVideoTask({
            prompt: videoPrompt,
            duration,
            ratio,
            resolution,
            images: refs
          })
          this.$set(assistant, 'taskId', String(created.id || ''))
          this.$set(assistant, 'taskStatus', 'queued')
          assistant.streaming = false
          assistant.content = `已创建分镜视频任务：\`${assistant.taskId}\`\n\n正在查询状态…`
          this.scrollChatBottom()
          this.resumeSeedancePollingForMessage(assistant)
        } catch (e) {
          const msg = e?.message ? String(e.message) : '创建视频任务失败'
          assistant.streaming = false
          assistant.content = `创建视频任务失败：${msg}`
          showErrorToast('创建视频任务失败')
        }
      })()

      try {
        this.$set(m, 'resolved', true)
      } catch {
        // ignore
      }
      try {
        this.$set(m, '_busy', false)
      } catch {
        m._busy = false
      }
      // 清理
      this.agentPendingPlan = null
      this.agentPendingCharacterImages = []
      this.agentPendingVideoParams = null
    },
    editAgentCharacters() {
      this.chatMessages.push({
        id: genId(),
        role: 'assistant',
        content: '请告诉我你想怎么改角色外观/风格（例如：年龄更大、发型改短、服装更赛博…）。我会重新生成角色设计图并再次让你确认。'
      })
      this.scrollChatBottom()
    },
    onStyleLibraryScroll() {
      if (this._styleScrollRaf) return
      this._styleScrollRaf = requestAnimationFrame(() => {
        this._styleScrollRaf = 0
        if (!this.shouldShowStyleLoadMore) return
        if (this.styleCategoryLoading) return
        const el = this.$refs.styleLibraryScroll
        if (!el) return
        const top = el.scrollTop || 0
        const height = el.clientHeight || 0
        const total = el.scrollHeight || 0
        const remain = total - (top + height)
        if (remain > 160) return
        const now = Date.now()
        if (now - (this._styleLastAutoLoadAt || 0) < 600) return
        this._styleLastAutoLoadAt = now
        this.loadMoreCategoryStyles()
      })
    },
    loadStyleMineFromStorage() {
      if (this._styleMineLoaded) return
      this._styleMineLoaded = true
      try {
        const raw = localStorage.getItem('cw_style_mine') || '[]'
        const arr = JSON.parse(raw)
        this.styleMine = Array.isArray(arr) ? arr.filter(x => x && x.id && x.name && x.thumb) : []
      } catch {
        this.styleMine = []
      }
    },
    loadStyleRecentFromStorage() {
      if (this._styleRecentLoaded) return
      this._styleRecentLoaded = true
      try {
        const raw = localStorage.getItem('cw_recent_styles') || '[]'
        const arr = JSON.parse(raw)
        this.styleRecent = Array.isArray(arr) ? arr.filter(x => x && x.id && x.name) : []
      } catch {
        this.styleRecent = []
      }
    },
    triggerStyleUploadPick() {
      const input = this.$refs.styleUploadInput
      if (!input || !input.click) return
      try {
        input.value = ''
      } catch {
        // ignore
      }
      input.click()
    },
    onStyleUploadSelected(e) {
      const file = e && e.target && e.target.files ? e.target.files[0] : null
      if (!file) return
      if (!String(file.type || '').startsWith('image/')) {
        showErrorToast('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        showErrorToast('风格图片过大（最大 5MB）')
        return
      }
      // 1) 生成 UI 缩略图（原图 dataURL）
      // 2) 生成“出图参考用”的压缩 dataURL（更小、更稳定，不依赖公网 URL）
      const readAsDataUrl = f =>
        new Promise((resolve, reject) => {
          const r = new FileReader()
          r.onload = () => resolve(typeof r.result === 'string' ? r.result : '')
          r.onerror = () => reject(new Error('read_failed'))
          r.readAsDataURL(f)
        })
      const compressToJpegDataUrl = (imgDataUrl, maxDim = 768, quality = 0.86) =>
        new Promise(resolve => {
          const img = new Image()
          img.onload = () => {
            const w = img.naturalWidth || img.width || 0
            const h = img.naturalHeight || img.height || 0
            if (!w || !h) return resolve('')
            const scale = Math.min(1, maxDim / Math.max(w, h))
            const cw = Math.max(1, Math.round(w * scale))
            const ch = Math.max(1, Math.round(h * scale))
            const canvas = document.createElement('canvas')
            canvas.width = cw
            canvas.height = ch
            const ctx = canvas.getContext('2d')
            if (!ctx) return resolve('')
            // 背景先铺白（避免透明通道导致色偏）
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, cw, ch)
            ctx.drawImage(img, 0, 0, cw, ch)
            try {
              const out = canvas.toDataURL('image/jpeg', quality)
              resolve(typeof out === 'string' ? out : '')
            } catch {
              resolve('')
            }
          }
          img.onerror = () => resolve('')
          img.src = imgDataUrl
        })

      ;(async () => {
        let dataUrl = ''
        try {
          dataUrl = await readAsDataUrl(file)
        } catch {
          dataUrl = ''
        }
        if (!dataUrl.startsWith('data:image/')) {
          showErrorToast('读取图片失败')
          return
        }
        const refDataUrl = await compressToJpegDataUrl(dataUrl, 768, 0.86)

        const fileTitleRaw = file && file.name ? String(file.name) : ''
        const fileTitle = fileTitleRaw
          ? fileTitleRaw.replace(/\.[a-zA-Z0-9]+$/, '').slice(0, 60)
          : `风格图-${new Date().toISOString().slice(0, 19).replace('T', ' ')}`

        // 仍尝试上传成 URL（可选）：用于某些后端/跨端场景复用
        let refUrl = ''
        try {
          refUrl = await uploadVideoImage({ image: refDataUrl || dataUrl, title: fileTitle })
        } catch {
          refUrl = ''
        }
        const id = `mine-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const name = `我的风格${(this.styleMine || []).length + 1}`
        const style = {
          id,
          tab: 'mine',
          name,
          badge: 'M',
          // 在卡片第二行显示“图片标题”（文件名去扩展名）
          desc: fileTitle,
          thumb: dataUrl,
          refDataUrl: refDataUrl || '',
          refUrl,
          // prompt 可为空：优先用参考图来“带风格”
          prompt: ''
        }
        this.styleMine = [style].concat(this.styleMine || []).slice(0, 30)
        try {
          localStorage.setItem('cw_style_mine', JSON.stringify(this.styleMine))
        } catch {
          // ignore
        }
        this.selectStyle(style)
        showSuccessToast('风格已添加')
      })()
    },
    openStyleLibrary() {
      if (this.isStreaming || this.awaitingVideoParams) return
      this.showStyleLibrary = true
      this.showModelPicker = false
      // 默认不加载“我的风格”；只预热“最近使用”即可
      this.loadStyleRecentFromStorage()
      this.fetchStyleCategories()
      this.$nextTick(() => {
        try {
          const el = this.$refs.styleLibraryScroll
          if (el) el.scrollTop = 0
        } catch {
          // ignore
        }
      })
    },
    closeStyleLibrary() {
      this.showStyleLibrary = false
    },
    selectStyle(s) {
      this.selectedStyle = s || null
      this.showStyleLibrary = false
      // 若是“上传风格”（带参考图），将其插入到聊天输入框（作为可见引用）
      // 只保留一个当前风格引用，避免反复点击堆叠多个风格缩略图
      this.$nextTick(() => {
        try {
          this.removeInlineStyleChips()
        } catch {
          // ignore
        }
        if (s && s.thumb) {
          const u = { kind: 'image', url: String(s.thumb), name: `风格：${s.name || '未命名'}` }
          this.insertStyleInlineAtCursor(u, String(s.id || ''))
        }
      })
      try {
        // 记录“最近使用”（轻量：写 localStorage，避免引入新表）
        const key = 'cw_recent_styles'
        const raw = localStorage.getItem(key) || '[]'
        const arr = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : []
        const next = [s].concat(arr.filter(x => x && x.id !== s.id)).slice(0, 12)
        localStorage.setItem(key, JSON.stringify(next))
        this.styleRecent = next
      } catch {
        // ignore
      }
    },
    removeInlineStyleChips() {
      const el = this.$refs.bottomComposer
      if (!el || !el.querySelectorAll) return
      const nodes = el.querySelectorAll('[data-cw-style="1"]')
      for (const n of nodes || []) {
        try {
          n.remove()
        } catch {
          try {
            if (n.parentNode) n.parentNode.removeChild(n)
          } catch {
            // ignore
          }
        }
      }
      this.$nextTick(() => this.onComposerInput())
    },
    insertStyleInlineAtCursor(u, styleId) {
      // 复用上传引用块结构，但额外打标 data-cw-style=1 便于一键替换
      this.insertUploadInlineAtCursor(u)
      const el = this.$refs.bottomComposer
      if (!el || !el.querySelectorAll) return
      try {
        const nodes = Array.from(el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') || [])
        const last = nodes.length ? nodes[nodes.length - 1] : null
        if (last && last.setAttribute) {
          last.setAttribute('data-cw-style', '1')
          if (styleId) last.setAttribute('data-cw-style-id', styleId)
        }
      } catch {
        // ignore
      }
      this.focusComposer()
    },
    clearStyle() {
      this.selectedStyle = null
    },
    onStyleCategoryTabClick(t) {
      // 接口分类：只更新 currentCategoryId；具体“按分类筛选风格列表”需要后端提供风格列表接口后再接
      const id = t && t.id != null ? String(t.id) : ''
      this.styleTab = id || 'all'
      if (Array.isArray(this.categoryList) && this.categoryList.length > 0) {
        if (id === 'mine') {
          this.loadStyleMineFromStorage()
          return
        }
        if (id === 'recent') {
          this.loadStyleRecentFromStorage()
          return
        }
        if (id === 'all') {
          this.fetchCategoryStyles({ categoryId: 'all', reset: false })
          return
        }
        if (id && id !== 'mine' && id !== 'recent') {
          this.currentCategoryId = id
          this.fetchCategoryStyles({ categoryId: id, reset: false })
        }
      }
    },
    loadMoreCategoryStyles() {
      const id = String(this.styleTab || '')
      if (!id || id === 'mine' || id === 'recent' || id === 'ip') return
      this.fetchCategoryStyles({ categoryId: id, reset: false })
    },
    normalizeRemoteStyleItem(it, idx, categoryId) {
      const o = it && typeof it === 'object' ? it : {}
      return normalizeWorkflowStyleItem(o, idx, categoryId)
    },
    fetchCategoryStyles({ categoryId, reset }) {
      const cid = categoryId != null ? String(categoryId) : ''
      if (!cid) return
      if (this.styleCategoryLoading) return
      const cur = (this.categoryStyleMap && this.categoryStyleMap[cid]) || null
      if (cur && cur.finished && !reset) return

      this.styleCategoryLoading = true
      const nextPage = reset ? 1 : Math.max(1, parseInt(cur && cur.page, 10) || 1)
      postWorkflowStyleSearch({ categoryId: cid, page: nextPage })
        .then(res => {
          const merged = mergeWorkflowStyleSearchResponse(res, {
            categoryId: cid,
            reset,
            previousBucket: cur
          })
          this.$set(this.categoryStyleMap, cid, merged)
        })
        .catch(() => {
          // ignore：不影响本地风格库
        })
        .finally(() => {
          this.styleCategoryLoading = false
        })
    },
    fetchStyleCategories() {
      // 风格库分类：用于后续按分类筛选（先把数据接入，UI 可逐步替换现有 tabs）
      if (this._categoryLoading) return
      this._categoryLoading = true
      api
        .get('/normal/categories')
        .then(res => {
          const list =
            (res && res.data && Array.isArray(res.data.list) && res.data.list) ||
            (res && Array.isArray(res.list) && res.list) ||
            []
          this.categoryList = list
          // 如果拿到了分类：默认选中“全部”，并记录第一个分类 id 作为 currentCategoryId 兜底
          if (list.length > 0) {
            const firstId = list[0] && list[0].id != null ? String(list[0].id) : ''
            if (firstId) {
              this.currentCategoryId = firstId
              if (!this.styleTab || this.styleTab === 'all') {
                this.styleTab = 'all'
              }
            }
          }
          // “全部”需要走接口拉取（不带 category_id）
          if (!this.categoryStyleMap || !this.categoryStyleMap.all) {
            this.fetchCategoryStyles({ categoryId: 'all', reset: true })
          }
          // 这里必须用字符串判断：typeof x 返回 'number'/'string' 等，不可能等于 Number 构造函数
          const cur = this.currentCategoryId
          const isNumberLike = cur !== null && cur !== '' && !Number.isNaN(Number(cur))
          if (!isNumberLike && list.length > 0) {
            this.currentCategoryId = list[0].id
          }
        })
        .catch(() => {
          // ignore：分类接口失败不影响使用内置风格
          this.categoryList = []
        })
        .finally(() => {
          this._categoryLoading = false
        })
    },
    startLoadingMoreUi() {
      this.loadingMoreUiSince = Date.now()
      this.loadingMoreUi = true
      try {
        if (this.loadingMoreUiTimer) clearTimeout(this.loadingMoreUiTimer)
      } catch {
        // ignore
      } finally {
        this.loadingMoreUiTimer = null
      }
    },
    stopLoadingMoreUi() {
      // 给用户可感知的转圈时间，避免“加载太快看不到”
      const minMs = 450
      const passed = Date.now() - (this.loadingMoreUiSince || 0)
      const wait = Math.max(0, minMs - passed)
      try {
        if (this.loadingMoreUiTimer) clearTimeout(this.loadingMoreUiTimer)
      } catch {
        // ignore
      }
      this.loadingMoreUiTimer = setTimeout(() => {
        this.loadingMoreUi = false
        this.loadingMoreUiTimer = null
      }, wait)
    },
    optimizeMediaElements(root) {
      const el = root && root.querySelectorAll ? root : null
      if (!el) return
      try {
        const imgs = el.querySelectorAll('img')
        for (const img of imgs) {
          if (!img || !img.setAttribute) continue
          if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy')
          if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async')
          // fetchpriority 在部分浏览器不支持，setAttribute 无副作用
          if (!img.getAttribute('fetchpriority')) img.setAttribute('fetchpriority', 'low')
        }
        const vids = el.querySelectorAll('video')
        for (const v of vids) {
          if (!v || !v.setAttribute) continue
          // 缩略图类 video 默认不预拉流；带 controls 的结果视频保持默认行为
          if (!v.getAttribute('controls') && !v.getAttribute('preload')) v.setAttribute('preload', 'none')
        }
      } catch {
        // ignore
      }
    },
    setupChatMediaObservers() {
      const root = this.$refs.chatScroll
      if (!root || !window || typeof window.MutationObserver !== 'function') return
      if (this._chatMediaMo) return
      this._chatMediaMo = new MutationObserver(() => {
        this.optimizeMediaElements(root)
      })
      try {
        this._chatMediaMo.observe(root, { subtree: true, childList: true })
      } catch {
        // ignore
      }
    },
    teardownChatMediaObservers() {
      try {
        if (this._chatMediaMo) this._chatMediaMo.disconnect()
      } catch {
        // ignore
      } finally {
        this._chatMediaMo = null
      }
    },
    async onChatScroll() {
      const el = this.$refs.chatScroll
      if (!el || this._loadingMoreChat) return
      const list = Array.isArray(this.chatMessages) ? this.chatMessages : []
      // 已把当前拉取到的都渲染出来后，如果还有更早的历史，则走“服务端分页”再拉一段
      if (this.chatRenderLimit >= list.length && this.serverHasMore) {
        if (el.scrollTop > 80) return
        this.startLoadingMoreUi()
        this._loadingMoreChat = true
        const prevHeight = el.scrollHeight
        const prevTop = el.scrollTop
        try {
          const nextTail = Math.min(400, Math.max(this.serverTail + this.serverTail, this.serverTail + 80))
          if (nextTail > this.serverTail) {
            const { state, meta } = await fetchChatWorkflowSessionDetail(this.sessionUuid, {
              tail: nextTail,
              strip_data_images: 1
            })
            this.serverTail = nextTail
            const total = Number(meta?.total_chat_messages || 0)
            const returned = Number(meta?.returned_chat_messages || 0)
            this.serverTotalChatMessages = Number.isFinite(total) ? total : 0
            this.serverHasMore = total > 0 ? returned < total : false
            // 注意：这里不滚到底，保持当前位置
            this.applyLoadedState(state || {}, { scrollToBottom: false })
          }
        } catch {
          // ignore
        } finally {
          this.$nextTick(() => {
            this.optimizeMediaElements(el)
            const nextHeight = el.scrollHeight
            const delta = nextHeight - prevHeight
            el.scrollTop = prevTop + delta
            this._loadingMoreChat = false
            this.stopLoadingMoreUi()
          })
        }
        return
      }

      if (this.chatRenderLimit >= list.length) return

      if (el.scrollTop > 80) return

      this.startLoadingMoreUi()
      this._loadingMoreChat = true
      const prevHeight = el.scrollHeight
      const prevTop = el.scrollTop

      this.chatRenderLimit = Math.min(list.length, this.chatRenderLimit + this.chatRenderStep)

      this.$nextTick(() => {
        this.optimizeMediaElements(el)
        const nextHeight = el.scrollHeight
        const delta = nextHeight - prevHeight
        el.scrollTop = prevTop + delta
        this._loadingMoreChat = false
        this.stopLoadingMoreUi()
      })
    },
    sanitizeUserBubbleHtml(html) {
      const raw = html == null ? '' : String(html)
      if (!raw) return ''
      try {
        const clean = DOMPurify.sanitize(raw, {
          USE_PROFILES: { html: true },
          ADD_TAGS: ['span', 'img', 'video', 'source'],
          // 允许输入框内联缩略图用到的 data-* 与 Tailwind class
          ADD_ATTR: [
            'data-cw-upload',
            'data-cw-url',
            'data-cw-kind',
            'data-cw-name',
            'class',
            'title',
            'src',
            'alt',
            'loading',
            'decoding',
            'playsinline',
            'muted',
            'preload',
            'controls',
            'contenteditable'
          ],
          ALLOWED_URI_REGEXP:
            // 不允许 blob:（刷新后必失效且拖慢页面）；允许 data: 与常规 http(s)
            /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
        })
        return this.normalizeUserBubbleMediaUrls(clean)
      } catch {
        return ''
      }
    },
    normalizeUserBubbleMediaUrls(html) {
      const s = html == null ? '' : String(html)
      if (!s) return ''
      if (typeof window === 'undefined' || !window.location || !window.DOMParser) return s
      try {
        const origin = String(window.location.origin || '').replace(/\/$/, '')
        if (!origin) return s
        const doc = new DOMParser().parseFromString(`<div class="cw-bubble-root">${s}</div>`, 'text/html')
        const root = doc.body && doc.body.firstElementChild ? doc.body.firstElementChild : null
        if (!root) return s

        const rewriteUrl = u => {
          const x = u != null ? String(u).trim() : ''
          if (!x) return x
          // 已经是相对路径：补全为当前站点 origin（避免保存了错误绝对域名）
          if (x.startsWith('/')) return origin + x
          if (!/^https?:\/\//i.test(x)) return x
          try {
            const uu = new URL(x)
            const cur = new URL(origin)
            // 注意：不要把“线上绝对地址”的 /uploads 强行改写到当前 devServer。
            // 仅处理明显错误的同源场景：路径是 /uploads，但 host 却是 localhost/127.0.0.1（或 dev 端口不一致）。
            const p = uu.pathname || ''
            if (!p.startsWith('/uploads/')) return x

            const badLocal =
              uu.hostname === 'localhost' ||
              uu.hostname === '127.0.0.1' ||
              uu.hostname.endsWith('.localhost')
            if (badLocal && uu.host !== cur.host) {
              return origin + p + (uu.search || '')
            }
            // 同 hostname 但端口不同（例如页面 8080，图片误指向 8081）
            if (uu.hostname === cur.hostname && uu.port !== cur.port) {
              return origin + p + (uu.search || '')
            }
          } catch {
            return x
          }
          return x
        }

        const applyToUrlAttr = (el, attr) => {
          if (!el || !el.getAttribute) return
          const v = el.getAttribute(attr)
          if (!v) return
          const nv = rewriteUrl(v)
          if (nv && nv !== v) el.setAttribute(attr, nv)
        }

        root.querySelectorAll('img[src],video[src],source[src]').forEach(el => applyToUrlAttr(el, 'src'))
        root.querySelectorAll('[data-cw-url]').forEach(el => applyToUrlAttr(el, 'data-cw-url'))

        return root.innerHTML || ''
      } catch {
        return s
      }
    },
    composerHasInlineUploads() {
      const el = this.$refs.bottomComposer
      if (!el || !el.querySelector) return false
      try {
        return !!el.querySelector('[data-cw-upload="1"][data-cw-url]')
      } catch {
        return false
      }
    },
    async urlToDataUrl(url) {
      const u = url != null ? String(url).trim() : ''
      if (!u) return ''
      if (u.startsWith('data:')) return u
      // http(s) 直接交给后端拉取，避免前端转 base64 过大
      if (/^https?:\/\//i.test(u)) return u
      if (!u.startsWith('blob:')) return u
      try {
        const res = await fetch(u)
        const blob = await res.blob()
        const dataUrl = await new Promise(resolve => {
          const reader = new FileReader()
          reader.onload = () => resolve(String(reader.result || ''))
          reader.onerror = () => resolve('')
          reader.readAsDataURL(blob)
        })
        return dataUrl
      } catch {
        return ''
      }
    },
    async urlToPersistentUrl(url, title) {
      const u = url != null ? String(url).trim() : ''
      if (!u) return ''
      if (/^https?:\/\//i.test(u)) return u

      this.bumpPendingPersistUploads(1)
      try {
        const pickExt = (mime, fallback) => {
          const m = String(mime || '').toLowerCase()
          if (m.includes('png')) return 'png'
          if (m.includes('jpeg') || m.includes('jpg')) return 'jpg'
          if (m.includes('webp')) return 'webp'
          if (m.includes('mp4')) return 'mp4'
          if (m.includes('webm')) return 'webm'
          return fallback || 'bin'
        }

        const uploadDataUrlSmart = async du => {
          const s = du != null ? String(du).trim() : ''
          if (!s) return ''
          if (/^https?:\/\//i.test(s)) return s
          // dataURL：统一 fetch 成 Blob，再决定 multipart / JSON
          try {
            const b = await (await fetch(s)).blob()
            const mime = String(b.type || '')
            if (mime.startsWith('video/')) {
              const ext = pickExt(mime, 'mp4')
              const fn = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`
              return await uploadVideoMediaFile({ file: b, filename: fn, ...(title ? { title } : {}) })
            }
          } catch {
            // ignore
          }
          // 图片 dataURL：走后端 JSON（后端按图片解码）
          return await uploadVideoImage({ image: s, ...(title ? { title } : {}) })
        }

        // blob: 优先按真实 mime 分路径上传（视频不要转 base64）
        if (u.startsWith('blob:')) {
          try {
            const res = await fetch(u)
            const blob = await res.blob()
            const mime = String(blob.type || '')
            if (mime.startsWith('video/')) {
              const ext = pickExt(mime, 'mp4')
              const fn =
                (title && String(title).trim() ? String(title).trim().replace(/[^\w.\-]+/g, '_') : '') ||
                `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`
              const persisted = await uploadVideoMediaFile({ file: blob, filename: fn, ...(title ? { title } : {}) })
              if (persisted) {
                this.rewriteTrackedUrlEverywhere(u, persisted)
                return persisted
              }
            }
            if (mime.startsWith('image/')) {
              const ext = pickExt(mime, 'png')
              const fn =
                (title && String(title).trim() ? String(title).trim().replace(/[^\w.\-]+/g, '_') : '') ||
                `upload-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`
              const persisted = await uploadVideoMediaFile({ file: blob, filename: fn, ...(title ? { title } : {}) })
              if (persisted) {
                this.rewriteTrackedUrlEverywhere(u, persisted)
                return persisted
              }
            }
          } catch {
            // fall through to dataURL path
          }
        }

        // 其它：先转成 dataURL/base64（兜底）
        const dataUrl = await this.urlToDataUrl(u)
        const du = dataUrl != null ? String(dataUrl).trim() : ''
        if (!du) return ''
        if (/^https?:\/\//i.test(du)) return du

        // 上传到 /video_images（后端保存 + 入库），拿到可访问 URL
        try {
          const persisted = await uploadDataUrlSmart(du)
          if (persisted) {
            this.rewriteTrackedUrlEverywhere(u, persisted)
            return persisted
          }
        } catch {
          // 上传失败就退化为 dataURL（至少不再用 blob，避免刷新后 404）
          this.rewriteTrackedUrlEverywhere(u, du)
          return du
        }
        return du
      } finally {
        this.bumpPendingPersistUploads(-1)
        // 上传完成后再尝试落盘（如果期间被推迟）
        try {
          this.schedulePersistSession()
        } catch {
          // ignore
        }
      }
    },
    rewriteTrackedUrlEverywhere(from, to) {
      const a = from != null ? String(from) : ''
      const b = to != null ? String(to) : ''
      if (!a || !b || a === b) return

      const replaceUrlOnObj = obj => {
        if (!obj || typeof obj !== 'object') return
        if (typeof obj.url === 'string' && obj.url === a) obj.url = b
      }

      if (Array.isArray(this.localUploads)) this.localUploads.forEach(replaceUrlOnObj)
      if (Array.isArray(this.referencedUploads)) this.referencedUploads.forEach(replaceUrlOnObj)

      // 输入框 DOM：把 data-cw-url 与 img.src 一并替换
      const el = this.$refs.bottomComposer
      if (el && el.querySelectorAll) {
        const nodes = el.querySelectorAll('[data-cw-upload="1"][data-cw-url]')
        for (const n of nodes || []) {
          try {
            if (String(n.getAttribute('data-cw-url') || '') === a) {
              n.setAttribute('data-cw-url', b)
              const img = n.querySelector ? n.querySelector('img') : null
              if (img) img.setAttribute('src', b)
              const vid = n.querySelector ? n.querySelector('video') : null
              if (vid) vid.setAttribute('src', b)
            }
          } catch {
            // ignore
          }
        }
      }
    },
    async materializeComposerInlineUploads(rootEl) {
      const el = rootEl
      if (!el || !el.querySelectorAll) return
      const nodes = Array.from(el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') || [])
      for (const n of nodes) {
        const url = String(n.getAttribute('data-cw-url') || '').trim()
        if (!url) continue
        const kind = String(n.getAttribute('data-cw-kind') || '').trim() === 'video' ? 'video' : 'image'
        const name = String(n.getAttribute('data-cw-name') || '').trim()
        const need =
          url.startsWith('blob:') ||
          (kind === 'image' && /^data:image\//i.test(url)) ||
          (kind === 'video' && /^data:video\//i.test(url))
        if (!need) continue
        // eslint-disable-next-line no-await-in-loop
        await this.urlToPersistentUrl(url, name)
      }
    },
    async buildReferenceImagesForApi(refs) {
      const list = Array.isArray(refs) ? refs : []
      const out = []
      for (const r of list) {
        const url = r && r.url ? String(r.url).trim() : ''
        if (!url) continue
        const v = await this.urlToDataUrl(url)
        if (v) out.push(v)
        // 控制数量，避免 payload 过大
        if (out.length >= 4) break
      }
      return out
    },
    /**
     * Seedance 参考图：只接受公网可拉取的 http(s) URL。
     * 这里尽量把引用素材“转存成后端 uploads 链接”，避免火山侧拉取超时。
     */
    async buildReferenceImagesForSeedance(refs) {
      const list = Array.isArray(refs) ? refs : []
      const out = []
      const isHttp = u => /^https?:\/\//i.test(u)

      for (const r of list) {
        const urlRaw = r && r.url ? String(r.url).trim() : ''
        if (!urlRaw) continue
        let u = urlRaw

        // 相对路径转绝对，便于后端拉取（后端会再转存到 /uploads）
        if (!isHttp(u) && u.startsWith('/')) {
          try {
            u = `${window.location.origin}${u}`
          } catch {
            // ignore
          }
        }

        // 通过后端拉取并转存（绕过 CORS/防盗链）
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await api.post('/cache_remote_media', { url: u, kind: 'image', dir: 'seedance', relative: 0 })
          const picked = res?.data?.public_url || res?.data?.url || ''
          if (typeof picked === 'string' && picked.trim()) {
            u = picked.trim()
          }
        } catch {
          // ignore
        }

        if (u) out.push(u)
        // 不限制 4 张；保守限制 20，避免请求体过大
        if (out.length >= 20) break
      }
      return out
    },
    hasInlineUploadByUrl(url) {
      const el = this.$refs.bottomComposer
      const u = url != null ? String(url) : ''
      if (!el || !u || !el.querySelector) return false
      try {
        return !!el.querySelector(`[data-cw-upload="1"][data-cw-url="${CSS.escape(u)}"]`)
      } catch {
        // 老环境没有 CSS.escape 或选择器异常时兜底遍历
        const nodes = el.querySelectorAll ? el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') : []
        for (const n of nodes || []) {
          try {
            if (String(n.getAttribute('data-cw-url') || '') === u) return true
          } catch {
            // ignore
          }
        }
        return false
      }
    },
    isUploadReferenced(u) {
      const url = u && u.url ? String(u.url) : ''
      if (!url) return false
      // 以输入框里的实际引用块为准（允许同一素材被插入多次）
      return this.hasInlineUploadByUrl(url)
    },
    toggleReferenceAsset(a) {
      if (!a || !a.url) return
      const u = { kind: 'image', url: a.url, name: a.title || '未命名' }

      if (!Array.isArray(this.localUploads)) this.localUploads = []
      const url = String(u.url)
      const exists = this.localUploads.some((x) => x && String(x.url) === url)
      if (exists) {
        showSuccessToast('该资产已在预览区')
        return
      }
      this.localUploads.push(u)
      showSuccessToast('已加入素材预览，点击「引用」插入输入框')
    },
    insertReferenceUpload(u) {
      if (!u || !u.url) return
      // 始终追加一个内联引用块（允许同一素材多次引用）
      this.insertUploadInlineAtCursor(u)
      this.focusComposer()
    },
    focusComposer() {
      const el = this.$refs.bottomComposer
      if (el && el.focus) {
        this.$nextTick(() => {
          try {
            el.focus()
          } catch {
            // ignore
          }
        })
      }
    },
    onComposerInput() {
      const el = this.$refs.bottomComposer
      const txt = el ? (el.innerText != null ? String(el.innerText) : '') : ''
      this.bottomInput = txt.replace(/\u00a0/g, ' ')
      this.composerIsEmpty = !((this.bottomInput || '').trim()) && !(el && el.querySelector && el.querySelector('[data-cw-upload="1"]'))
      this.syncReferencedUploadsFromComposer()
      this.syncAtMentionFromInput()
    },
    removeInlineUploadByUrl(url) {
      const el = this.$refs.bottomComposer
      const u = url != null ? String(url) : ''
      if (!el || !u) return
      const nodes = el.querySelectorAll ? el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') : []
      for (const n of nodes || []) {
        try {
          if (String(n.getAttribute('data-cw-url') || '') === u) n.remove()
        } catch {
          // ignore
        }
      }
      this.$nextTick(() => this.onComposerInput())
    },
    syncReferencedUploadsFromComposer() {
      const el = this.$refs.bottomComposer
      if (!el || !el.querySelectorAll) return

      const nodes = Array.from(el.querySelectorAll('[data-cw-upload="1"][data-cw-url]') || [])
      const refs = []
      for (const n of nodes) {
        const url = String(n.getAttribute('data-cw-url') || '').trim()
        if (!url) continue
        const kind = String(n.getAttribute('data-cw-kind') || '').trim() === 'video' ? 'video' : 'image'
        const name = String(n.getAttribute('data-cw-name') || '').trim()
        refs.push({ kind, url, name })
      }

      // 不去重：允许同一素材被引用多次
      this.referencedUploads = refs
    },
    onComposerKeydown(e) {
      if (!e) return
      if (this.isAtMentionKeydownHandled(e)) return
      // 删除内联引用块（contenteditable=false 的 span），否则 Backspace/Delete 容易“卡住删不掉”
      if ((e.key === 'Backspace' || e.key === 'Delete') && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
        const el = this.$refs.bottomComposer
        const sel = window.getSelection ? window.getSelection() : null
        if (el && sel && sel.rangeCount) {
          const range = sel.getRangeAt(0)

          const removeNodeIfUpload = node => {
            if (!node) return false
            let n = node
            // 若点击到内部子节点（比如 video/img），向上找引用外层
            if (n.nodeType !== 1 && n.parentNode) n = n.parentNode
            const isElem = n && n.nodeType === 1
            const wrap = isElem && n.getAttribute && n.getAttribute('data-cw-upload') === '1' ? n : null
            if (!wrap) return false
            try {
              wrap.remove()
            } catch {
              try {
                if (wrap.parentNode) wrap.parentNode.removeChild(wrap)
              } catch {
                /* ignore */
              }
            }
            this.$nextTick(() => this.onComposerInput())
            return true
          }

          // 若选区覆盖了引用块，直接删除
          if (!range.collapsed) {
            const frag = range.cloneContents()
            const hasUpload =
              frag && frag.querySelector && !!frag.querySelector('[data-cw-upload="1"][data-cw-url]')
            if (hasUpload) {
              e.preventDefault()
              range.deleteContents()
              this.$nextTick(() => this.onComposerInput())
              return
            }
          }

          // 光标相邻删除：Backspace 删前一个节点；Delete 删后一个节点
          const container = range.startContainer
          const offset = range.startOffset

          // 情况 A：光标在文本节点里
          if (container && container.nodeType === 3) {
            if (e.key === 'Backspace' && offset === 0) {
              const prev = container.previousSibling
              if (removeNodeIfUpload(prev)) {
                e.preventDefault()
                return
              }
            }
            if (e.key === 'Delete' && offset === (container.nodeValue || '').length) {
              const next = container.nextSibling
              if (removeNodeIfUpload(next)) {
                e.preventDefault()
                return
              }
            }
          }

          // 情况 B：光标在元素节点里（常见于 div/contenteditable 根）
          if (container && container.nodeType === 1) {
            const child = container.childNodes ? container.childNodes[offset] : null
            if (e.key === 'Delete') {
              if (removeNodeIfUpload(child)) {
                e.preventDefault()
                return
              }
            }
            if (e.key === 'Backspace') {
              const prev = container.childNodes ? container.childNodes[offset - 1] : null
              if (removeNodeIfUpload(prev)) {
                e.preventDefault()
                return
              }
            }
          }
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.onEnterSend()
      }
    },
    onComposerPaste(e) {
      if (!e) return
      if (this.isStreaming || this.awaitingVideoParams) return
      const cd = e.clipboardData
      if (!cd || !cd.items || !cd.items.length) return

      const items = Array.from(cd.items || [])
      const imgItems = items.filter(it => it && it.kind === 'file' && String(it.type || '').startsWith('image/'))
      if (!imgItems.length) return

      // 有图片则优先处理图片上传，并阻止浏览器把 base64/文件名等内容直接塞进输入框
      e.preventDefault()

      if (!Array.isArray(this.localUploads)) this.localUploads = []
      if (!Array.isArray(this._localObjectUrls)) this._localObjectUrls = []

      const added = []
      for (const it of imgItems) {
        const f = it.getAsFile ? it.getAsFile() : null
        if (!f) continue
        // 给粘贴图片一个更友好的名字
        const ext = (String(f.type || '').split('/')[1] || 'png').replace(/[^a-z0-9]/gi, '')
        const name = `paste-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext || 'png'}`
        const file = new File([f], name, { type: f.type || 'image/png' })

        const url = URL.createObjectURL(file)
        this._localObjectUrls.push(url)
        const u = { kind: 'image', url, name: file.name, type: file.type, size: file.size || 0 }
        this.localUploads.push(u)
        added.push(u)

        // 立即上传到 /video_images 固化为可访问 URL（写入数据库），避免 blob 被拦截/刷新丢失
        this.urlToPersistentUrl(url, u.name).catch(() => {})
      }

      if (added.length) {
        showSuccessToast(`已粘贴 ${added.length} 张图片，点击「引用」插入输入框`)
      }
    },
    insertUploadInlineAtCursor(u) {
      const el = this.$refs.bottomComposer
      if (!el) return

      // 确保 selection 在 composer 内
      const sel = window.getSelection ? window.getSelection() : null
      if (!sel) return
      if (!sel.rangeCount) {
        const r = document.createRange()
        r.selectNodeContents(el)
        r.collapse(false)
        sel.removeAllRanges()
        sel.addRange(r)
      }

      const range = sel.getRangeAt(0)
      // 如果光标不在 composer 内，把光标放到末尾
      if (!el.contains(range.commonAncestorContainer)) {
        range.selectNodeContents(el)
        range.collapse(false)
      }

      const wrap = document.createElement('span')
      wrap.setAttribute('data-cw-upload', '1')
      wrap.setAttribute('data-cw-url', u && u.url ? String(u.url) : '')
      wrap.setAttribute('data-cw-kind', u && u.kind === 'video' ? 'video' : 'image')
      wrap.setAttribute('data-cw-name', u && u.name ? String(u.name) : '')
      wrap.contentEditable = 'false'
      wrap.className = 'cw-upload-chip inline-flex items-center align-middle mx-1 my-0.5'
      // 不在输入框里展示长文件名，避免出现一串字符；需要名称时用悬停 title
      wrap.title = (u && u.name ? String(u.name) : '') || (u && u.kind === 'video' ? '视频' : '图片')

      if (u && u.kind === 'image') {
        const img = document.createElement('img')
        img.src = u.url
        img.alt = ''
        img.className = 'w-8 h-8 rounded-xl object-cover'
        wrap.appendChild(img)
      } else {
        // 视频引用：用 video 首帧做缩略图（muted + preload metadata）
        const box = document.createElement('span')
        box.className = 'relative inline-block w-8 h-8 rounded-xl overflow-hidden bg-black/40'

        const vid = document.createElement('video')
        vid.src = u && u.url ? String(u.url) : ''
        vid.muted = true
        vid.playsInline = true
        vid.preload = 'metadata'
        vid.className = 'w-8 h-8 object-cover opacity-90'
        box.appendChild(vid)

        const overlay = document.createElement('span')
        overlay.className = 'absolute inset-0 flex items-center justify-center text-white/75'
        overlay.innerHTML = '<i class="fas fa-play-circle text-[12px]"></i>'
        box.appendChild(overlay)

        wrap.appendChild(box)
      }
      // 不追加文件名文本，保持输入框干净

      range.insertNode(wrap)
      // 插入后加个空格并把光标移动到后面
      const space = document.createTextNode(' ')
      wrap.after(space)
      const nr = document.createRange()
      nr.setStartAfter(space)
      nr.collapse(true)
      sel.removeAllRanges()
      sel.addRange(nr)
      this.$nextTick(() => this.onComposerInput())
    },
    removeLocalUpload(idx) {
      const i = Number(idx)
      if (!Number.isFinite(i)) return
      const list = Array.isArray(this.localUploads) ? [...this.localUploads] : []
      const removed = list.splice(i, 1)[0]
      this.localUploads = list
      // 同步移除引用
      if (removed && removed.url && Array.isArray(this.referencedUploads)) {
        const url = String(removed.url)
        const pos2 = this.referencedUploads.findIndex(x => x && String(x.url) === url)
        if (pos2 >= 0) this.referencedUploads.splice(pos2, 1)
      }
      if (removed && removed.url) {
        const url = removed.url
        const pool = Array.isArray(this._localObjectUrls) ? [...this._localObjectUrls] : []
        const pos = pool.indexOf(url)
        if (pos >= 0) pool.splice(pos, 1)
        this._localObjectUrls = pool
        try {
          URL.revokeObjectURL(url)
        } catch {
          // ignore
        }
      }
    },
    triggerUploadPick() {
      if (this.isStreaming || this.awaitingVideoParams) return
      const tryClick = el => {
        try {
          if (el && el.click) el.click()
          return true
        } catch {
          return false
        }
      }

      const el = this.$refs.uploadInput
      if (tryClick(el)) return

      // 兜底：动态创建 input（兼容某些环境对隐藏 input 的限制）
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*,video/*'
      input.multiple = true
      input.onchange = evt => this.onUploadFilesSelected(evt)
      tryClick(input)
    },
    onUploadFilesSelected(e) {
      const files = Array.from(e && e.target && e.target.files ? e.target.files : [])
      // 允许再次选择同一个文件
      if (e && e.target) e.target.value = ''
      if (!files.length) return

      // 防御：避免状态被覆盖导致 undefined
      if (!Array.isArray(this._localObjectUrls)) this._localObjectUrls = []
      if (!Array.isArray(this.localUploads)) this.localUploads = []
      if (!Array.isArray(this.chatMessages)) this.chatMessages = []

      const uploads = []
      for (const f of files) {
        if (!f) continue
        const type = String(f.type || '')
        const isImg = type.startsWith('image/')
        const isVid = type.startsWith('video/')
        if (!isImg && !isVid) continue
        const url = URL.createObjectURL(f)
        this._localObjectUrls.push(url)
        uploads.push({
          kind: isVid ? 'video' : 'image',
          url,
          name: f.name || '',
          type,
          size: f.size || 0
        })
      }

      if (!uploads.length) {
        showErrorToast('请选择图片或视频文件')
        return
      }

      this.localUploads = [...(this.localUploads || []), ...uploads]
      showSuccessToast(`已添加 ${uploads.length} 个文件，点击「引用」插入输入框`)

      // 选中文件后立即上传图片到 /video_images 固化 URL（写入数据库）
      for (const u of uploads) {
        if (u && typeof u.url === 'string' && u.url.startsWith('blob:')) {
          this.urlToPersistentUrl(u.url, u.name).catch(() => {})
        }
      }
    },
    openLocalUpload(u) {
      if (!u || !u.url) return
      if (u.kind === 'image') {
        this.openImagePreview(u.url)
        return
      }
      try {
        window.open(u.url, '_blank')
      } catch {
        // ignore
      }
    },
    onGlobalResize() {
      if (!this.showAssetPanel) return
      this.updateAssetPanelPosition()
    },
    assetTabBtnClass(active) {
      return [
        'px-4 py-2 rounded-full text-xs font-bold transition-all',
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-200'
      ]
    },
    openAssetPanel() {
      this.showAssetPanel = true
      this.updateAssetPanelPosition()
      this.loadAssetPanel()
    },
    closeAssetPanel() {
      this.showAssetPanel = false
    },
    updateAssetPanelPosition() {
      this.$nextTick(() => {
        const anchor = this.$refs.assetAnchor
        if (!anchor || !anchor.getBoundingClientRect) return
        const rect = anchor.getBoundingClientRect()
        const gap = 16
        const desiredWidth = 720
        const viewportW = window.innerWidth || 1200
        const viewportH = window.innerHeight || 800

        // 面板放在输入框区域的右侧
        let left = rect.right + gap
        let top = rect.top

        // 防止溢出屏幕右边
        if (left + desiredWidth + 16 > viewportW) {
          left = Math.max(16, viewportW - desiredWidth - 16)
        }
        // 防止溢出屏幕下边
        const estimatedH = 420
        if (top + estimatedH + 16 > viewportH) {
          top = Math.max(16, viewportH - estimatedH - 16)
        }
        this.assetPanelStyle = {
          left: `${Math.round(left)}px`,
          top: `${Math.round(top)}px`,
          width: `${desiredWidth}px`
        }
      })
    },
    async loadAssetPanel() {
      this.assetLoading = true
      this.assetError = ''
      try {
        const rows = await fetchAssetLibraryList({ limit: 200 })
        this.assetRows = (rows || []).map(x => ({
          id: x.id != null ? String(x.id) : '',
          kind: x.kind || 'image',
          url: x.url || '',
          title: x.title || '未命名'
        })).filter(r => r.url)
      } catch (e) {
        const msg = e?.message || e?.response?.data?.message || '加载资产失败'
        this.assetRows = []
        this.assetError = String(msg)
      } finally {
        this.assetLoading = false
      }
    },
    onGlobalKeydown(e) {
      if (!e) return
      if (e.key === 'Escape' && this.showImagePreview) {
        this.closeImagePreview()
      }
      if (e.key === 'Escape' && this.showMediaPreview) {
        this.closeMediaPreview()
      }
      if (e.key === 'Escape' && this.showAssetPanel) {
        this.closeAssetPanel()
      }
    },
    openMediaPreview(u) {
      const url = u && u.url ? String(u.url).trim() : ''
      if (!url) return
      this.previewMediaKind = u && u.kind === 'video' ? 'video' : 'video'
      this.previewMediaUrl = url
      this.showMediaPreview = true
    },
    onUserBubbleHtmlClick(e) {
      if (!e) return
      // 只处理点击到内联引用块（输入框的缩略图 span）
      const t = e.target
      const el = t && t.closest ? t.closest('[data-cw-upload="1"][data-cw-url]') : null
      if (!el || !el.getAttribute) return
      const url = String(el.getAttribute('data-cw-url') || '').trim()
      if (!url) return
      const kind = String(el.getAttribute('data-cw-kind') || '').trim() === 'video' ? 'video' : 'image'
      e.preventDefault()
      e.stopPropagation()
      if (kind === 'video') {
        this.openMediaPreview({ kind: 'video', url })
      } else {
        this.openImagePreview(url)
      }
    },
    closeMediaPreview() {
      this.showMediaPreview = false
      this.previewMediaUrl = ''
      this.previewMediaKind = 'video'
    },
    openImagePreview(url) {
      const u = typeof url === 'string' ? url.trim() : ''
      if (!u) return
      this.previewImageUrl = u
      this.showImagePreview = true
    },
    closeImagePreview() {
      this.showImagePreview = false
      this.previewImageUrl = ''
    },
    async downloadImages(urls) {
      const list = Array.isArray(urls) ? urls : []
      const clean = list.map(u => (typeof u === 'string' ? u.trim() : '')).filter(Boolean)
      if (!clean.length) return

      const stamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, '-')

      // 逐张下载：优先 fetch blob（更可控），失败则退化为直接打开链接
      for (let i = 0; i < clean.length; i++) {
        const u = clean[i]
        const filename = `seedream-${stamp}-${i + 1}.png`
        try {
          const res = await fetch(u, { mode: 'cors' })
          if (!res.ok) throw new Error('bad status')
          const blob = await res.blob()
          const objUrl = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = objUrl
          a.download = filename
          document.body.appendChild(a)
          a.click()
          a.remove()
          URL.revokeObjectURL(objUrl)
        } catch (e) {
          // 兜底：新开标签页，让用户手动保存
          window.open(u, '_blank')
        }
      }
      showSuccessToast('已开始下载')
    },
    addImagesToAssetLibrary(urls) {
      const list = Array.isArray(urls) ? urls : []
      const clean = list.map(u => (typeof u === 'string' ? u.trim() : '')).filter(Boolean)
      if (!clean.length) return
      const now = new Date().toISOString()
      const items = clean.map(u => ({
        kind: 'image',
        url: u,
        title: '未命名',
        prompt: '',
        meta: { source: 'conversation_workflow', createdAt: now }
      }))

      // 先尝试入库到数据库；失败时兜底写本地，避免用户丢资产
      addAssetsToLibrary(items)
        .then(() => {
          showSuccessToast('已加入资产')
        })
        .catch(() => {
          const key = 'assetLibrary'
          const raw = localStorage.getItem(key)
          const arr = safeJsonParse(raw || '[]', [])
          const fallbackItems = clean.map(u => ({
            id: `asset-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            kind: 'image',
            url: u,
            createdAt: now
          }))
          const next = Array.isArray(arr) ? [...fallbackItems, ...arr] : [...fallbackItems]
          localStorage.setItem(key, JSON.stringify(next))
          showSuccessToast('已加入资产（本地兜底）')
        })
    },
    toggleModelPicker() {
      const next = !this.showModelPicker
      this.showModelPicker = next
      if (next) {
        const m = (this.modelOptions || []).find((x) => x.id === this.selectedModelId)
        const kind = m && m.kind
        this.modelTab = ['image', 'video', 'text'].includes(kind) ? kind : 'image'
      }
    },
    closeModelPicker() {
      this.showModelPicker = false
    },
    selectModel(m) {
      if (!m || !m.id) return
      this.selectedModelId = m.id
      this.showModelPicker = false
      if (m.id === 'agent') {
        this.rightWorkspaceTab = 'canvas'
      }
    },
    async loadSessionList() {
      this.sessionListLoading = true
      this.sessionListError = ''
      try {
        this.sessionList = await fetchChatWorkflowSessionList({ limit: 50 })
      } catch (e) {
        this.sessionList = []
        const status = e?.response?.status
        if (status === 401) {
          this.sessionListError = '登录后可查看历史项目'
        } else {
          this.sessionListError = (e && e.message) || '加载历史项目失败'
        }
      } finally {
        this.sessionListLoading = false
      }
    },
    onSidebarNewProject() {
      this.startNewWorkflowLocal()
      this.$router.replace({ name: 'ConversationWorkflow', query: {} }).catch(() => {})
    },
    openWorkflowSession(p) {
      const uuid = p && p.uuid != null ? String(p.uuid).trim() : ''
      if (!uuid) return
      if (uuid === this.sessionUuid) return
      this.$router
        .push({ name: 'ConversationWorkflow', query: { session: uuid } })
        .catch(() => {})
    },

    /** 新建空白工作流（不写 URL，由调用方负责路由） */
    startNewWorkflowLocal() {
      this.closeAtMention()
      if (this.streamAbort) {
        this.streamAbort.abort()
        this.streamAbort = null
      }
      if (this._persistTimer) {
        clearTimeout(this._persistTimer)
        this._persistTimer = null
      }
      this.isStreaming = false
      this.awaitingVideoParams = false
      this.chatMessages = []
      this.bottomInput = ''
      this.sessionUuid = ''
      this.videoDuration = 5
      this.videoRatio = '16:9'
      this.videoResolution = '720p'
      this.agentCanvasNodes = []
      this.agentCanvasRunContext = null
      this.agentCanvasSelectedId = null
      this.agentCanvasPan = { x: 100, y: 60 }
      this.agentCanvasScale = 1
      this.agentCanvasEditTarget = null
      this.agentCanvasEditNote = ''
      this.teardownAgentCanvasListeners()
      this.scrollChatBottom()
    },
    /** 已在对话工作流页时再次点侧栏「对话工作流」：路由不变也要清空 */
    onNewWorkflowNavClick() {
      if (this.$route.name !== 'ConversationWorkflow') return
      const sid = this.$route.query && String(this.$route.query.session || '').trim()
      if (!sid) {
        this.startNewWorkflowLocal()
      }
    },

    buildSessionTitle() {
      const u = this.chatMessages.find(m => {
        if (!m || m.role !== 'user') return false
        const plain = String(m.contentPlain || m.content || '').trim()
        const html = String(m.contentHtml || '').trim()
        return !!(plain || html)
      })
      const t = u ? String((u.contentPlain || u.content || '').trim()) : ''
      if (!t) return '未命名工作流'
      return t.length > 80 ? t.slice(0, 80) + '…' : t
    },
    extractThumbUrl() {
      const a = [...this.chatMessages]
        .reverse()
        .find(m => m && m.role === 'assistant' && (String(m.videoUrl || '').trim() || (Array.isArray(m.images) && m.images.length)))
      if (!a) return ''
      if (a.videoUrl && String(a.videoUrl).trim()) return String(a.videoUrl).trim()
      if (Array.isArray(a.images) && a.images.length) {
        const u = a.images.find(x => typeof x === 'string' && x.trim())
        return u ? String(u).trim() : ''
      }
      return ''
    },
    buildPersistState() {
      return {
        version: 1,
        chatMessages: cloneMessagesForStorage(this.chatMessages),
        bottomInput: this.bottomInput || '',
        awaitingVideoParams: !!this.awaitingVideoParams,
        videoDuration: this.videoDuration,
        videoRatio: this.videoRatio,
        videoResolution: this.videoResolution
      }
    },
    bumpPendingPersistUploads(delta) {
      const n = parseInt(this.pendingPersistUploads, 10) || 0
      const next = Math.max(0, n + (parseInt(delta, 10) || 0))
      this.pendingPersistUploads = next
      return next
    },
    schedulePersistSession() {
      if (!this.sessionUuid) return
      if (this._persistTimer) {
        clearTimeout(this._persistTimer)
        this._persistTimer = null
      }
      this._persistTimer = setTimeout(() => {
        this._persistTimer = null
        // 仍有后台上传在进行：再等一会儿，避免把 blob/dataURL 写进会话导致刷新丢图
        if ((parseInt(this.pendingPersistUploads, 10) || 0) > 0) {
          const n = (parseInt(this._persistBackoffAttempts, 10) || 0) + 1
          this._persistBackoffAttempts = n
          // 最多等待 ~12s（避免上传异常时无限推迟保存）
          if (n <= 12) {
            this._persistTimer = setTimeout(() => {
              this._persistTimer = null
              this.schedulePersistSession()
            }, 250)
            return
          }
        }
        this._persistBackoffAttempts = 0
        this.persistSessionNow()
      }, 900)
    },
    async persistSessionNow() {
      if (!this.sessionUuid) return
      try {
        await saveChatWorkflowSession({
          uuid: this.sessionUuid,
          title: this.buildSessionTitle(),
          thumb_url: this.extractThumbUrl(),
          state: this.buildPersistState()
        })
        this.loadSessionList()
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || '保存工作流记录失败'
        console.warn(msg)
      }
    },
    applyLoadedState(state, opts) {
      const scrollToBottom = !(opts && opts.scrollToBottom === false)
      const v = state && typeof state === 'object' ? state : {}
      this.bottomInput = typeof v.bottomInput === 'string' ? v.bottomInput : ''
      this.videoDuration = Math.min(15, Math.max(1, parseInt(v.videoDuration, 10) || 5))
      this.videoRatio = v.videoRatio === '9:16' ? '9:16' : '16:9'
      const presets = this.videoResolutionPresets
      let res = (v.videoResolution && String(v.videoResolution).toLowerCase()) || '720p'
      if (!presets.includes(res)) res = '720p'
      this.videoResolution = res

      const raw = Array.isArray(v.chatMessages) ? v.chatMessages : []
      this.chatMessages = raw.map(m => {
        const role = m.role === 'assistant' ? 'assistant' : 'user'
        const pr = String(m.paramResolution || '').toLowerCase()
        return {
          id: m.id != null ? String(m.id) : genId(),
          role,
          content: typeof m.content === 'string' ? stripDataImageUrls(m.content) : '',
          contentPlain: typeof m.contentPlain === 'string' ? stripDataImageUrls(m.contentPlain) : '',
          contentHtml: typeof m.contentHtml === 'string' ? stripDataImageUrls(m.contentHtml) : '',
          kind: m.kind,
          uploads: Array.isArray(m.uploads)
            ? m.uploads
                .filter(u => u && typeof u === 'object')
                .map(u => ({
                  kind: u.kind === 'video' ? 'video' : 'image',
                  url: typeof u.url === 'string' ? stripDataImageUrls(u.url) : '',
                  name: typeof u.name === 'string' ? u.name : ''
                }))
                .filter(u => u.url && !String(u.url).startsWith('data:image/'))
            : [],
          referenceImages: Array.isArray(m.referenceImages)
            ? m.referenceImages
                .filter(x => typeof x === 'string' && x.trim())
                .map(x => stripDataImageUrls(String(x)))
                .filter(x => x && !String(x).startsWith('data:image/'))
            : [],
          images: Array.isArray(m.images)
            ? m.images
                .filter(u => typeof u === 'string' && u.trim())
                .map(u => stripDataImageUrls(String(u)))
                .filter(u => u && !String(u).startsWith('data:image/'))
            : [],
          paramRatio: m.paramRatio === '9:16' ? '9:16' : '16:9',
          paramDuration: Math.min(15, Math.max(1, parseInt(m.paramDuration, 10) || 5)),
          paramResolution: presets.includes(pr) ? pr : '720p',
          resolved: !!m.resolved,
          cancelled: !!m.cancelled,
          streaming: false,
          videoUrl: typeof m.videoUrl === 'string' ? stripDataImageUrls(m.videoUrl) : '',
          taskId: typeof m.taskId === 'string' ? String(m.taskId) : '',
          taskStatus: typeof m.taskStatus === 'string' ? String(m.taskStatus) : '',
          seedreamJob: m.seedreamJob && typeof m.seedreamJob === 'object' ? m.seedreamJob : null,
          apiContent: typeof m.apiContent === 'string' ? stripDataImageUrls(m.apiContent) : ''
        }
      })
      this.awaitingVideoParams = this.chatMessages.some(
        m => m.role === 'assistant' && m.kind === 'video_options' && !m.resolved
      )

      // 性能优化：历史会话里若残留 blob:，刷新时浏览器会反复尝试加载并拖慢页面。
      // blob: 只在创建它的页面会话中有效，无法在刷新后恢复，因此直接清理掉。
      for (const m of this.chatMessages || []) {
        if (!m) continue
        if (typeof m.contentHtml === 'string' && m.contentHtml.includes('blob:')) {
          // 移除所有 blob: 引用块与 src，避免触发网络/解码慢路径
          m.contentHtml = m.contentHtml.replace(/blob:[^\s"'<>]+/g, '')
        }
        if (Array.isArray(m.uploads) && m.uploads.length) {
          m.uploads = m.uploads.filter(u => u && typeof u.url === 'string' && !u.url.startsWith('blob:'))
        }
        if (Array.isArray(m.referenceImages) && m.referenceImages.length) {
          m.referenceImages = m.referenceImages.filter(u => typeof u === 'string' && !String(u).startsWith('blob:'))
        }
        if (Array.isArray(m.images) && m.images.length) {
          m.images = m.images.filter(u => typeof u === 'string' && !String(u).startsWith('blob:'))
        }
      }

      // 会话加载后默认只渲染最近一段，避免大列表首屏卡顿
      const total = (this.chatMessages || []).length
      this.chatRenderLimit = Math.min(Math.max(this.chatRenderLimit || 20, 20), total || 20)
      if (scrollToBottom) this.scrollChatBottom()

      this.$nextTick(() => {
        this.optimizeMediaElements(this.$refs.chatScroll)
        this.resumeSeedanceTasksFromChat()
        this.resumeSeedreamJobsFromChat()
      })
    },
    resumeSeedreamJobsFromChat() {
      const list = Array.isArray(this.chatMessages) ? this.chatMessages : []
      for (const m of list) {
        if (!m || m.role !== 'assistant') continue
        if (m.kind !== 'images') continue
        if (Array.isArray(m.images) && m.images.length) continue
        if (!m.seedreamJob || typeof m.seedreamJob !== 'object') continue
        this.resumeSeedreamForMessage(m)
      }
    },
    resumeSeedreamForMessage(msg) {
      const id = String(msg?.id || '').trim()
      if (!id) return
      if (!this._resumingSeedreamMsgIds) this._resumingSeedreamMsgIds = {}
      if (this._resumingSeedreamMsgIds[id]) return
      this._resumingSeedreamMsgIds[id] = true

      ;(async () => {
        try {
          const job = msg.seedreamJob || {}
          const attempts = Number(job.attempts || 0)
          if (attempts >= 2) return
          job.attempts = attempts + 1
          msg.seedreamJob = job
          msg.streaming = true
          msg.content = (msg.content || '') + '\n\n（页面刷新后已自动继续生成…）'
          // eslint-disable-next-line no-await-in-loop
          const out = await generateSeedreamImages({
            prompt: String(job.prompt || ''),
            n: Number(job.n || 1),
            response_format: String(job.response_format || 'url'),
            ...(Array.isArray(job.images) && job.images.length ? { image: job.images[0], images: job.images.slice(0, 15) } : {})
          })
          const urls = pickSeedreamImageUrls(out)
          this.$set(msg, 'images', urls)
          msg.streaming = false
          if (urls.length) {
            this.$set(msg, 'seedreamJob', null)
            // 若原文是“生成中…”，替换成完成提示
            if (String(msg.content || '').includes('正在使用')) {
              msg.content = '生成完成（刷新后已自动恢复任务）。'
            }
          } else {
            msg.content = (msg.content || '') + '\n\n未返回图片链接。'
          }
        } catch {
          msg.streaming = false
        } finally {
          delete this._resumingSeedreamMsgIds[id]
          try {
            await this.persistSessionNow()
          } catch {
            // ignore
          }
        }
      })()
    },
    resumeSeedanceTasksFromChat() {
      const list = Array.isArray(this.chatMessages) ? this.chatMessages : []
      for (const m of list) {
        if (!m || m.role !== 'assistant') continue
        const id = String(m.taskId || '').trim()
        if (!id) continue
        if (m.videoUrl && String(m.videoUrl).trim()) continue
        const st = String(m.taskStatus || '').toLowerCase()
        if (st === 'succeeded' || st === 'failed' || st === 'cancelled') continue
        this.resumeSeedancePollingForMessage(m)
      }
    },
    resumeSeedancePollingForMessage(msg) {
      const taskId = String(msg?.taskId || '').trim()
      if (!taskId) return
      if (!this._resumingSeedanceTaskIds) this._resumingSeedanceTaskIds = {}
      if (this._resumingSeedanceTaskIds[taskId]) return
      this._resumingSeedanceTaskIds[taskId] = true

      const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))
      const controller = new AbortController()

      ;(async () => {
        try {
          for (let i = 0; i < 200; i++) {
            // eslint-disable-next-line no-await-in-loop
            const info = await getSeedanceVideoTask(taskId, controller.signal)
            const st2 = String(info.status || '').toLowerCase()
            this.$set(msg, 'taskStatus', String(info.status || ''))
            msg.content = `任务 \`${taskId}\` 当前状态：**${seedanceStatusZh(info.status)}**\n\n`

            if (st2 === 'succeeded') {
              const url = info.video_url || (info.content && info.content.video_url)
              if (url) {
                this.$set(msg, 'videoUrl', url)
                msg.content += '**生成完成**（刷新后已自动恢复任务）。\n\n'
              } else {
                msg.content += '任务显示成功但未返回 `video_url`。\n\n'
              }
              break
            }
            if (st2 === 'failed' || st2 === 'cancelled') {
              msg.content += `**任务结束**：${seedanceStatusZh(st2)}\n\n`
              break
            }
            // eslint-disable-next-line no-await-in-loop
            await sleep(3000)
          }
        } catch {
          // ignore
        } finally {
          delete this._resumingSeedanceTaskIds[taskId]
          try {
            await this.persistSessionNow()
          } catch {
            // ignore
          }
        }
      })()
    },
    async applyPromptHandoffFromPlus() {
      if (String(this.$route.query.handoff || '') !== '1') return

      const sid = this.$route.query && String(this.$route.query.session || '').trim()
      const payload = consumeWorkflowPromptHandoff()
      const nextQuery = { ...(this.$route.query || {}) }
      delete nextQuery.handoff
      this.$router.replace({ name: 'ConversationWorkflow', query: nextQuery }).catch(() => {})

      if (!payload) return
      if (sid) return

      const modelId = payload.modelId != null ? String(payload.modelId) : ''
      if (modelId && (this.modelOptions || []).some((m) => m.id === modelId)) {
        this.selectedModelId = modelId
      }

      if (payload.style && payload.style.id) {
        this.selectedStyle = { ...payload.style }
      } else if (payload.style === null) {
        this.selectedStyle = null
      }

      const comp = this.$refs.bottomComposer
      const html = String(payload.contentHtml || '').trim()
      const text = String(payload.contentPlain || payload.prompt || '').trim()

      if (comp && html) {
        try {
          comp.innerHTML = html
        } catch (e) {
          /* ignore */
        }
        this.bottomInput = getComposerPlainText(comp).trim() || text
        this.composerIsEmpty = false
      } else if (text && comp) {
        this.bottomInput = text
        try {
          comp.innerText = text
        } catch (e) {
          /* ignore */
        }
        this.composerIsEmpty = false
      }

      try {
        this.onComposerInput()
      } catch (e) {
        /* ignore */
      }

      const uploads = Array.isArray(payload.referencedUploads)
        ? payload.referencedUploads
        : Array.isArray(payload.uploads)
          ? payload.uploads
          : []
      for (const raw of uploads) {
        if (!raw || !raw.url) continue
        let url = String(raw.url)
        if (!/^https?:\/\//i.test(url)) {
          try {
            url = await persistWorkflowUploadUrl(url, raw.name)
          } catch (e) {
            /* ignore */
          }
        }
        const u = {
          kind: raw.kind === 'video' ? 'video' : 'image',
          url,
          name: raw.name || ''
        }
        if (!Array.isArray(this.localUploads)) this.localUploads = []
        const exists = this.localUploads.some((x) => x && String(x.url) === url)
        if (!exists) this.localUploads.push(u)
        if (!hasInlineUploadByUrl(comp, url)) {
          this.insertReferenceUpload(u)
        }
      }

      this.focusComposer()
      await this.$nextTick()

      if (payload.autoSend) {
        try {
          await this.onSend()
        } catch (e) {
          /* ignore */
        }
      }
    },

    async loadSessionFromRoute() {
      const sid = this.$route.query && String(this.$route.query.session || '').trim()
      if (!sid) {
        this.startNewWorkflowLocal()
        return
      }
      if (this._loadingRemoteSession) return
      if (sid === this.sessionUuid && (this.chatMessages || []).length > 0) return

      this._loadingRemoteSession = true
      try {
        const { state, meta } = await fetchChatWorkflowSessionDetail(sid, {
          tail: this.serverTail || 20,
          strip_data_images: 1
        })
        this.sessionUuid = sid
        const total = Number(meta?.total_chat_messages || 0)
        const returned = Number(meta?.returned_chat_messages || 0)
        this.serverTotalChatMessages = Number.isFinite(total) ? total : 0
        this.serverHasMore = total > 0 ? returned < total : false
        this.applyLoadedState(state || {})
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || '加载会话失败'
        showErrorToast(msg)
      } finally {
        this._loadingRemoteSession = false
      }
    },

    assistantHtml(content) {
      const raw = content == null ? '' : String(content)
      if (!raw) return ''
      try {
        const html = marked.parse(raw)
        return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
      } catch (e) {
        return DOMPurify.sanitize(`<p class="whitespace-pre-wrap">${String(raw)}</p>`)
      }
    },
    /** 参数卡片内滑块：1～15 的当前整秒 */
    durationSliderValue(m) {
      const n = parseInt(m && m.paramDuration, 10)
      if (Number.isNaN(n)) return 5
      return Math.min(15, Math.max(1, n))
    },
    /** 已选时长在轨道上的百分比（用于蓝/灰双色轨） */
    durationSliderPct(m) {
      const v = this.durationSliderValue(m)
      return ((v - 1) / 14) * 100
    },
    onDurationSliderInput(m, e) {
      if (!m || !e || !e.target) return
      const raw = parseInt(e.target.value, 10)
      const v = Number.isNaN(raw) ? 5 : Math.min(15, Math.max(1, raw))
      this.$set(m, 'paramDuration', v)
    },
    scrollChatBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatScroll
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    resetSession() {
      this.startNewWorkflowLocal()
      this.$router.replace({ name: 'ConversationWorkflow', query: {} }).catch(() => {})
    },
    confirmVideoParams(m) {
      if (!m || m.resolved || m.kind !== 'video_options') return
      this.$set(m, 'resolved', true)
      this.$set(m, 'cancelled', false)
      const ratio = m.paramRatio === '9:16' ? '9:16' : '16:9'
      const duration = Math.min(15, Math.max(1, parseInt(m.paramDuration, 10) || 5))
      const presets = this.videoResolutionPresets
      let resolution = (m.paramResolution && String(m.paramResolution).toLowerCase()) || '720p'
      if (!presets.includes(resolution)) resolution = '720p'
      this.$set(m, 'paramDuration', duration)
      this.$set(m, 'paramResolution', resolution)
      this.videoRatio = ratio
      this.videoDuration = duration
      this.videoResolution = resolution
      this.awaitingVideoParams = false
      this.scrollChatBottom()
      this.schedulePersistSession()
      this.runVideoJob({ ratio, duration, resolution })
    },
    cancelVideoParams(m) {
      if (!m || m.resolved || m.kind !== 'video_options') return
      this.$set(m, 'resolved', true)
      this.$set(m, 'cancelled', true)
      this.awaitingVideoParams = false
      this.scrollChatBottom()
      this.schedulePersistSession()
    },
    onEnterSend() {
      if (this.isStreaming || this.awaitingVideoParams) return
      const hasText = !!((this.bottomInput || '').trim())
      const hasRefs = !!(Array.isArray(this.referencedUploads) && this.referencedUploads.length)
      const hasInline = this.composerHasInlineUploads()
      if (!hasText && !hasRefs && !hasInline) return
      this.onSend()
    },
    async onSend() {
      if (this.isStreaming || this.awaitingVideoParams) return

      const comp = this.$refs.bottomComposer
      // 发送前强制同步一次引用列表（避免仅内联缩略图时 referencedUploads 为空）
      try {
        this.onComposerInput()
      } catch {
        // ignore
      }

      const contentPlainRaw = comp && comp.innerText != null ? String(comp.innerText) : ''
      const contentPlain = contentPlainRaw.replace(/\u00a0/g, ' ').trim()
      // 发送前：把输入框里所有内联引用（blob/dataURL）先上传到 /video_images 固化为 http(s)
      // 否则保存进会话的 HTML 仍可能是 blob/dataURL，刷新后会被清理/无法显示。
      try {
        await this.materializeComposerInlineUploads(comp)
      } catch {
        // ignore
      }
      try {
        this.onComposerInput()
      } catch {
        // ignore
      }
      // 上传/替换后重新读取最新 innerHTML（确保会话里保存的是可访问 URL，而不是 blob）
      const contentHtmlRaw = comp ? String(comp.innerHTML || '') : ''
      const contentHtml = this.sanitizeUserBubbleHtml(contentHtmlRaw)

      const text = (this.bottomInput || '').trim() || contentPlain
      const refs = Array.isArray(this.referencedUploads) ? this.referencedUploads : []
      const hasInline = this.composerHasInlineUploads()
      if (!text && !refs.length && !hasInline) return

      const isNewSession = !this.sessionUuid
      const isSeedream = this.selectedModelId === 'seedream-5'
      const isCharacterDesigner = this.selectedModelId === 'character-designer'
      const isScriptWriter = this.selectedModelId === 'script-writer'
      const isSeedance = this.selectedModelId === 'seedance-video'
      const isAgent = this.selectedModelId === 'agent'
      const apiContent = isSeedance ? buildVideoApiContent(text || '（含参考图）') : ''
      const referenceImages = isSeedance
        ? await this.buildReferenceImagesForSeedance(refs)
        : await this.buildReferenceImagesForApi(refs)

      // 发送后气泡与输入框一致：保存 contentHtml（含内联缩略图顺序）
      const userMsg = {
        id: genId(),
        role: 'user',
        content: text,
        contentPlain,
        ...(contentHtml ? { contentHtml } : {}),
        ...(apiContent ? { apiContent } : {}),
        ...(referenceImages.length ? { referenceImages } : {})
      }
      this.chatMessages.push(userMsg)
      this.bottomInput = ''
      this.referencedUploads = []
      this.composerIsEmpty = true
      if (comp) {
        try {
          comp.innerHTML = ''
        } catch {
          // ignore
        }
      }
      this.scrollChatBottom()

      if (isAgent) {
        // Agent：先请求后端返回“剧情风格选项”，再让用户点击确认
        this.agentPendingPrompt = text
        this.agentPendingIsNewSession = !!isNewSession
        // 艺术总监：先给一段“规划说明”（前端模拟流式打字），再进入风格选项
        const directorMsg = {
          id: genId(),
          role: 'assistant',
          kind: 'agent_art_director',
          collapsed: false,
          content: '',
          streaming: true
        }
        this.chatMessages.push(directorMsg)
        this.scrollChatBottom()
        const fullText =
          `- 导演，你最新的创作需求是制作一部短片，我已经收到。\n` +
          `- 我们会先根据题材匹配**剧情风格**与情绪基调，再确认视频参数（长度/比例/对白语言）。\n` +
          `- 之后按流程产出：**剧本 → 剧本确认 → 角色设计图（逐角色）→ 角色确认 → 分镜视频生成**。\n` +
          `- 正在启动前期规划，请稍作等待…`
        this.startAgentTyping(directorMsg, fullText, 14)
        this.requestAgentStyleOptions({ prompt: text })
        return
      }

      if (isScriptWriter) {
        this.runScriptJob({ prompt: text, isNewSession })
        return
      }

      if (isCharacterDesigner) {
        this.runCharacterDesignerJob({ prompt: text, isNewSession })
        return
      }

      if (isSeedream) {
        this.runSeedreamJob({ prompt: text, isNewSession, referenceImages })
        return
      }

      // 默认仍走对话工作流的视频参数卡片（Seedance）
      if (isSeedance || this.modelTab === 'video') {
        this.chatMessages.push({
          id: genId(),
          role: 'assistant',
          kind: 'video_options',
          paramRatio: '9:16',
          paramDuration: 5,
          paramResolution: '720p',
          resolved: false,
          cancelled: false
        })
        this.awaitingVideoParams = true
      } else {
        // 其它图片模型：暂未接入，提示用户切换到 Seedream
        this.chatMessages.push({
          id: genId(),
          role: 'assistant',
          content: '当前仅接入 **Seedream 5.0** 图片生成与 **Seedance** 文生视频。\n\n请在下方「模型」里选择 Seedream 5.0（图片）或 Seedance（视频）。'
        })
      }
      this.scrollChatBottom()

      if (isNewSession) {
        this.sessionUuid = newSessionUuid()
        this.$router
          .replace({ name: 'ConversationWorkflow', query: { session: this.sessionUuid } })
          .catch(() => {})
      }
      this.schedulePersistSession()
    },
    /**
     * 解析 /agent_workflow 与 /agent2_finalize 的成功响应，更新气泡并触发角色出图/确认卡片。
     */
    async handleAgentWorkflowApiSuccess(res, assistant, ctx) {
      const raw = ctx.raw
      const isNewSession = ctx.isNewSession
      const stage = ctx.stage
      const scriptCn = ctx.scriptCn
      const genImages = ctx.genImages
      const genVideoTask = ctx.genVideoTask
      const prependMd = ctx.prependMarkdown ? String(ctx.prependMarkdown) : ''

        // 兼容：后端可能返回 {code,data:{plan,...}} 或 {data:{plan,...}} 或更深一层 data
        const payload0 = res && res.data ? res.data : res
        const payload =
          (payload0 && payload0.data && typeof payload0.data === 'object' ? payload0.data : payload0) || {}
        // plan 可能是 object，也可能是后端透传的一段 JSON 字符串，甚至直接把 plan 字段“拍平”到 data 根上
        const planCandidate =
          (payload && payload.plan != null ? payload.plan : null) || (payload0 && payload0.plan != null ? payload0.plan : null)
        let plan = {}
        const coercePlanFromString = s0 => {
          const s = s0 != null ? String(s0).trim() : ''
          if (!s) return {}
          try {
            const o = JSON.parse(s)
            return o && typeof o === 'object' ? o : {}
          } catch {
            const p1 = s.indexOf('{')
            const p2 = s.lastIndexOf('}')
            if (p1 >= 0 && p2 > p1) {
              try {
                const o2 = JSON.parse(s.slice(p1, p2 + 1))
                return o2 && typeof o2 === 'object' ? o2 : {}
              } catch {
                return {}
              }
            }
            return {}
          }
        }
        if (planCandidate && typeof planCandidate === 'object') {
          plan = planCandidate
        } else if (typeof planCandidate === 'string') {
          plan = coercePlanFromString(planCandidate)
        } else if (typeof payload === 'string') {
          // 少数后端直接返回 JSON 字符串
          plan = coercePlanFromString(payload)
        } else if (typeof payload0 === 'string') {
          plan = coercePlanFromString(payload0)
        } else if (payload && typeof payload === 'object' && (payload.script_cn || payload.storyboard_md || payload.characters)) {
          // 后端把 plan “拍平”到 data 根上
          plan = payload
        } else if (payload0 && typeof payload0 === 'object' && (payload0.script_cn || payload0.storyboard_md || payload0.characters)) {
          plan = payload0
        }
        // 若后端把 JSON 放在 _raw，且其它字段为空，也尝试解析 _raw
        if (plan && typeof plan === 'object' && (!plan.script_cn || !String(plan.script_cn).trim()) && typeof plan._raw === 'string') {
          const s2 = String(plan._raw || '').trim()
          if (s2 && s2.startsWith('{')) {
            try {
              const parsed = JSON.parse(s2)
              if (parsed && typeof parsed === 'object') plan = parsed
            } catch {
              // ignore
            }
          }
        }

        const script = String(plan && plan.script_cn ? plan.script_cn : '').trim()
        const storyboard = String(plan && plan.storyboard_md ? plan.storyboard_md : '').trim()
        const charPrompt = String(plan && plan.character_prompt_en ? plan.character_prompt_en : '').trim()

        const parts = []
        // 避免“剧本显示两次”：
        // - stage=script：需要展示剧本供用户确认
        // - stage!=script 且前端已传入已确认剧本（scriptCn）：后续阶段不再重复展示剧本，只展示分镜/提示词等
        const hasConfirmedScript = scriptCn != null && String(scriptCn).trim()
        const shouldShowScript = stage === 'script' || !hasConfirmedScript
        if (shouldShowScript && script) parts.push(`## 剧本\n\n${script}`)
        if (stage !== 'script') {
          if (storyboard) parts.push(`## 分镜\n\n${storyboard}`)
          if (charPrompt) parts.push(`## 角色设计图提示词（英文）\n\n${charPrompt}`)
        }
        if (parts.length) {
          assistant.content = prependMd + parts.join('\n\n')
        } else if (plan._raw) {
          assistant.content = prependMd + String(plan._raw)
        } else {
          // 兜底：展示原始 payload，便于定位后端返回结构（避免“未返回有效内容”）
          let dumped = ''
          try {
            dumped = JSON.stringify(payload0, null, 2)
          } catch {
            dumped = String(payload0 || '')
          }
          assistant.content = prependMd + `未解析到 plan 内容。\n\n【原始返回】\n${dumped.slice(0, 4000)}`
        }
        assistant.streaming = false
        this.scrollChatBottom()

        if (this.isAgentModel && stage !== 'script') {
          this.seedAgentCanvasFromWorkflowPlan(script, storyboard, charPrompt, raw)
        }

        // 脚本阶段：记录剧本并弹出确认卡片（不继续出图/出视频）
        if (stage === 'script') {
          this.agentPendingScript = script
          this.chatMessages.push({
            id: genId(),
            role: 'assistant',
            kind: 'agent_script_confirm'
          })
          this.scrollChatBottom()
          return
        }

        // 角色设计图图片（逐角色）
        const charImgsRaw =
          (payload.character_images && Array.isArray(payload.character_images) && payload.character_images) ||
          (payload.characterImages && Array.isArray(payload.characterImages) && payload.characterImages) ||
          []
        const charTask =
          (payload && payload.character_images_task && typeof payload.character_images_task === 'object'
            ? payload.character_images_task
            : null) ||
          (payload && payload.characterImagesTask && typeof payload.characterImagesTask === 'object' ? payload.characterImagesTask : null)
        const charTaskId = charTask && charTask.id ? String(charTask.id).trim() : ''
        if (charImgsRaw.length) {
          // 记录角色参考图（用于后续创建视频任务逐一引用）
          this.agentPendingCharacterImages = []
          // 记录 plan（用于拿 storyboard_video_prompt_cn）
          this.agentPendingPlan = plan

          for (const ci of charImgsRaw) {
            const name = ci && ci.name ? String(ci.name) : ''
            const promptText = ci && ci.prompt ? String(ci.prompt).trim() : ''
            const imgs = ci && Array.isArray(ci.images) ? ci.images : []
            const urls = imgs
              .map(x => (x && typeof x === 'object' ? (x.public_url || x.url || '') : ''))
              .filter(u => typeof u === 'string' && u.trim())
              .map(u => u.trim())
            if (!urls.length) continue
            // 每个角色取第一张作为参考图
            this.agentPendingCharacterImages.push({ name, url: urls[0] })
            this.chatMessages.push({
              id: genId(),
              role: 'assistant',
              kind: 'images',
              content:
                (name ? `角色「${name}」设计图已生成。` : '角色设计图已生成。') +
                (promptText ? `\n\n提示词：\n${promptText}` : ''),
              streaming: false,
              images: urls
            })
          }
          this.scrollChatBottom()
          this.attachAgentCanvasCharacterImages(charImgsRaw)
          // 角色设计确认卡片
          this.chatMessages.push({
            id: genId(),
            role: 'assistant',
            kind: 'agent_character_confirm'
          })
          this.scrollChatBottom()
        } else {
          // 更稳定：若后端返回了“角色出图任务”，则前端轮询推进（每次生成一个角色）
          if (genImages && charTaskId) {
            this.startAgentCharacterImagesTaskPolling({ taskId: charTaskId, plan })
          } else {
          // 兼容：后端未返回 character_images，但 plan 里有 characters（前端用 Seedream 兜底生成角色设计图）
          const chars = plan && Array.isArray(plan.characters) ? plan.characters : []
          if (genImages && chars.length) {
            this.agentPendingCharacterImages = []
            this.agentPendingPlan = plan

            const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
            const stylePrompt = (this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : '').trim()
            const styleRef =
              (this.selectedStyle && this.selectedStyle.refDataUrl ? String(this.selectedStyle.refDataUrl) : '') ||
              (this.selectedStyle && this.selectedStyle.refUrl ? String(this.selectedStyle.refUrl) : '') ||
              (this.selectedStyle && this.selectedStyle.thumb ? String(this.selectedStyle.thumb) : '')
            const styleImages = styleRef ? [styleRef, styleRef] : []

            for (const c of chars) {
              const name = c && c.name ? String(c.name) : ''
              const briefCn = c && c.brief_cn ? String(c.brief_cn).trim() : ''
              const instrEn = c && c.design_instruction_en ? String(c.design_instruction_en).trim() : ''
              const basePrompt =
                instrEn ||
                (briefCn
                  ? `Character design sheet. ${briefCn}`
                  : 'Character design sheet, clean layout, neutral background.')
              const styleBlock =
                styleName || stylePrompt || styleRef
                  ? `STYLE LOCK (must follow):\n` +
                    `- Match the style reference image as closely as possible (materials, lighting, color palette, rendering).\n` +
                    `- The style reference is ONLY for STYLE TRANSFER, NOT for subject/identity.\n` +
                    `- Selected style name: ${styleName || 'custom style'}.\n` +
                    (stylePrompt ? `- Style keywords (must reflect): ${stylePrompt}\n` : '') +
                    `- Clean design-sheet layout, white or light gray background.\n`
                  : ''
              const finalPrompt = styleBlock ? `${styleBlock}\n\n${basePrompt}` : basePrompt

              const assistantImg = {
                id: genId(),
                role: 'assistant',
                kind: 'images',
                content: (name ? `正在生成角色「${name}」设计图…` : '正在生成角色设计图…'),
                streaming: true,
                images: [],
                seedreamJob: {
                  kind: 'seedream',
                  prompt: finalPrompt,
                  images: styleImages,
                  n: 1,
                  response_format: 'url',
                  createdAt: Date.now(),
                  attempts: 0
                }
              }
              this.chatMessages.push(assistantImg)
              this.scrollChatBottom()

              try {
                const out = await generateSeedreamImages({
                  prompt: finalPrompt,
                  n: 1,
                  response_format: 'url',
                  ...(styleRef ? { image: styleRef, images: styleImages } : {})
                })
                const urls = pickSeedreamImageUrls(out)
                this.$set(assistantImg, 'images', urls)
                assistantImg.streaming = false
                if (urls.length) this.$set(assistantImg, 'seedreamJob', null)
                assistantImg.content = urls.length
                  ? (name ? `角色「${name}」设计图已生成。` : '角色设计图已生成。')
                  : (name ? `角色「${name}」未返回图片链接。` : '未返回图片链接。')
                if (urls.length) this.agentPendingCharacterImages.push({ name, url: urls[0] })
              } catch (e) {
                const msg = e?.message ? String(e.message) : '角色设计图生成失败'
                assistantImg.streaming = false
                assistantImg.content = (name ? `角色「${name}」设计图生成失败：${msg}` : `角色设计图生成失败：${msg}`)
              }
            }

            this.scrollChatBottom()
            this.chatMessages.push({
              id: genId(),
              role: 'assistant',
              kind: 'agent_character_confirm'
            })
            this.scrollChatBottom()
          }
          }

          // 兼容：旧字段（扁平 images）
          const imagesRaw = Array.isArray(payload.images) ? payload.images : (payload0 && Array.isArray(payload0.images) ? payload0.images : [])
          const imageUrls = (imagesRaw || [])
            .map(x => (x && typeof x === 'object' ? (x.public_url || x.url || '') : ''))
            .filter(u => typeof u === 'string' && u.trim())
            .map(u => u.trim())
          if (imageUrls.length) {
            this.chatMessages.push({
              id: genId(),
              role: 'assistant',
              kind: 'images',
              content: '角色设计图已生成（点击图片可预览/下载）。',
              streaming: false,
              images: imageUrls
            })
            this.scrollChatBottom()
          }
        }

        // 视频生成改为“角色确认后再执行”，这里不再自动创建任务

        if (isNewSession) {
          this.sessionUuid = newSessionUuid()
          this.$router
            .replace({ name: 'ConversationWorkflow', query: { session: this.sessionUuid } })
            .catch(() => {})
        }
        this.schedulePersistSession()
    },

    async runAgentJob(opts) {
      // Agent：统一走后端 /agent_workflow
      if (this.isStreaming) return
      const raw = (opts && opts.prompt != null ? String(opts.prompt) : '').trim()
      const isNewSession = !!(opts && opts.isNewSession)
      if (!raw) return

      this.isStreaming = true
      this.streamAbort = new AbortController()

      const assistant = {
        id: genId(),
        role: 'assistant',
        content: 'Agent 正在生成：剧本 · 分镜 · 角色设计图 · 分镜视频…',
        streaming: true
      }
      this.chatMessages.push(assistant)
      this.scrollChatBottom()

      try {
        const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
        const stylePrompt = this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : ''
        const styleRef =
          (this.selectedStyle && this.selectedStyle.refUrl ? String(this.selectedStyle.refUrl) : '') ||
          (this.selectedStyle && this.selectedStyle.thumb ? String(this.selectedStyle.thumb) : '')

        const vp = (opts && opts.videoParams && typeof opts.videoParams === 'object') ? opts.videoParams : {}
        const stage = (opts && opts.stage ? String(opts.stage) : 'full').trim() || 'full'
        const scriptCn = (opts && opts.scriptCn != null) ? String(opts.scriptCn) : ''
        const genImages = opts && Object.prototype.hasOwnProperty.call(opts, 'generateImages') ? !!opts.generateImages : true
        const genVideoTask = opts && Object.prototype.hasOwnProperty.call(opts, 'generateVideoTask') ? !!opts.generateVideoTask : true
        assistant.retryOpts = {
          prompt: raw,
          isNewSession,
          videoParams: { ...vp },
          stage,
          scriptCn,
          generateImages: genImages,
          generateVideoTask: genVideoTask,
          agent2: false
        }
        const res = await api.post(
          '/agent_workflow',
          {
            prompt: raw,
            stage,
            ...(scriptCn ? { script_cn: scriptCn } : {}),
            ...(styleName ? { style_name: styleName } : {}),
            ...(stylePrompt ? { style_prompt: stylePrompt } : {}),
            ...(styleRef ? { style_ref: styleRef } : {}),
            ...(vp.ratio ? { video_ratio: String(vp.ratio) } : {}),
            ...(vp.duration ? { video_duration: Number(vp.duration) } : {}),
            ...(vp.resolution ? { video_resolution: String(vp.resolution) } : {}),
            ...(vp.dialogLang ? { video_dialog_lang: String(vp.dialogLang) } : {}),
            generate_images: genImages ? 1 : 0,
            generate_video_task: genVideoTask ? 1 : 0
          },
          { signal: this.streamAbort.signal }
        )

        await this.handleAgentWorkflowApiSuccess(res, assistant, {
          raw,
          isNewSession,
          stage,
          scriptCn,
          genImages,
          genVideoTask
        })
      } catch (e) {
        const status = e?.response?.status
        const data = e?.response?.data
        let serverMsg = ''
        if (data && typeof data === 'object' && data.message) serverMsg = String(data.message)
        const msg = serverMsg || e?.message || 'Agent 生成失败'
        let detail = ''
        try {
          if (data && typeof data === 'object') detail = JSON.stringify(data).slice(0, 600)
          else if (typeof data === 'string') detail = String(data).slice(0, 600)
        } catch {
          detail = ''
        }
        const tip =
          status === 502
            ? '（通常是后端网关/上游服务异常或超时；需要看后端/Nginx日志）'
            : status === 504
              ? '（网关超时；需要提高后端超时或改为异步任务）'
              : ''
        assistant.kind = 'agent_error'
        assistant.content = `Agent 生成失败${status ? `（HTTP ${status}）` : ''}：${msg}${tip ? `\n\n${tip}` : ''}${detail ? `\n\n【后端返回片段】\n${detail}` : ''}`
        assistant.streaming = false
        showErrorToast('Agent 生成失败')
      } finally {
        this.isStreaming = false
        this.streamAbort = null
        this.scrollChatBottom()
      }
    },

    /**
     * 长耗时 POST：中间代理/浏览器掐断连接时 axios 常报 Network Error / ERR_NETWORK；有限重试（用户取消 abort 不重试）。
     */
    async postWithNetworkRetry(url, body, axiosConfig, meta) {
      const retries = meta && meta.retries != null ? Number(meta.retries) : 2
      const label = meta && meta.label != null ? String(meta.label) : ''
      const cfg = axiosConfig && typeof axiosConfig === 'object' ? { ...axiosConfig } : {}
      let last
      const maxAttempts = Math.max(0, retries) + 1
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          return await api.post(url, body, cfg)
        } catch (err) {
          last = err
          if (cfg.signal && cfg.signal.aborted) throw err
          const hasResp = !!(err && err.response)
          const msg = err && err.message ? String(err.message) : ''
          const code = err && err.code ? String(err.code) : ''
          const transient =
            !hasResp &&
            (code === 'ERR_NETWORK' ||
              code === 'ECONNRESET' ||
              code === 'ECONNABORTED' ||
              /network error/i.test(msg) ||
              /failed to fetch/i.test(msg))
          if (!transient || attempt >= maxAttempts - 1) {
            throw err
          }
          const waitMs = 2000 * (attempt + 1)
          try {
            if (label) {
              console.warn(
                `[Agent²] ${label} 连接异常，${waitMs}ms 后重试 (${attempt + 1}/${retries})`,
                code || msg
              )
            }
          } catch (e2) {
            /* ignore */
          }
          await new Promise(r => setTimeout(r, waitMs))
        }
      }
      throw last
    },

    teardownAgentCanvasListeners() {
      try {
        if (this._agentCanvasWinMove) {
          window.removeEventListener('mousemove', this._agentCanvasWinMove)
        }
        if (this._agentCanvasWinUp) {
          window.removeEventListener('mouseup', this._agentCanvasWinUp)
        }
      } catch {
        /* ignore */
      }
      this._agentCanvasWinMove = null
      this._agentCanvasWinUp = null
      this.agentCanvasPanning = null
      this.agentCanvasNodeDragging = null
    },
    bindAgentCanvasWindowDrag() {
      if (this._agentCanvasWinMove) return
      this._agentCanvasWinMove = e => this.onAgentCanvasWindowMove(e)
      this._agentCanvasWinUp = () => this.onAgentCanvasWindowUp()
      window.addEventListener('mousemove', this._agentCanvasWinMove)
      window.addEventListener('mouseup', this._agentCanvasWinUp)
    },
    onAgentCanvasWindowMove(e) {
      if (this.agentCanvasNodeDragging) {
        const d = this.agentCanvasNodeDragging
        const n = (this.agentCanvasNodes || []).find(x => x && x.id === d.id)
        if (n) {
          const s = this.agentCanvasScale || 1
          const dx = (e.clientX - d.sx) / s
          const dy = (e.clientY - d.sy) / s
          this.$set(n, 'x', Math.round(d.nx + dx))
          this.$set(n, 'y', Math.round(d.ny + dy))
        }
      } else if (this.agentCanvasPanning) {
        const p = this.agentCanvasPanning
        this.agentCanvasPan = {
          x: p.px + (e.clientX - p.sx),
          y: p.py + (e.clientY - p.sy)
        }
      }
    },
    onAgentCanvasWindowUp() {
      this.teardownAgentCanvasListeners()
    },
    onAgentCanvasWheel(e) {
      const delta = e.deltaY < 0 ? 1.08 : 1 / 1.08
      let ns = (this.agentCanvasScale || 1) * delta
      ns = Math.min(2.2, Math.max(0.4, ns))
      this.agentCanvasScale = ns
    },
    agentCanvasZoomDelta(dir) {
      const f = dir > 0 ? 1.12 : 1 / 1.12
      let ns = (this.agentCanvasScale || 1) * f
      ns = Math.min(2.2, Math.max(0.4, ns))
      this.agentCanvasScale = ns
    },
    onAgentCanvasSurfaceMouseDown(e) {
      if (e.target && e.target.closest && e.target.closest('.cw-agent-node')) return
      this.onAgentCanvasBgMouseDown(e)
    },
    onAgentCanvasBgMouseDown(e) {
      if (this.agentCanvasTool !== 'hand' && e.button !== 1) return
      if (e.button !== 0 && e.button !== 1) return
      e.preventDefault()
      this.agentCanvasSelectedId = null
      const pan = this.agentCanvasPan || { x: 0, y: 0 }
      this.agentCanvasPanning = {
        sx: e.clientX,
        sy: e.clientY,
        px: pan.x,
        py: pan.y
      }
      this.bindAgentCanvasWindowDrag()
    },
    onAgentCanvasNodeMouseDown(e, n) {
      if (!n) return
      if (this.agentCanvasTool === 'select') {
        this.agentCanvasSelectedId = n.id
      }
    },
    onAgentCanvasNodeHeaderDown(e, n) {
      if (!n || e.button !== 0) return
      e.preventDefault()
      this.agentCanvasSelectedId = n.id
      this.agentCanvasNodeDragging = {
        id: n.id,
        sx: e.clientX,
        sy: e.clientY,
        nx: n.x,
        ny: n.y
      }
      this.bindAgentCanvasWindowDrag()
    },
    resetAgentCanvasLayout() {
      const list = this.agentCanvasNodes || []
      list.forEach((n, i) => {
        this.$set(n, 'x', 80 + (i % 2) * 44)
        this.$set(n, 'y', 48 + i * 216)
      })
      this.agentCanvasPan = { x: 100, y: 60 }
      this.agentCanvasScale = 1
    },
    agentCanvasStepLabel(n) {
      const list = this.agentCanvasNodes || []
      const t = Math.max(list.length, 8)
      const s = n && n.step != null ? Number(n.step) : 1
      return `${s}/${t}`
    },
    /** Character Designer：按正文里 ### 角色名 拆成多段（与画布分块编辑一致） */
    parseCharacterDesignerBody(body) {
      const raw = String(body || '').replace(/\r\n/g, '\n')
      if (!raw.trim()) return { intro: '', slots: [] }
      const chunks = raw.split(/\n(?=###\s+)/)
      const introParts = []
      const slots = []
      for (const chunk of chunks) {
        const c = chunk.trimEnd()
        if (!c.trim()) continue
        if (/^###\s+/.test(c)) {
          const m = c.match(/^###\s+([^\n]+)(?:\n([\s\S]*))?$/m)
          if (m) {
            slots.push({
              name: String(m[1] || '').trim() || '未命名',
              content: String(m[2] || '').trim()
            })
          }
        } else {
          introParts.push(c)
        }
      }
      return { intro: introParts.join('\n\n').trim(), slots }
    },
    mergeCharacterDesignerBody(intro, slots) {
      const parts = []
      const i = String(intro || '').trim()
      if (i) parts.push(i)
      for (const s of slots || []) {
        const name = String(s.name || '').trim() || '未命名'
        const content = String(s.content || '').trim()
        parts.push(`### ${name}\n${content}`)
      }
      return parts.join('\n\n')
    },
    characterSlotNameMatch(a, b) {
      const x = String(a || '')
        .trim()
        .replace(/^###\s*/, '')
      const y = String(b || '')
        .trim()
        .replace(/^###\s*/, '')
      if (!x || !y) return false
      if (x === y) return true
      if (x.includes(y) || y.includes(x)) return true
      return false
    },
    getAgentCanvasNodeSerialBody(n) {
      if (!n || n.expert !== 'character_designer') return String(n.body || '')
      if (!n.characterSlots || !n.characterSlots.length) return String(n.body || '')
      return this.mergeCharacterDesignerBody(n.characterIntro, n.characterSlots)
    },
    applyCharacterDesignerParseToNode(n, rawBody) {
      if (!n || n.expert !== 'character_designer') return
      const prev = Array.isArray(n.characterSlots) ? [...n.characterSlots] : []
      const parsed = this.parseCharacterDesignerBody(rawBody)
      const findPrev = name => prev.find(p => this.characterSlotNameMatch(p.name, name))
      const slots = parsed.slots.map(s => {
        const o = findPrev(s.name)
        return {
          id: o && o.id ? o.id : genId(),
          name: s.name,
          content: s.content,
          images: o && Array.isArray(o.images) && o.images.length ? [...o.images] : [],
          regenerating: false
        }
      })
      this.$set(n, 'characterIntro', parsed.intro)
      this.$set(n, 'characterSlots', slots)
      const merged = this.mergeCharacterDesignerBody(parsed.intro, slots)
      this.$set(n, 'body', merged)
      const flat = []
      for (const s of slots) {
        for (const u of s.images || []) {
          if (u) flat.push(u)
        }
      }
      if (flat.length) this.$set(n, 'images', flat)
    },
    ensureSingleSlotCharacterDesigner(n) {
      if (!n || n.expert !== 'character_designer') return
      const parsed = this.parseCharacterDesignerBody(n.body)
      if (parsed.slots.length) {
        const slots = parsed.slots.map(s => ({
          id: genId(),
          name: s.name,
          content: s.content,
          images: [],
          regenerating: false
        }))
        this.$set(n, 'characterIntro', parsed.intro)
        this.$set(n, 'characterSlots', slots)
        this.$set(n, 'body', this.mergeCharacterDesignerBody(parsed.intro, slots))
        return
      }
      const b = String(n.body || '').trim()
      if (!b) {
        this.$set(n, 'characterIntro', '')
        this.$set(n, 'characterSlots', [])
        return
      }
      const one = {
        id: genId(),
        name: '全文',
        content: b,
        images: [...(n.images || [])],
        regenerating: false
      }
      this.$set(n, 'characterIntro', '')
      this.$set(n, 'characterSlots', [one])
      this.$set(n, 'body', this.mergeCharacterDesignerBody('', [one]))
    },
    syncNodeBodyFromCharacterSlots(n) {
      if (!n || n.expert !== 'character_designer') return
      const merged = this.mergeCharacterDesignerBody(n.characterIntro, n.characterSlots)
      this.$set(n, 'body', merged)
      const flat = []
      for (const s of n.characterSlots || []) {
        for (const u of s.images || []) {
          if (u) flat.push(u)
        }
      }
      if (flat.length) this.$set(n, 'images', flat)
    },
    onCharacterIntroInput(n, e) {
      if (!n || !e || !e.target) return
      this.$set(n, 'characterIntro', e.target.value)
      this.syncNodeBodyFromCharacterSlots(n)
    },
    onCharacterSlotContentInput(n, slot, e) {
      if (!n || !slot || !e || !e.target) return
      this.$set(slot, 'content', e.target.value)
      this.syncNodeBodyFromCharacterSlots(n)
    },
    async copyAgentCanvasCharacterSlot(slot) {
      if (!slot) return
      const t = `### ${String(slot.name || '').trim()}\n${String(slot.content || '')}`
      try {
        await navigator.clipboard.writeText(t)
        showSuccessToast('已复制本节')
      } catch {
        showErrorToast('复制失败')
      }
    },
    async regenerateAgentCanvasCharacterSlot(n, slot) {
      if (!n || !slot || n.expert !== 'character_designer') return
      const ctx = this.agentCanvasRunContext
      if (!ctx || !String(ctx.prompt || '').trim()) {
        showErrorToast('请先运行一次 Agent²，或从左侧重试生成')
        return
      }
      const idx = this.agentCanvasNodes.findIndex(x => x && x.id === n.id)
      if (idx < 0) return
      const prior = this.buildAgentCanvasPriorOutputs(idx)
      const full = this.getAgentCanvasNodeSerialBody(n)
      const slotName = String(slot.name || '').trim()
      let userPrompt =
        String(ctx.prompt || '') +
        `\n\n【当前角色设计定稿全文】\n${full}\n\n【本次任务】只重写「### ${slotName}」这一节：返回 **仅该节** 的正文（建议以「### ${slotName}」标题行开头，下面接英文生图指令）；其它 ### 章节不要输出、不要改。若当前仅有「全文」一节，则返回新的完整角色生图指令即可。`
      const note = String(n.editNote || '').trim()
      if (note) userPrompt += '\n\n【本步补充指令】\n' + note
      const savedName = slotName
      const savedImages = [...(slot.images || [])]
      const singleFull = slotName === '全文' && n.characterSlots && n.characterSlots.length === 1
      this.$set(slot, 'regenerating', true)
      try {
        const body = {
          expert: n.expert,
          user_prompt: userPrompt,
          ...(prior ? { prior_outputs: prior } : {}),
          ...(ctx.styleName ? { style_name: ctx.styleName } : {}),
          ...(ctx.stylePrompt ? { style_prompt: ctx.stylePrompt } : {}),
          ...(ctx.chatBaseUrl ? { base_url: ctx.chatBaseUrl } : {}),
          ...(ctx.chatModel ? { model: ctx.chatModel } : {})
        }
        const er = await this.postWithNetworkRetry('/agent2_expert', body, {}, { retries: 2, label: '角色段落重生' })
        const payload0 = er && er.data ? er.data : er
        const expertPayload =
          (payload0 && payload0.data && typeof payload0.data === 'object' ? payload0.data : payload0) || {}
        const block =
          expertPayload && expertPayload.content != null ? String(expertPayload.content).trim() : ''
        if (!block) {
          showErrorToast('未返回内容')
          return
        }
        const sections = block
          .split(/\n(?=###\s+)/)
          .map(s => s.trim())
          .filter(Boolean)
        const headingSections = sections.filter(s => /^###\s+/.test(s))
        if (headingSections.length > 1) {
          this.applyCharacterDesignerParseToNode(n, block)
          const s2 = (n.characterSlots || []).find(s => this.characterSlotNameMatch(s.name, savedName))
          if (s2 && savedImages.length) this.$set(s2, 'images', savedImages)
        } else if (singleFull) {
          this.applyCharacterDesignerParseToNode(n, block)
          const s0 = (n.characterSlots || [])[0]
          if (s0 && savedImages.length) this.$set(s0, 'images', savedImages)
        } else if (/^###\s+/m.test(block)) {
          const m = block.trim().match(/^###\s+([^\n]+)\n?([\s\S]*)$/m)
          const heading = m ? String(m[1] || '').trim() : ''
          const inner = m ? String(m[2] || '').trim() : block.trim()
          if (heading && !this.characterSlotNameMatch(heading, savedName)) {
            this.$set(slot, 'name', heading)
          }
          this.$set(slot, 'content', inner)
          this.syncNodeBodyFromCharacterSlots(n)
          if (savedImages.length) this.$set(slot, 'images', savedImages)
        } else {
          this.$set(slot, 'content', block.trim())
          this.syncNodeBodyFromCharacterSlots(n)
          if (savedImages.length) this.$set(slot, 'images', savedImages)
        }
        showSuccessToast('本节已更新')
      } catch (e) {
        showErrorToast('重生失败')
      } finally {
        this.$set(slot, 'regenerating', false)
      }
    },
    /** 与 runCharacterDesignerJob 一致：交给 preset=character_designer 的对话模型处理 */
    buildAgentCanvasCharacterDesignerSlotUserMessage(slot) {
      if (!slot) return ''
      let base = String(slot.content || '').trim()
      if (!base) return ''
      const slotName = String(slot.name || '').trim()
      if (slotName && slotName !== '全文') {
        base = `角色：${slotName}\n\n${base}`
      }
      const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
      const stylePrompt = (this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : '').trim()
      const meta = []
      if (styleName) meta.push(`StyleName=${styleName}`)
      if (stylePrompt) meta.push(`StylePrompt=${stylePrompt}`)
      if (meta.length) {
        base = `${base}\n\n【已选风格】${meta.join('；')}`
      }
      return base
    },
    patchAgentPendingCharacterImageForSlot(slotName, url) {
      const list = this.agentPendingCharacterImages
      if (!Array.isArray(list) || !list.length || !url) return
      const name = String(slotName || '').trim()
      if (!name || name === '全文') return
      const u = String(url).trim()
      if (!u) return
      const i = list.findIndex(c => c && c.name && this.characterSlotNameMatch(c.name, name))
      if (i >= 0) this.$set(list[i], 'url', u)
    },
    async regenerateAgentCanvasCharacterSlotImage(n, slot) {
      if (!n || !slot || n.expert !== 'character_designer') return
      if (!this.selectedStyle) {
        showErrorToast('请先选择一个风格（底部「风格」）')
        this.openStyleLibrary()
        return
      }
      const userText = this.buildAgentCanvasCharacterDesignerSlotUserMessage(slot)
      if (!userText) {
        showErrorToast('请先填写本节生图指令')
        return
      }
      this.$set(slot, 'regeneratingImage', true)
      let designerFull = ''
      try {
        await streamWorkspaceReply({
          messages: [{ role: 'user', content: userText }],
          preset: 'character_designer',
          onDelta: chunk => {
            designerFull += chunk
          }
        })
      } catch (e) {
        const msg = e && e.message ? String(e.message) : '角色设计模型调用失败'
        showErrorToast(msg.length > 120 ? `${msg.slice(0, 120)}…` : msg)
        this.$set(slot, 'regeneratingImage', false)
        return
      }

      const full = String(designerFull || '').trim()
      if (!full) {
        showErrorToast('角色设计模型未返回内容')
        this.$set(slot, 'regeneratingImage', false)
        return
      }
      if (full.includes('【需要补充】')) {
        showErrorToast('模型需要补充信息，请完善本节描述后再试')
        this.$set(slot, 'regeneratingImage', false)
        return
      }

      let paintPrompt = full
      const m = full.match(/【绘图提示词】\s*([\s\S]+)/)
      if (m && m[1]) paintPrompt = String(m[1]).trim()
      paintPrompt = paintPrompt.replace(/^\s+|\s+$/g, '')
      if (!paintPrompt) {
        showErrorToast('未能解析绘图提示词')
        this.$set(slot, 'regeneratingImage', false)
        return
      }

      const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
      const stylePrompt = (this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : '').trim()
      const styleRef =
        (this.selectedStyle && this.selectedStyle.refDataUrl ? String(this.selectedStyle.refDataUrl) : '') ||
        (this.selectedStyle && this.selectedStyle.refUrl ? String(this.selectedStyle.refUrl) : '') ||
        (this.selectedStyle && this.selectedStyle.thumb ? String(this.selectedStyle.thumb) : '')
      const styleBlock =
        `STYLE LOCK (must follow):\n` +
        `- Match the style reference image as closely as possible (linework/brush, materials, lighting, color palette, rendering).\n` +
        `- The style reference is ONLY for STYLE TRANSFER, NOT for subject/identity. Do NOT copy the reference subject.\n` +
        `- Selected style name: ${styleName || 'custom style'}.\n` +
        (stylePrompt ? `- Style keywords (must reflect): ${stylePrompt}\n` : '') +
        `- Keep the character identity from the text prompt.\n` +
        `- Clean design-sheet layout, white or light gray background.\n`
      const finalPaintPrompt = `${styleBlock}\n\n${paintPrompt}`
      const styleImages = styleRef ? [styleRef, styleRef] : []

      try {
        const out = await generateSeedreamImages({
          prompt: finalPaintPrompt,
          n: 1,
          response_format: 'url',
          ...(styleRef ? { image: styleRef, images: styleImages } : {})
        })
        const urls = pickSeedreamImageUrls(out)
        if (!urls.length) {
          showErrorToast('未返回图片链接')
          return
        }
        this.$set(slot, 'images', urls)
        this.syncNodeBodyFromCharacterSlots(n)
        const first = urls[0]
        if (first) this.patchAgentPendingCharacterImageForSlot(slot.name, first)
        showSuccessToast('图片已更新')
      } catch (e) {
        const msg = e && e.message ? String(e.message) : '出图失败'
        showErrorToast(msg.length > 120 ? `${msg.slice(0, 120)}…` : msg)
      } finally {
        this.$set(slot, 'regeneratingImage', false)
      }
    },
    buildAgentCanvasPriorOutputs(beforeIdx) {
      const parts = []
      const list = this.agentCanvasNodes || []
      const t = Math.max(list.length, 8)
      for (let i = 0; i < beforeIdx; i++) {
        const n = list[i]
        if (!n) continue
        parts.push(`### ${n.step}/${t} ${n.title}\n\n${this.getAgentCanvasNodeSerialBody(n)}`)
      }
      return parts.join('\n\n---\n\n')
    },
    attachAgentCanvasCharacterImages(charImgsRaw) {
      if (!this.isAgentModel || !Array.isArray(charImgsRaw) || !charImgsRaw.length) return
      const node = [...this.agentCanvasNodes].reverse().find(n => n && n.expert === 'character_designer')
      if (!node) return
      const slots = node.characterSlots
      const hasSlots = node.expert === 'character_designer' && Array.isArray(slots) && slots.length

      const collectUrls = imgs => {
        const out = []
        if (!Array.isArray(imgs)) return out
        for (const im of imgs) {
          if (!im) continue
          const u =
            typeof im === 'string'
              ? im.trim()
              : typeof im === 'object'
                ? String(im.public_url || im.url || '').trim()
                : ''
          if (u) out.push(u)
        }
        return out
      }

      if (!hasSlots) {
        const all = []
        for (const ci of charImgsRaw) {
          all.push(...collectUrls(ci && ci.images))
        }
        if (all.length) this.$set(node, 'images', all)
        return
      }

      for (const ci of charImgsRaw) {
        const name = ci && ci.name != null ? String(ci.name).trim() : ''
        const urls = collectUrls(ci && ci.images)
        if (!urls.length) continue
        const target = name ? slots.find(s => this.characterSlotNameMatch(s.name, name)) : null
        if (target) {
          const merged = [...(target.images || []), ...urls]
          this.$set(target, 'images', merged)
        } else {
          const last = slots[slots.length - 1]
          if (last) {
            const merged = [...(last.images || []), ...urls]
            this.$set(last, 'images', merged)
          }
        }
      }
      const flat = []
      for (const s of slots) {
        for (const u of s.images || []) {
          if (u) flat.push(u)
        }
      }
      if (flat.length) this.$set(node, 'images', flat)
    },
    openAgentCanvasEdit(n) {
      if (!n) return
      this.agentCanvasEditTarget = n
      this.agentCanvasEditNote = String(n.editNote || '')
    },
    closeAgentCanvasEdit() {
      this.agentCanvasEditTarget = null
      this.agentCanvasEditNote = ''
    },
    confirmAgentCanvasEditRegenerate() {
      const n = this.agentCanvasEditTarget
      if (!n) return
      this.$set(n, 'editNote', String(this.agentCanvasEditNote || '').trim())
      this.closeAgentCanvasEdit()
      this.regenerateAgentCanvasNode(n)
    },
    async copyAgentCanvasNode(n) {
      if (!n) return
      try {
        await navigator.clipboard.writeText(this.getAgentCanvasNodeSerialBody(n))
        showSuccessToast('已复制正文')
      } catch {
        showErrorToast('复制失败')
      }
    },
    downloadAgentCanvasNode(n) {
      if (!n) return
      const blob = new Blob([this.getAgentCanvasNodeSerialBody(n)], { type: 'text/plain;charset=utf-8' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${String(n.expert || 'node')}.txt`
      a.click()
      URL.revokeObjectURL(a.href)
      showSuccessToast('已下载')
    },
    seedAgentCanvasFromWorkflowPlan(script, storyboard, charPrompt, raw) {
      if (!this.isAgentModel) return
      if (this.agentCanvasNodes && this.agentCanvasNodes.length) return
      const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
      const stylePrompt = (this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : '').trim()
      this.agentCanvasRunContext = {
        prompt: String(raw || ''),
        styleName,
        stylePrompt,
        chatModel: '',
        chatBaseUrl: '',
        genImages: true,
        genVideoTask: true,
        videoParams: this.agentPendingVideoParams && typeof this.agentPendingVideoParams === 'object'
          ? { ...this.agentPendingVideoParams }
          : {}
      }
      const nodes = []
      let i = 0
      const push = (expert, title, body) => {
        if (!String(body || '').trim()) return
        i += 1
        nodes.push({
          id: genId(),
          expert,
          step: i,
          title,
          body: String(body).slice(0, 20000),
          x: 80 + ((i - 1) % 2) * 44,
          y: 48 + (i - 1) * 220,
          images: [],
          editNote: '',
          regenerating: false
        })
      }
      push('one_shot_script', '剧本（一键 Agent）', script)
      push('one_shot_storyboard', '分镜', storyboard)
      push('one_shot_char_prompt', '角色设计图提示词', charPrompt)
      if (nodes.length) {
        this.agentCanvasNodes = nodes
        this.rightWorkspaceTab = 'canvas'
      }
    },
    async regenerateAgentCanvasNode(n) {
      if (n && String(n.expert || '').indexOf('one_shot') === 0) {
        showErrorToast('一键 Agent 卡片请从左侧重试；使用 Agent² 可单步调用专家接口重生成')
        return
      }
      const ctx = this.agentCanvasRunContext
      if (!ctx || !String(ctx.prompt || '').trim()) {
        showErrorToast('请先运行一次 Agent²，或从左侧重试生成')
        return
      }
      const idx = this.agentCanvasNodes.findIndex(x => x && x.id === n.id)
      if (idx < 0) return
      const prior = this.buildAgentCanvasPriorOutputs(idx)
      let userPrompt = String(ctx.prompt || '')
      const note = String(n.editNote || '').trim()
      if (note) userPrompt += '\n\n【本步补充指令】\n' + note
      this.$set(n, 'regenerating', true)
      try {
        const body = {
          expert: n.expert,
          user_prompt: userPrompt,
          ...(prior ? { prior_outputs: prior } : {}),
          ...(ctx.styleName ? { style_name: ctx.styleName } : {}),
          ...(ctx.stylePrompt ? { style_prompt: ctx.stylePrompt } : {}),
          ...(ctx.chatBaseUrl ? { base_url: ctx.chatBaseUrl } : {}),
          ...(ctx.chatModel ? { model: ctx.chatModel } : {})
        }
        const er = await this.postWithNetworkRetry('/agent2_expert', body, {}, { retries: 2, label: '画布节点重生成' })
        const payload0 = er && er.data ? er.data : er
        const expertPayload =
          (payload0 && payload0.data && typeof payload0.data === 'object' ? payload0.data : payload0) || {}
        const block =
          expertPayload && expertPayload.content != null ? String(expertPayload.content).trim() : ''
        const txt = block || '（未返回内容）'
        if (n.expert === 'character_designer') {
          this.applyCharacterDesignerParseToNode(n, txt)
        } else {
          this.$set(n, 'body', txt)
        }
        showSuccessToast('本步已更新')
      } catch (e) {
        showErrorToast('重生成失败')
      } finally {
        this.$set(n, 'regenerating', false)
      }
    },

    /**
     * Agent²：按智剧通 ZJT 八专家顺序链式调用，再汇总为与一键 Agent 相同 plan。
     */
    async runAgent2Job(opts) {
      if (this.isStreaming) return
      const raw = (opts && opts.prompt != null ? String(opts.prompt) : '').trim()
      const isNewSession = !!(opts && opts.isNewSession)
      if (!raw) return

      const AGENT2_IDS = [
        'story_writer',
        'character_creator',
        'location_creator',
        'plot_analyzer',
        'content_compliance',
        'novel_splitter',
        'character_designer',
        'location_designer'
      ]
      const AGENT2_TITLES = {
        story_writer: 'Story Writer · 故事编剧',
        character_creator: 'Character Creator · 人物设定',
        location_creator: 'Location Creator · 场景与世界观美术',
        plot_analyzer: 'Plot Analyzer · 情节与节奏',
        content_compliance: 'Content Compliance · 内容合规',
        novel_splitter: 'Novel Splitter · 场次拆分与节拍',
        character_designer: 'Character Designer · 角色形象（生图指令）',
        location_designer: 'Location Designer · 场景道具（生图指令）'
      }

      this.isStreaming = true
      this.streamAbort = new AbortController()

      const assistant = {
        id: genId(),
        role: 'assistant',
        content: 'Agent² 启动：按 **8 位专家**（智剧通分工）依次创作…',
        streaming: true
      }
      this.chatMessages.push(assistant)
      this.scrollChatBottom()

      const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
      const stylePrompt = this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : ''
      const styleRef =
        (this.selectedStyle && this.selectedStyle.refUrl ? String(this.selectedStyle.refUrl) : '') ||
        (this.selectedStyle && this.selectedStyle.thumb ? String(this.selectedStyle.thumb) : '')
      const vp = (opts && opts.videoParams && typeof opts.videoParams === 'object') ? opts.videoParams : {}
      const genImages = opts && Object.prototype.hasOwnProperty.call(opts, 'generateImages') ? !!opts.generateImages : true
      const genVideoTask = opts && Object.prototype.hasOwnProperty.call(opts, 'generateVideoTask') ? !!opts.generateVideoTask : true
      const chatModel = opts && opts.chatModel != null ? String(opts.chatModel).trim() : ''
      const chatBaseUrl = opts && opts.chatBaseUrl != null ? String(opts.chatBaseUrl).trim() : ''

      assistant.retryOpts = {
        prompt: raw,
        isNewSession,
        videoParams: { ...vp },
        stage: 'full',
        scriptCn: '',
        generateImages: genImages,
        generateVideoTask: genVideoTask,
        agent2: true,
        agent2ChatModel: chatModel,
        agent2ChatBaseUrl: chatBaseUrl
      }

      this.agentCanvasRunContext = {
        prompt: raw,
        styleName,
        stylePrompt,
        chatModel,
        chatBaseUrl,
        genImages,
        genVideoTask,
        videoParams: { ...vp }
      }
      this.agentCanvasNodes = []
      if (this.isAgentModel) {
        this.rightWorkspaceTab = 'canvas'
      }

      let prior = ''
      let step = 0
      const reportSections = []
      try {
        for (const expert of AGENT2_IDS) {
          step += 1
          const title = AGENT2_TITLES[expert] || expert
          assistant.content =
            `## Agent² · 智剧通八专家\n\n` +
            reportSections.join('\n\n---\n\n') +
            (reportSections.length ? '\n\n---\n\n' : '') +
            `### ${step}/8 ${title}\n\n*请求中…*`
          this.scrollChatBottom()

          const er = await this.postWithNetworkRetry(
            '/agent2_expert',
            {
              expert,
              user_prompt: raw,
              ...(prior ? { prior_outputs: prior } : {}),
              ...(styleName ? { style_name: styleName } : {}),
              ...(stylePrompt ? { style_prompt: stylePrompt } : {}),
              ...(chatBaseUrl ? { base_url: chatBaseUrl } : {}),
              ...(chatModel ? { model: chatModel } : {})
            },
            { signal: this.streamAbort.signal },
            { retries: 2, label: `第 ${step}/8 步 ${title}` }
          )
          // 与 handleAgentWorkflowApiSuccess 一致：兼容 Dingo 外层 { data: { code, data: {...} } }
          const payload0 = er && er.data ? er.data : er
          const expertPayload =
            (payload0 && payload0.data && typeof payload0.data === 'object' ? payload0.data : payload0) || {}
          const block =
            expertPayload && expertPayload.content != null ? String(expertPayload.content) : ''
          const section = `### ${step}/8 ${title}\n\n${block.trim() || '（本步未返回内容）'}`
          prior = prior ? `${prior}\n\n---\n\n${section}` : section
          reportSections.push(section)
          if (this.isAgentModel) {
            const node = {
              id: genId(),
              expert,
              step,
              title,
              body: block.trim() || '（本步未返回内容）',
              x: 80 + ((step - 1) % 2) * 44,
              y: 48 + (step - 1) * 216,
              images: [],
              editNote: '',
              regenerating: false
            }
            if (expert === 'character_designer') {
              this.ensureSingleSlotCharacterDesigner(node)
            }
            this.agentCanvasNodes.push(node)
          }
          assistant.content = `## Agent² · 智剧通八专家\n\n` + reportSections.join('\n\n---\n\n')
          this.scrollChatBottom()
        }

        assistant.content =
          `## Agent² · 智剧通八专家\n\n` +
          reportSections.join('\n\n---\n\n') +
          '\n\n---\n\n**正在汇总为成片方案（分镜 / 角色 / 视频提示词）…**'
        this.scrollChatBottom()

        const fr = await this.postWithNetworkRetry(
          '/agent2_finalize',
          {
            user_prompt: raw,
            expert_bundle: prior,
            ...(styleName ? { style_name: styleName } : {}),
            ...(stylePrompt ? { style_prompt: stylePrompt } : {}),
            ...(styleRef ? { style_ref: styleRef } : {}),
            ...(vp.ratio ? { video_ratio: String(vp.ratio) } : {}),
            ...(vp.duration ? { video_duration: Number(vp.duration) } : {}),
            ...(vp.resolution ? { video_resolution: String(vp.resolution) } : {}),
            ...(vp.dialogLang ? { video_dialog_lang: String(vp.dialogLang) } : {}),
            generate_images: genImages ? 1 : 0,
            generate_video_task: genVideoTask ? 1 : 0,
            ...(chatBaseUrl ? { base_url: chatBaseUrl } : {}),
            ...(chatModel ? { model: chatModel } : {})
          },
          { signal: this.streamAbort.signal },
          { retries: 2, label: '汇总 agent2_finalize' }
        )

        await this.handleAgentWorkflowApiSuccess(fr, assistant, {
          raw,
          isNewSession,
          stage: 'full',
          scriptCn: '',
          genImages,
          genVideoTask,
          prependMarkdown:
            `## Agent² · 智剧通八专家产出\n\n` +
            prior +
            `\n\n---\n\n## 汇总方案（可拍成片）\n\n`
        })
      } catch (e) {
        const status = e?.response?.status
        const data = e?.response?.data
        let serverMsg = ''
        if (data && typeof data === 'object' && data.message) serverMsg = String(data.message)
        const msg = serverMsg || e?.message || 'Agent² 生成失败'
        let detail = ''
        try {
          if (data && typeof data === 'object') detail = JSON.stringify(data).slice(0, 600)
          else if (typeof data === 'string') detail = String(data).slice(0, 600)
        } catch {
          detail = ''
        }
        const isNetworkErr =
          !status &&
          (String(msg).toLowerCase().includes('network') ||
            e?.code === 'ERR_NETWORK' ||
            e?.code === 'ECONNRESET' ||
            e?.code === 'ECONNABORTED')
        const tip = isNetworkErr
          ? '（无 HTTP 状态码：连接在传输中被关闭。常见原因：① 反向代理/云 WAF/公司网络中断长连接；② 线上站点未把 /agent2_expert、/agent2_finalize 反代到 PHP；③ 开发环境 webpack 代理超时（已加长 socket，请重启 npm run serve）；④ 系统 VPN/代理软件。请打开 F12→Network 看该请求是否红色 (failed)。已对每步自动重试 2 次。）'
          : status === 502
            ? '（多为反向代理或 PHP-FPM 超时：单次 /agent2_expert 会等 LLM 数十秒～数分钟，默认 Nginx 约 60s 即 502。请在 Nginx 为 /agent2_expert、/agent2_finalize 设置 proxy_read_timeout 600s；PHP-FPM request_terminate_timeout / max_execution_time 亦需放宽。仓库内 nginx-proxy.example.conf 已附示例。）'
            : status === 504
              ? '（网关超时）'
              : ''
        assistant.kind = 'agent_error'
        assistant.content = `Agent² 失败${status ? `（HTTP ${status}）` : ''}：${msg}${tip ? `\n\n${tip}` : ''}${detail ? `\n\n【后端返回片段】\n${detail}` : ''}`
        assistant.streaming = false
        showErrorToast('Agent² 生成失败')
      } finally {
        this.isStreaming = false
        this.streamAbort = null
        this.scrollChatBottom()
      }
    },

    retryAgentJob(m) {
      if (!m || !m.retryOpts || m._busy || m.resolved) return
      try {
        this.$set(m, '_busy', true)
      } catch {
        m._busy = true
      }
      // 复用同一个气泡做“重试中”展示
      m.streaming = true
      m.content = '正在重试…'
      this.scrollChatBottom()
      // 重新跑 agent（runAgentJob 会新建一条 assistant 消息；这里把旧错误条标记 resolved）
      try {
        this.$set(m, 'resolved', true)
      } catch {
        m.resolved = true
      }
      if (m.retryOpts && m.retryOpts.agent2) {
        this.runAgent2Job({
          prompt: m.retryOpts.prompt,
          isNewSession: m.retryOpts.isNewSession,
          videoParams: m.retryOpts.videoParams || {},
          chatModel:
            m.retryOpts.agent2ChatModel != null ? String(m.retryOpts.agent2ChatModel).trim() : '',
          chatBaseUrl:
            m.retryOpts.agent2ChatBaseUrl != null ? String(m.retryOpts.agent2ChatBaseUrl).trim() : '',
          generateImages: m.retryOpts.generateImages !== false,
          generateVideoTask: !!m.retryOpts.generateVideoTask
        })
        return
      }
      this.runAgentJob(m.retryOpts)
    },
    async runSeedreamJob(opts) {
      if (this.isStreaming) return
      let prompt = (opts && opts.prompt != null ? String(opts.prompt) : '').trim()
      const referenceImages = Array.isArray(opts?.referenceImages) ? opts.referenceImages : []
      if (!prompt && referenceImages.length) {
        prompt = '请基于参考图继续生成。'
      }
      if (!prompt) return

      this.isStreaming = true
      this.streamAbort = new AbortController()

      const assistant = {
        id: genId(),
        role: 'assistant',
        kind: 'images',
        content: '正在使用 **Doubao-Seedream 5.0** 生成图片…',
        streaming: true,
        images: [],
        seedreamJob: null
      }
      this.chatMessages.push(assistant)
      this.scrollChatBottom()

      try {
        const styleRef = pickWorkflowStyleRef(this.selectedStyle)
        const styleMeta = formatWorkflowStyleMeta(this.selectedStyle, { userPrompt: prompt })
        const styleBlock = buildWorkflowStylePromptSuffix(this.selectedStyle, { userPrompt: prompt })
        const finalPrompt = `${prompt}${styleMeta ? `\n\n${styleMeta}` : ''}${styleBlock}`
        // 风格参考图只传一次，避免封面人物被加权拷贝
        const mergedRefs = (styleRef ? [styleRef] : []).concat(referenceImages || []).filter(Boolean)

        // 保存 seedream job（刷新后可自动重试）
        this.$set(assistant, 'seedreamJob', {
          kind: 'seedream',
          prompt: finalPrompt,
          images: mergedRefs.slice(0, 15),
          n: 1,
          response_format: 'url',
          createdAt: Date.now(),
          attempts: 0
        })
        try {
          await this.persistSessionNow()
        } catch {
          // ignore
        }
        const out = await generateSeedreamImages({
          prompt: finalPrompt,
          // 默认返回 1 张 url；你后续想做多张/尺寸可接 UI
          n: 1,
          response_format: 'url',
          ...(mergedRefs.length ? { image: mergedRefs[0], images: mergedRefs.slice(0, 15) } : {}),
          signal: this.streamAbort.signal
        })
        const urls = pickSeedreamImageUrls(out)
        this.$set(assistant, 'images', urls)
        assistant.streaming = false
        // 成功后清理 job（避免刷新反复重试）
        if (urls.length) this.$set(assistant, 'seedreamJob', null)
        if (urls.length) {
          assistant.content = '生成完成（点击图片可打开原图）。'
          showSuccessToast('图片已生成')
        } else {
          const snippet = (() => {
            try {
              const s = JSON.stringify(out)
              return s.length > 400 ? s.slice(0, 400) + '…' : s
            } catch {
              return ''
            }
          })()
          assistant.content =
            '未返回图片链接。\n\n请把下面这段返回体发我，我可以继续适配字段：\n\n```json\n' +
            (snippet || '（无法序列化返回体）') +
            '\n```'
          showErrorToast('未返回图片链接')
        }
      } catch (e) {
        const msg = e?.message ? String(e.message) : '图片生成失败'
        assistant.streaming = false
        assistant.content = `图片生成失败：${msg}`
        showErrorToast('图片生成失败')
      } finally {
        this.isStreaming = false
        this.streamAbort = null
        this.scrollChatBottom()

        // 确保生成图片也能保存到会话
        const isNewSession = !!(opts && opts.isNewSession)
        if (isNewSession && !this.sessionUuid) {
          this.sessionUuid = newSessionUuid()
          this.$router
            .replace({ name: 'ConversationWorkflow', query: { session: this.sessionUuid } })
            .catch(() => {})
        }
        this.schedulePersistSession()
      }
    },
    async runVideoJob(opts) {
      if (this.isStreaming) return
      this.isStreaming = true
      this.streamAbort = new AbortController()

      const assistant = {
        id: genId(),
        role: 'assistant',
        content: '',
        streaming: true,
        videoUrl: ''
      }
      this.chatMessages.push(assistant)
      this.scrollChatBottom()

      const history = this.chatMessages.filter(m => !m.streaming)
      const lastUser = [...history].reverse().find(m => m.role === 'user')
      const videoPrompt = (lastUser && (lastUser.apiContent || lastUser.content)) || ''
      const referenceImages = Array.isArray(lastUser?.referenceImages) ? lastUser.referenceImages : []
      const ratio = opts && opts.ratio != null ? opts.ratio : this.videoRatio
      const ratioLabel = ratio === '9:16' ? '竖版 9:16' : '横版 16:9'
      const duration = Math.min(
        15,
        Math.max(1, Math.round(Number((opts && opts.duration != null ? opts.duration : this.videoDuration) || 5)))
      )
      const presets = this.videoResolutionPresets
      let resolution =
        (opts && opts.resolution != null && String(opts.resolution).toLowerCase()) || this.videoResolution || '720p'
      if (!presets.includes(resolution)) resolution = '720p'

      const sleep = ms =>
        new Promise(resolve => {
          setTimeout(resolve, ms)
        })

      try {
        assistant.content =
          `已按你的选择提交：**${ratioLabel}**（\`${ratio}\`）、**${duration} 秒**、**${resolution}**。\n\n正在向火山方舟创建视频任务…\n\n`
        this.scrollChatBottom()

        const created = await createSeedanceVideoTask({
          prompt: videoPrompt,
          duration,
          ratio,
          resolution,
          ...(referenceImages.length ? { images: referenceImages } : {}),
          signal: this.streamAbort.signal
        })

        // 记录 taskId，便于刷新后恢复轮询
        this.$set(assistant, 'taskId', String(created.id || ''))
        this.$set(assistant, 'taskStatus', 'queued')
        try {
          await this.persistSessionNow()
        } catch {
          // ignore
        }

        const appliedRatio =
          created.applied_ratio && created.applied_ratio !== ratio
            ? ` 比例服务端确认为 \`${created.applied_ratio}\``
            : ''
        const appliedDur =
          created.applied_duration != null && Number(created.applied_duration) !== duration
            ? ` 时长服务端确认为 ${created.applied_duration} 秒`
            : ''
        const appliedRes =
          created.applied_resolution && String(created.applied_resolution).toLowerCase() !== resolution
            ? ` 分辨率服务端确认为 ${created.applied_resolution}`
            : ''
        assistant.content += `任务已创建：\`${created.id}\`${appliedRatio}${appliedDur}${appliedRes}\n\n正在排队/渲染，请稍候（一般需数分钟）…\n\n`
        this.scrollChatBottom()

        const maxAttempts = 200
        let finalInfo = null
        for (let i = 0; i < maxAttempts; i++) {
          if (this.streamAbort.signal.aborted) {
            throw new Error('aborted')
          }
          const info = await getSeedanceVideoTask(created.id, this.streamAbort.signal)
          finalInfo = info
          const st = String(info.status || '').toLowerCase()
          this.$set(assistant, 'taskStatus', String(info.status || ''))
          assistant.content =
            `任务 \`${created.id}\` 当前状态：**${seedanceStatusZh(info.status)}**\n\n`

          if (st === 'succeeded') {
            const url = info.video_url || (info.content && info.content.video_url)
            if (url) {
              this.$set(assistant, 'videoUrl', url)
              assistant.content += '**生成完成**，请播放上方视频（链接通常有时效，请及时下载保存）。\n\n'
              showSuccessToast('视频已生成')
            } else {
              assistant.content += '任务显示成功但未返回 `video_url`，请检查控制台任务详情。\n\n'
              showErrorToast('未返回视频地址')
            }
            break
          }
          if (st === 'failed' || st === 'cancelled') {
            const errText = info.error
              ? '```json\n' + JSON.stringify(info.error, null, 2) + '\n```\n\n'
              : '_无详细错误字段_\n\n'
            assistant.content += `**任务结束**：${seedanceStatusZh(st)}\n\n${errText}`
            showErrorToast(st === 'failed' ? '视频生成失败' : '任务已取消')
            break
          }

          await sleep(3000)
        }

        if (finalInfo) {
          const st = String(finalInfo.status || '').toLowerCase()
          if (st !== 'succeeded' && st !== 'failed' && st !== 'cancelled') {
            assistant.content += `\n\n（已达到前端轮询上限，最后一次状态：**${seedanceStatusZh(finalInfo.status)}**，可记下任务 id 稍后在控制台查询。）\n\n`
            showErrorToast('等待超时，请稍后查询任务')
          }
        }
      } catch (e) {
        if (e && (e.name === 'AbortError' || e.message === 'aborted')) {
          assistant.content += '\n\n_已取消。_\n\n'
        } else {
          const msg = e?.message ? String(e.message) : '视频任务失败'
          showErrorToast(msg)
          assistant.content += `\n\n**出错**：${msg}\n\n`
        }
      } finally {
        assistant.streaming = false
        this.isStreaming = false
        this.streamAbort = null
        this.scrollChatBottom()
        this.schedulePersistSession()
      }
    }
    ,
    async runScriptJob(opts) {
      if (this.isStreaming) return
      const prompt = (opts && opts.prompt != null ? String(opts.prompt) : '').trim()
      if (!prompt) return

      this.isStreaming = true
      this.streamAbort = new AbortController()

      const assistant = {
        id: genId(),
        role: 'assistant',
        content: '',
        streaming: true
      }
      this.chatMessages.push(assistant)
      this.scrollChatBottom()

      const history = (this.chatMessages || []).filter(m => m && m.role && !m.streaming)
      const apiMessages = history
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({
          role: m.role,
          content: String(m.content || '')
        }))

      try {
        await streamWorkspaceReply({
          messages: apiMessages,
          preset: 'script_factory',
          signal: this.streamAbort.signal,
          onDelta: chunk => {
            assistant.content = (assistant.content || '') + chunk
            this.scrollChatBottom()
          }
        })
      } catch (e) {
        const msg = e?.message ? String(e.message) : '剧本生成失败'
        assistant.content = (assistant.content || '') + `\n\n**出错**：${msg}\n`
      } finally {
        assistant.streaming = false
        this.isStreaming = false
        this.streamAbort = null
        this.scrollChatBottom()

        const isNewSession = !!(opts && opts.isNewSession)
        if (isNewSession && !this.sessionUuid) {
          this.sessionUuid = newSessionUuid()
          this.$router
            .replace({ name: 'ConversationWorkflow', query: { session: this.sessionUuid } })
            .catch(() => {})
        }
        this.schedulePersistSession()
      }
    }
    ,
    async runCharacterDesignerJob(opts) {
      if (this.isStreaming) return
      const prompt = (opts && opts.prompt != null ? String(opts.prompt) : '').trim()
      if (!prompt) return

      // 角色设计图：强制先选择风格（避免“无风格”导致输出不可控）
      if (!this.selectedStyle) {
        this.chatMessages.push({
          id: genId(),
          role: 'assistant',
          content: '请先选择一个**风格**（底部「风格」按钮）再生成角色设计图。'
        })
        this.scrollChatBottom()
        this.openStyleLibrary()
        return
      }

      this.isStreaming = true
      this.streamAbort = new AbortController()

      // 先生成英文绘图提示词（流式）
      const assistantPrompt = {
        id: genId(),
        role: 'assistant',
        content: '',
        streaming: true
      }
      this.chatMessages.push(assistantPrompt)
      this.scrollChatBottom()

      const history = (this.chatMessages || []).filter(m => m && m.role && !m.streaming)
      const baseMsgs = history.filter(m => m.role === 'user' || m.role === 'assistant')
      // 将“风格”明确提交给提示词生成接口（/volc_chat），保证提示词能体现选定风格
      const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
      // 角色设计图：只向接口提交“prompt 字段”（后端将其作为 StylePrompt 解析）
      const stylePrompt = (this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : '').trim()
      // 注意：/volc_chat 的默认模型通常不支持 image input（会报 param=image_url）
      // 风格参考图只在 Seedream 出图时传，不在提示词生成阶段传。

      // 找到最后一条 user 消息，把风格信息附加进去，并在可能时附加风格参考图（多模态）
      let lastUserIdx = -1
      for (let i = baseMsgs.length - 1; i >= 0; i--) {
        if (baseMsgs[i] && baseMsgs[i].role === 'user') {
          lastUserIdx = i
          break
        }
      }

      const apiMessages = baseMsgs.map((m, idx) => {
        const out = { role: m.role, content: String(m.content || '') }
        if (m.role === 'user' && idx === lastUserIdx) {
          const meta = []
          if (styleName) meta.push(`StyleName=${styleName}`)
          if (stylePrompt) meta.push(`StylePrompt=${stylePrompt}`)
          if (meta.length) {
            out.content = `${out.content}\n\n【已选风格】${meta.join('；')}`
          }
        }
        return out
      })

      try {
        await streamWorkspaceReply({
          messages: apiMessages,
          preset: 'character_designer',
          signal: this.streamAbort.signal,
          onDelta: chunk => {
            assistantPrompt.content = (assistantPrompt.content || '') + chunk
            this.scrollChatBottom()
          }
        })
      } catch (e) {
        const msg = e?.message ? String(e.message) : '提示词生成失败'
        assistantPrompt.content = (assistantPrompt.content || '') + `\n\n**出错**：${msg}\n`
        assistantPrompt.streaming = false
        return
      } finally {
        assistantPrompt.streaming = false
        this.isStreaming = false
        this.streamAbort = null
        this.scrollChatBottom()
        const isNewSession = !!(opts && opts.isNewSession)
        if (isNewSession && !this.sessionUuid) {
          this.sessionUuid = newSessionUuid()
          this.$router
            .replace({ name: 'ConversationWorkflow', query: { session: this.sessionUuid } })
            .catch(() => {})
        }
        this.schedulePersistSession()
      }

      const full = String(assistantPrompt.content || '').trim()
      if (!full) return

      // 若需要补充信息，则不出图
      if (full.includes('【需要补充】')) {
        return
      }

      // 提取绘图提示词
      let paintPrompt = full
      const m = full.match(/【绘图提示词】\s*([\s\S]+)/)
      if (m && m[1]) paintPrompt = String(m[1]).trim()
      // 防御：若模型误输出多行标题/空行，压缩成单段英文提示词
      paintPrompt = paintPrompt.replace(/^\s+|\s+$/g, '')

      // 再调用 Seedream 出图
      const assistantImg = {
        id: genId(),
        role: 'assistant',
        kind: 'images',
        content: '正在使用 **Seedream 5.0** 生成角色设计图…',
        streaming: true,
        images: [],
        seedreamJob: null
      }
      this.chatMessages.push(assistantImg)
      this.scrollChatBottom()

      try {
        const styleName = this.selectedStyle && this.selectedStyle.name ? String(this.selectedStyle.name) : ''
        const stylePrompt = (this.selectedStyle && this.selectedStyle.prompt ? String(this.selectedStyle.prompt) : '').trim()
        const styleRef =
          (this.selectedStyle && this.selectedStyle.refDataUrl ? String(this.selectedStyle.refDataUrl) : '') ||
          (this.selectedStyle && this.selectedStyle.refUrl ? String(this.selectedStyle.refUrl) : '') ||
          (this.selectedStyle && this.selectedStyle.thumb ? String(this.selectedStyle.thumb) : '')
        const styleBlock =
          `STYLE LOCK (must follow):\n` +
          `- Match the style reference image as closely as possible (linework/brush, materials, lighting, color palette, rendering).\n` +
          `- The style reference is ONLY for STYLE TRANSFER, NOT for subject/identity. Do NOT copy the reference subject.\n` +
          `- Selected style name: ${styleName || 'custom style'}.\n` +
          (stylePrompt ? `- Style keywords (must reflect): ${stylePrompt}\n` : '') +
          `- Keep the character identity from the text prompt.\n` +
          `- Clean design-sheet layout, white or light gray background.\n`
        // Seedream 往往更偏好“前置约束”，把风格锁定放在最前面
        const finalPaintPrompt = `${styleBlock}\n\n${paintPrompt}`

        // 提高风格参考权重：把风格图放在 images 的前面并适度重复
        const styleImages = styleRef ? [styleRef, styleRef] : []
        this.$set(assistantImg, 'seedreamJob', {
          kind: 'seedream',
          prompt: finalPaintPrompt,
          images: styleImages,
          n: 1,
          response_format: 'url',
          createdAt: Date.now(),
          attempts: 0
        })
        try {
          await this.persistSessionNow()
        } catch {
          // ignore
        }
        const out = await generateSeedreamImages({
          prompt: finalPaintPrompt,
          n: 1,
          response_format: 'url',
          ...(styleRef ? { image: styleRef, images: styleImages } : {})
        })
        const urls = pickSeedreamImageUrls(out)
        this.$set(assistantImg, 'images', urls)
        assistantImg.streaming = false
        if (urls.length) this.$set(assistantImg, 'seedreamJob', null)
        assistantImg.content = urls.length
          ? `已根据你描述的形象生成角色设计图。\n\n【绘图提示词】\n${paintPrompt}`
          : `未返回图片链接。\n\n【绘图提示词】\n${paintPrompt}`
        if (urls.length) showSuccessToast('角色设计图已生成')
        else showErrorToast('未返回图片链接')
      } catch (e) {
        const msg = e?.message ? String(e.message) : '角色设计图生成失败'
        assistantImg.streaming = false
        assistantImg.content = `角色设计图生成失败：${msg}\n\n【绘图提示词】\n${paintPrompt}`
        showErrorToast('角色设计图生成失败')
      } finally {
        this.scrollChatBottom()
        this.schedulePersistSession()
      }
    }
  },
  beforeDestroy() {
    if (this._persistTimer) {
      clearTimeout(this._persistTimer)
      this._persistTimer = null
    }
    if (this.streamAbort) {
      this.streamAbort.abort()
      this.streamAbort = null
    }
    for (const u of this._localObjectUrls || []) {
      try {
        URL.revokeObjectURL(u)
      } catch {
        // ignore
      }
    }
    this._localObjectUrls = []
  }
}
</script>

<style scoped>
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
}
.stream-prose {
  font-variant-ligatures: none;
  text-rendering: optimizeLegibility;
}
.markdown-reply :deep(p) {
  margin: 0.35em 0;
}
.markdown-reply :deep(p:first-child) {
  margin-top: 0;
}
.markdown-reply :deep(p:last-child) {
  margin-bottom: 0;
}
.markdown-reply :deep(ul),
.markdown-reply :deep(ol) {
  margin: 0.35em 0;
  padding-left: 1.25em;
}
.markdown-reply :deep(pre) {
  margin: 0.5em 0;
  padding: 0.75em;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.35);
  overflow-x: auto;
  font-size: 0.85em;
}
.markdown-reply :deep(code) {
  font-size: 0.9em;
}

/* Markdown 表格：不要挤压换行，超宽可横向滚动（像图2那样平铺） */
.markdown-reply {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.markdown-reply :deep(table) {
  border-collapse: collapse;
  /* 关键：按内容撑开，不要被容器压缩 */
  width: max-content;
  min-width: 100%;
}
.markdown-reply :deep(th),
.markdown-reply :deep(td) {
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 0.45rem 0.55rem;
  vertical-align: top;
  /* 关键：不自动折行；需要换行用 <br> */
  white-space: nowrap;
}
.markdown-reply :deep(th) {
  background: rgba(255, 255, 255, 0.06);
  font-weight: 600;
}

/* 用户消息气泡：与 Plus 首页输入面板一致的深海军蓝灰 */
.cw-user-bubble {
  color: rgba(228, 226, 240, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(152deg, #2a2838 0%, #1e1c28 52%, #181622 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 10px 28px -14px rgba(0, 0, 0, 0.55);
}

/* 对话区强调色：对齐 Plus 靛蓝微光，替代品红紫 */
.cw-chat-panel :deep(.text-fuchsia-300),
.cw-chat-panel :deep(.text-fuchsia-200),
.cw-chat-panel :deep(.text-fuchsia-100) {
  color: rgba(199, 210, 254, 0.92);
}
.cw-chat-panel :deep(.border-fuchsia-500) {
  border-color: rgba(129, 140, 248, 0.42) !important;
}
.cw-chat-panel :deep(.bg-fuchsia-600\/20) {
  background-color: rgba(99, 102, 241, 0.14) !important;
}
.cw-chat-panel :deep(.from-fuchsia-600),
.cw-chat-panel :deep(.from-fuchsia-700),
.cw-chat-panel :deep(.to-pink-600),
.cw-chat-panel :deep(.to-pink-700),
.cw-chat-panel :deep(.hover\:from-fuchsia-600),
.cw-chat-panel :deep(.hover\:to-pink-600) {
  --tw-gradient-from: #3a384c var(--tw-gradient-from-position);
  --tw-gradient-to: #282636 var(--tw-gradient-to-position);
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}

/* 用户消息：复用输入框内联缩略图结构（需要 :deep 才能命中子节点） */
.cw-user-html :deep([data-cw-upload='1']) {
  vertical-align: middle;
}
.cw-user-html :deep([data-cw-upload='1'] img) {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border-radius: 0.75rem;
  object-fit: cover;
}

/* 底栏发送：低调深灰描边，与 Plus 输入框一致 */
.cw-composer-submit {
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: linear-gradient(180deg, rgba(48, 46, 62, 0.95) 0%, rgba(32, 30, 44, 0.98) 100%);
  color: rgba(210, 208, 228, 0.92);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.cw-composer-submit:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: rgba(255, 255, 255, 0.2);
  background: linear-gradient(180deg, rgba(58, 56, 74, 0.98) 0%, rgba(40, 38, 54, 1) 100%);
  color: #fff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 0 14px rgba(129, 140, 248, 0.1);
}
.cw-composer-submit:disabled {
  transform: none;
  box-shadow: none;
}

/* 大会话优化：离屏消息不参与布局/绘制，显著减轻首屏与滚动压力 */
.cw-chat-item {
  content-visibility: auto;
  contain-intrinsic-size: 160px;
}

/* 纯 CSS 转圈：避免依赖图标库加载状态 */
.cw-loading-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: rgba(255, 255, 255, 0.78);
  animation: cwSpin 0.8s linear infinite;
}
@keyframes cwSpin {
  to {
    transform: rotate(360deg);
  }
}

.cw-mini-frame {
  display: block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}
.cw-mini-frame--landscape {
  width: 2.5rem;
  height: 1.35rem;
}
.cw-mini-frame--portrait {
  width: 1.1rem;
  height: 1.85rem;
}

/* 视频时长滑块：粉色段 + 灰段 + 白圆钮（与整体风格一致） */
.cw-duration-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  background: transparent;
  cursor: pointer;
}
.cw-duration-slider:focus {
  outline: none;
}
.cw-duration-slider::-webkit-slider-runnable-track {
  height: 5px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    #ec4899 0%,
    #ec4899 var(--cw-fill, 28.57%),
    #3f3f46 var(--cw-fill, 28.57%),
    #3f3f46 100%
  );
}
.cw-duration-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  margin-top: -6.5px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  border: none;
}
.cw-duration-slider::-moz-range-track {
  height: 5px;
  border-radius: 9999px;
  background: #3f3f46;
}
.cw-duration-slider::-moz-range-progress {
  height: 5px;
  border-radius: 9999px;
  background: #ec4899;
}
.cw-duration-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  cursor: pointer;
}

.cw-agent-node-action {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 10px;
  color: rgba(229, 231, 235, 0.95);
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}
.cw-agent-node-action:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
.cw-agent-node-action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
